import React, { forwardRef, useState, useEffect, useId, cloneElement, Children, useMemo, useRef, useImperativeHandle, useLayoutEffect, createContext, useContext, useCallback } from 'react';
import classNames from 'classnames';
import icons from '@kite/icons/dist/kite-icons.js';
import { useUID } from 'react-uid';
import tokens from '@kite/tokens/dist/kite/web/theme/white/tokens.flat.json';
import { defaultArraySearch } from '@react-kite-plus/utils';
import ReactDOM, { createPortal } from 'react-dom';
import FocusTrap from 'focus-trap-react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __classPrivateFieldGet(receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
}

function __classPrivateFieldSet(receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const KiteBadge = forwardRef((_a, ref) => {
    var { small, xPos, yPos, disabled, inline, assistiveText, className, content, children } = _a, other = __rest(_a, ["small", "xPos", "yPos", "disabled", "inline", "assistiveText", "className", "content", "children"]);
    const [formattedContent, setFormattedContent] = useState();
    useEffect(() => {
        // If content in badge is >= 99, show '99+'
        let formatContent = Math.min(content, 99).toString();
        if (content >= 99) {
            formatContent += '+';
        }
        setFormattedContent(formatContent);
    }, [content]);
    const { ref: propRef } = other, nonRefAttributes = __rest(other, ["ref"]); // remove ref from other props as it otherwise throws ts error
    return (React.createElement("div", Object.assign({ className: classNames('rk-badge-container', {
            'rk-badge-container--flex': yPos && yPos === 'center',
            [className]: className,
        }) }, nonRefAttributes),
        children,
        React.createElement("span", { ref: ref, className: classNames('kite-badge rk-badge', {
                'kite-badge--sm': small,
                'kite-badge--top': yPos && yPos === 'top',
                'kite-badge--right': xPos && xPos === 'right',
                'kite-badge--bottom': yPos && yPos === 'bottom',
                'kite-badge--left': xPos && xPos === 'left',
                'rk-badge--align-center': yPos && yPos === 'center',
                'kite-badge--disabled': disabled,
                'kite-badge--inline': inline,
            }) },
            !small && formattedContent,
            assistiveText && (React.createElement("span", { className: "kite-assistive-text" }, assistiveText)))));
});

const useNormalId = (id) => {
    const uid = useId();
    return id || uid;
};

const KiteSvgIcon = (_a) => {
    var { ariaHidden, ariaLabel, className, color, fill, fillRule, focusable, icon, id, title, viewBox, children } = _a, other = __rest(_a, ["ariaHidden", "ariaLabel", "className", "color", "fill", "fillRule", "focusable", "icon", "id", "title", "viewBox", "children"]);
    const normalId = useNormalId(id);
    const svgId = `icon-svg-${normalId}`;
    const defaultTitle = `Icon: ${icon === null || icon === void 0 ? void 0 : icon.split('-').filter((part) => ['ki', 'f'].indexOf(part) < 0).join(' ')}.`;
    // prioritize aria-label then title, then default title and make aria-label
    const finalAriaLabel = title === null
        ? undefined // title override with null should prevent any label
        : !!ariaLabel
            ? ariaLabel // prioritize ariaLabel direct set
            : !!title
                ? title // then prioritize the title having been directly set
                : defaultTitle; // or default to default icon label
    return (React.createElement("svg", Object.assign({ id: svgId, className: className, style: color ? { '--color': color } : { fill }, role: "img", focusable: focusable, fillRule: fillRule, viewBox: viewBox !== null && viewBox !== void 0 ? viewBox : '0 0 32 32', "data-icon": icon, "aria-hidden": ariaHidden, "aria-label": finalAriaLabel }, other),
        children,
        icons
            .filter((i) => i.name === icon)
            .map((kiteIcon, index) => {
            if (kiteIcon.name !== icon) {
                return undefined;
            }
            const svgElements = [];
            for (let i = 0; i < kiteIcon.svgData.paths.length; i++) {
                svgElements.push(React.createElement("path", { d: kiteIcon.svgData.paths[i].d, key: 'd-' + index + '-' + i }));
            }
            if (kiteIcon.svgData.circles.length > 0) {
                for (let i = 0; i < kiteIcon.svgData.circles.length; i++) {
                    svgElements.push(React.createElement("circle", { cx: kiteIcon.svgData.circles[i].cx, cy: kiteIcon.svgData.circles[i].cy, r: kiteIcon.svgData.circles[i].r, key: 'circle-' + index + '-' + i }));
                }
            }
            return svgElements;
        })));
};

/**
 * Latest Kite icons served from the [kite-icons](https://gitlab.spectrumflow.net/kite/kite-icons) repo.
 */
const KiteIcon = (_a) => {
    var { icon, color, fill, size, inline, offset, badge, title = '', id, ariaHidden = true, ariaLabel, className = '', svgClassName } = _a, other = __rest(_a, ["icon", "color", "fill", "size", "inline", "offset", "badge", "title", "id", "ariaHidden", "ariaLabel", "className", "svgClassName"]);
    if (!!fill) {
        console.warn('Property fill has been deprecated. Use color prop instead.');
    }
    const svgElement = (React.createElement(KiteSvgIcon, { icon: icon, fill: fill, color: color, className: svgClassName, ariaLabel: ariaLabel, ariaHidden: ariaHidden, focusable: false, title: title, id: id, viewBox: "0 0 32 32", fillRule: "evenodd" }));
    const iconElement = (React.createElement("span", Object.assign({ className: classNames({
            'kite-icon': true,
            'kite-icon--inline': inline,
            [className]: className,
        }), style: { '--size': size, '--offset': offset } }, other),
        React.createElement("span", { className: "kite-icon__inner" }, svgElement)));
    if (badge) {
        return (React.createElement(KiteBadge, { xPos: "right", yPos: "top", content: badge }, iconElement));
    }
    // icons within an alert have different markup than a normal icon for styling purposes
    if (className.includes('kite-alert__icon')) {
        return React.createElement("span", { className: "kite-alert__icon" }, iconElement);
    }
    return iconElement;
};
KiteIcon.displayName = 'KiteIcon';

const noop = () => undefined;
var MenuActionKey;
(function (MenuActionKey) {
    MenuActionKey["ARROW_DOWN"] = "ArrowDown";
    MenuActionKey["ARROW_UP"] = "ArrowUp";
    MenuActionKey["ESCAPE"] = "Escape";
    MenuActionKey["ENTER"] = "Enter";
    MenuActionKey["SPACE"] = " ";
})(MenuActionKey || (MenuActionKey = {}));
const InteractiveComponents = Object.freeze(['KiteButton', 'KiteLink']);
const dropdownMaxItemCount = 5;

/**
 * @function allTrue
 * @param {...boolean} args
 * @description Accepts any number of boolean values.  Returns True if all are true.  No args returns True.
 */
const allTrue = (...args) => !args.length || args.every((arg) => arg);
const combineIdentifiers = (...args) => classNames(...args);
const getIsComponentInteractive = (component) => {
    if (!React.isValidElement(component))
        return false;
    const componentType = component.type;
    return InteractiveComponents.includes(componentType.displayName);
};
const createApplyUnits = (units) => (value) => `${value}${units}`;
const getDeprecationMessage = (prop, deprecatedVal, newVal) => `Value '${deprecatedVal}' has been deprecated. Use '${newVal}' instead.  Example: '${prop} = ${newVal}'.`;
const getHelperTextIdFromLabelId = (labelId) => `${labelId}-helper-text`;
const arrayIntersect = (arr1, arr2) => arr1.filter((value) => arr2.includes(value));
const arrayDedupe = (arr) => {
    const seen = {};
    // Add each array item as key and value. Duplicate keys will overwrite.
    arr.forEach((item) => {
        const key = JSON.stringify(item);
        seen[key] = item;
    });
    // Return deduped values
    return Object.values(seen);
};

const partitionChildren = (children, filter) => {
    const mainChildren = [], otherChildren = [];
    Children.toArray(children).forEach((e, i, array) => (filter(e) ? mainChildren : otherChildren).push(e));
    return [mainChildren, otherChildren];
};
const partitionChildrenByDisplayName = (children, displayName) => {
    const childrenFilter = (child) => {
        var _a;
        if (typeof child === 'string') {
            return true;
        }
        return ((_a = child === null || child === void 0 ? void 0 : child.type) === null || _a === void 0 ? void 0 : _a['displayName']) !== displayName;
    };
    return partitionChildren(children, childrenFilter);
};
const hasOneChild = (children) => {
    var _a;
    return ((_a = React.Children.toArray(children)) === null || _a === void 0 ? void 0 : _a.length) === 1;
};
const childrenContainsAtLeastOne = (children, componentType) => {
    if (!children) {
        return false;
    }
    return React.Children.toArray(children).some((child) => {
        var _a, _b, _c;
        if ((child instanceof Object &&
            ((_a = child['type']) === null || _a === void 0 ? void 0 : _a.name) &&
            ((_b = child['type']) === null || _b === void 0 ? void 0 : _b.name) === componentType) ||
            ((_c = child['type']) === null || _c === void 0 ? void 0 : _c.displayName) === componentType) {
            return true;
        }
        else if (child instanceof Object && child['type'] === componentType) {
            return true;
        }
        return false;
    });
};
const isLastChild = (children, componentType) => {
    var _a, _b, _c;
    if (!children) {
        return false;
    }
    const childrenArray = React.Children.toArray(children);
    let lastChild;
    if (childrenArray.length >= 1) {
        lastChild = childrenArray[childrenArray.length - 1];
    }
    else {
        return false;
    }
    if ((lastChild instanceof Object &&
        ((_a = lastChild['type']) === null || _a === void 0 ? void 0 : _a.name) &&
        ((_b = lastChild['type']) === null || _b === void 0 ? void 0 : _b.name) === componentType) ||
        ((_c = lastChild['type']) === null || _c === void 0 ? void 0 : _c.displayName) === componentType) {
        return true;
    }
    else {
        return false;
    }
};
/**
 * Iterate through children and for each child that is the same type as wrapType, wrap it with wrapper
 * @param children - children
 * @param wrapType - functional component type you want to search through children for
 * @param wrapper - wrapper element to wrap child in
 */
const wrapChildrenOfType = (children, wrapType, wrapper) => {
    if (children) {
        return React.Children.map(children, (child, index) => {
            if (child.type === wrapType) {
                return cloneElement(wrapper, {
                    children: child,
                });
            }
            else {
                return child;
            }
        });
    }
    else {
        return [React.createElement(React.Fragment, null)];
    }
};
const removeChildrenOfType = (children, wrapType) => {
    if (children) {
        return React.Children.map(children, (child, index) => {
            if (child.type === wrapType) {
                return null;
            }
            else {
                return child;
            }
        });
    }
    else {
        return [React.createElement(React.Fragment, null)];
    }
};
const applyPropsToChildrenOfType = (children, wrapType, props) => {
    if (children) {
        return React.Children.map(children, (child, index) => {
            if (child && child.type === wrapType) {
                return cloneElement(child, Object.assign(Object.assign({}, child.props), props));
            }
            else {
                return child;
            }
        });
    }
    else {
        return [React.createElement(React.Fragment, null)];
    }
};

const KiteLinkIcon = (_a) => {
    var { icon } = _a, other = __rest(_a, ["icon"]);
    return (React.createElement("span", { className: "kite-link__icon" },
        React.createElement(KiteIcon, Object.assign({ icon: icon, size: '14px' }, other, { inline: true }))));
};
/**
 * If the children of KiteLink is a ReactRouter Link, pass all the api props to the Link component
 * and put the left/right icons in the correct place inside Link
 * @param children
 * @param originalChildren
 * @constructor
 */
const KiteLinkReactRouter = (_a) => {
    var { children, maybeLeftIcon, maybeRightIcon } = _a, other = __rest(_a, ["children", "maybeLeftIcon", "maybeRightIcon"]);
    return cloneElement(children, Object.assign({ children: (React.createElement(React.Fragment, null,
            maybeLeftIcon,
            children.props.children,
            maybeRightIcon)) }, other));
};
const resolveIconId = (dir) => (fuzzyId) => {
    if (typeof fuzzyId === 'boolean' && fuzzyId)
        return `ki-chevron-${dir}`;
    if (typeof fuzzyId === 'string')
        return fuzzyId;
    return undefined;
};
const KiteLink = forwardRef((_a, ref) => {
    var { ariaDescribedBy, href = '#', type = 'inline', children, onClick = (e) => null, className, iconLeft, iconLeftAriaHidden = true, iconLeftAriaLabel = '', iconRight, iconRightAriaHidden = true, iconRightAriaLabel = '' } = _a, other = __rest(_a, ["ariaDescribedBy", "href", "type", "children", "onClick", "className", "iconLeft", "iconLeftAriaHidden", "iconLeftAriaLabel", "iconRight", "iconRightAriaHidden", "iconRightAriaLabel"]);
    const iconLeftId = resolveIconId('left')(iconLeft);
    const iconRightId = resolveIconId('right')(iconRight);
    const hasReactRouterLink = useMemo(() => {
        return (children &&
            hasOneChild(children) &&
            childrenContainsAtLeastOne(children, 'Link'));
    }, [children]);
    const iconLeftProps = {
        icon: iconLeftId,
        ariaHidden: iconLeftAriaHidden,
        ariaLabel: iconLeftAriaLabel,
    };
    const iconRightProps = {
        icon: iconRightId,
        ariaHidden: iconRightAriaHidden,
        ariaLabel: iconRightAriaLabel,
    };
    const mapTypeToIconRight = {
        'standalone-icon': React.createElement(KiteLinkIcon, { icon: 'ki-chevron-right' }),
        'inline-icon': React.createElement(KiteLinkIcon, { icon: 'ki-chevron-right' }),
        standalone: allTrue(type === 'standalone', !!iconRightId) ? (React.createElement(KiteLinkIcon, Object.assign({}, iconRightProps))) : null,
    };
    const maybeLeftIcon = allTrue(type === 'standalone', !!iconLeftId) ? (React.createElement(KiteLinkIcon, Object.assign({}, iconLeftProps))) : null;
    const maybeRightIcon = mapTypeToIconRight[type] || null;
    return (React.createElement(React.Fragment, null, !hasReactRouterLink ? (React.createElement("a", Object.assign({ ref: ref, "aria-describedby": ariaDescribedBy, href: href, onClick: onClick, className: classNames({
            'kite-link': true,
            'kite-link--standalone': [
                'standalone',
                'standalone-icon',
            ].includes(type),
            [className]: className,
        }) }, other),
        maybeLeftIcon,
        children,
        maybeRightIcon)) : (React.createElement(KiteLinkReactRouter, Object.assign({ ref: ref, "aria-describedby": ariaDescribedBy, href: href, onClick: onClick, className: classNames({
            'kite-link': true,
            'kite-link--standalone': [
                'standalone',
                'standalone-icon',
            ].includes(type),
            [className]: className,
        }) }, other, { maybeRightIcon: maybeRightIcon, maybeLeftIcon: maybeLeftIcon }), children))));
});
KiteLink.displayName = 'KiteLink';

/**
 *  [View the design specifications](https://beam.kiteds.com/web/components/progress-indicator/design.html)
 *
 */
const circleSizeSpecs = {
    sm: {
        viewBox: '0 0 24 24',
        position: '12',
        radius: '11',
    },
    md: {
        viewBox: '0 0 40 40',
        position: '20',
        radius: '18',
    },
    lg: {
        viewBox: '0 0 80 80',
        position: '40',
        radius: '38',
    },
};
const KiteProgressIndicatorCircular = ({ id, hide, size = 'lg', percent, status, title, message, errorCTAText, errorCTALoading, errorCTAOnClick, }) => {
    const hasPercent = percent !== undefined && percent !== null;
    const progressPercentage = hasPercent && size === 'lg'
        ? percent + '%'
        : hide || size !== 'lg'
            ? ''
            : '';
    const circleSizes = !!circleSizeSpecs[size]
        ? circleSizeSpecs[size]
        : circleSizeSpecs.lg;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "kite-progress__indicator" },
            React.createElement("div", { className: "kite-progress__icons" },
                React.createElement("svg", { className: "kite-progress__spinner", viewBox: circleSizes.viewBox, focusable: "false", "aria-hidden": status === 'success' || status === 'failure', "aria-label": status !== 'success' && status !== 'failure' ? 'loading' : null },
                    React.createElement("circle", { className: "kite-progress__circle", cx: circleSizes.position, cy: circleSizes.position, r: circleSizes.radius })),
                status === 'success' && (React.createElement(KiteIcon, { icon: "ki-checkmark-circle-f", className: "kite-progress__success", size: "100%", ariaLabel: "Success", ariaHidden: false })),
                status === 'failure' && (React.createElement(KiteIcon, { icon: "ki-caution-circle-f", className: "kite-progress__error", size: "100%", ariaLabel: "Error", ariaHidden: false }))),
            React.createElement("span", { className: "kite-progress__value-label" }, progressPercentage),
            hasPercent ? (React.createElement("progress", { max: "100", value: percent },
                percent,
                "%")) : (React.createElement(React.Fragment, null,
                React.createElement("progress", null, "Loading")))),
        title && (React.createElement("div", { id: `progress-text-${id}`, className: "kite-progress__label" }, title)),
        message && (React.createElement("div", { id: `progress-secondary-text-${id}`, className: "kite-progress__secondary-label" }, message)),
        errorCTAText && status === 'failure' && (React.createElement(KiteButton, { onClick: errorCTAOnClick, className: "kite-progress__button", size: "shrinkwrap", loading: errorCTALoading }, errorCTAText))));
};
KiteProgressIndicatorCircular.displayName = 'KiteProgressIndicatorCircular';

const KiteProgressIndicatorLinear = ({ id, hide, percent, status, title, message, errorCTAText, errorCTALoading, errorCTAOnClick, }) => {
    const hasPercent = percent !== undefined && percent !== null;
    const progressPercentage = hasPercent ? percent + '%' : hide ? '' : '';
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "kite-progress__indicator" },
            React.createElement("div", { className: "kite-progress__label-group" },
                React.createElement("div", { id: `progress-text-${id}`, className: "kite-progress__label" }, title),
                React.createElement("span", { className: "kite-progress__value-label" }, progressPercentage),
                hasPercent ? (React.createElement("progress", { max: "100", value: percent },
                    percent,
                    "%")) : (React.createElement(React.Fragment, null,
                    React.createElement("progress", null, "Loading"))),
                status === 'success' && (React.createElement("div", { className: "kite-progress__icons" },
                    React.createElement(KiteIcon, { icon: "ki-checkmark-circle-f", className: "kite-progress__success", size: "100%" }))),
                status === 'failure' && (React.createElement("div", { className: "kite-progress__icons" },
                    React.createElement(KiteIcon, { icon: "ki-caution-circle-f", className: "kite-progress__error", size: "100%" })))),
            React.createElement("div", { className: "kite-progress__track", "aria-hidden": "true" },
                React.createElement("div", { className: "kite-progress__progress-fill" }))),
        React.createElement("div", { id: `progress-secondary-text-${id}`, className: "kite-progress__secondary-label" }, message),
        errorCTAText && status === 'failure' && (React.createElement(KiteButton, { onClick: errorCTAOnClick, className: "kite-progress__button", size: "shrinkwrap", loading: errorCTALoading }, errorCTAText))));
};
KiteProgressIndicatorLinear.displayName = 'KiteProgressIndicatorLinear';

const KiteProgressIndicator = (_a) => {
    var { id, hide, useOverlay, size = 'lg', percent, className, status, title, variant = 'circular', message, errorCTAText, errorCTALoading, errorCTAOnClick } = _a, other = __rest(_a, ["id", "hide", "useOverlay", "size", "percent", "className", "status", "title", "variant", "message", "errorCTAText", "errorCTALoading", "errorCTAOnClick"]);
    const hasPercent = percent !== undefined && percent !== null;
    return (React.createElement("div", Object.assign({ className: classNames('kite-progress rk-progress', {
            'kite-progress--circular': variant === 'circular',
            'kite-progress--hidden': hide,
            'kite-progress--overlay': useOverlay,
            'kite-progress--sm': size === 'sm' && variant === 'circular',
            'kite-progress--lg': size === 'lg' && variant === 'circular',
            'kite-progress--determinate': hasPercent,
            'kite-progress--success': status === 'success',
            'kite-progress--error': status === 'failure',
            [className]: className,
        }), style: {
            ['--percent']: hasPercent && percent / 100,
        }, "aria-live": "polite", "aria-atomic": true, role: status === 'failure' ? 'alert' : null }, other),
        React.createElement("div", { className: "kite-progress__backdrop" }),
        variant === 'circular' && (React.createElement(KiteProgressIndicatorCircular, { id: id, message: message, percent: percent, size: size, status: status, title: title, errorCTAOnClick: errorCTAOnClick, errorCTALoading: errorCTALoading, errorCTAText: errorCTAText, hide: hide })),
        variant === 'linear' && (React.createElement(KiteProgressIndicatorLinear, { id: id, message: message, percent: percent, status: status, title: title, errorCTAOnClick: errorCTAOnClick, errorCTALoading: errorCTALoading, errorCTAText: errorCTAText, hide: hide }))));
};

/**
 * Standard button to trigger an action. View the design specifications for [buttons](https://beam.kiteds.com/web/components/button/design.html).
 *
 * **NOTE:** Any other props not specifically named that are supplied will be provided to the native `<button>` element.
 */
const KiteButton = forwardRef((_a, ref) => {
    var { ariaDescribedBy, children, className, disabled, icon, id, loading, loadingDisplayLabel, onClick, legacy, size, type = 'button', variant = 'primary', colorVariant = 'default' } = _a, other = __rest(_a, ["ariaDescribedBy", "children", "className", "disabled", "icon", "id", "loading", "loadingDisplayLabel", "onClick", "legacy", "size", "type", "variant", "colorVariant"]);
    const normalId = useNormalId(id);
    const buttonProgressId = `btn-progress-${normalId}`;
    const [init, setInit] = useState(false);
    const [iconVariant, setIconVariant] = useState('icon-none');
    /**
     * Slot `buttonBefore`
     */
    let buttonBefore = null;
    !!icon &&
        (buttonBefore = React.createElement(KiteIcon, { icon: icon, className: "rk-button--icon" }));
    !!loading &&
        (buttonBefore = (React.createElement(KiteProgressIndicator, { className: "kite-progress--btn", id: buttonProgressId, size: "sm" })));
    // Always display Children if `loading` === `false`
    // Otherwise, when `loading` === `true`, only display children if `loadingDisplayLabel` also === `true`
    const shouldDisplayChildren = !loading || !!loadingDisplayLabel;
    const displayChildren = () => (React.createElement("span", { className: "rk-button--copy kite-button__inner" },
        buttonBefore,
        shouldDisplayChildren && children));
    const buttonSizeClassName = `kite-button--${size}`;
    const isSized = !!size;
    /**
     * Determine if children is only a KiteIcon, then set property to true, toggle specific css class on button
     */
    const isChildIcon = () => {
        if (hasOneChild(children) &&
            childrenContainsAtLeastOne(children, 'KiteIcon')) {
            setIconVariant('icon-only');
        }
        else if (isLastChild(children, 'KiteIcon')) {
            setIconVariant('icon-end');
        }
    };
    useEffect(() => {
        if (!init) {
            isChildIcon();
            setInit(true);
        }
    }, [init]);
    return (React.createElement(React.Fragment, null,
        React.createElement("button", Object.assign({ className: classNames('kite-button rk-button', {
                [`kite-button--${variant}`]: variant,
                'kite-button--icon-only': iconVariant === 'icon-only' || !children,
                'kite-button--icon-end': iconVariant === 'icon-end',
                'rk-button--loading': loading,
                'kite-button--destructive': colorVariant === 'destructive',
                [className]: className,
                'kite-button--fullwidth-to-shrinkwrap': legacy,
                [buttonSizeClassName]: isSized,
                'kite-button--shrinkwrap': isSized,
            }), onClick: !loading ? onClick : null, disabled: disabled, ref: ref, type: type, id: id, "aria-describedby": ariaDescribedBy }, other), displayChildren())));
});
KiteButton.displayName = 'KiteButton';

const iconNames = {
    caution: 'ki-caution-alert-f',
    error: 'ki-caution-circle-f',
    info: 'ki-info-circle-f',
    success: 'ki-checkmark-circle-f',
    negative: 'ki-caution-circle-f',
    neutral: 'ki-info-circle-f',
    positive: 'ki-checkmark-circle-f',
};
const alertCSSMap = {
    caution: 'caution',
    negative: 'negative',
    error: 'error',
    neutral: 'neutral',
    info: 'info',
    positive: 'positive',
    success: 'success',
};
const iconStatusNamesMap = {
    caution: 'ki-caution-alert-f',
    negative: 'ki-caution-circle-f',
    neutral: 'ki-info-circle-f',
    positive: 'ki-checkmark-circle-f',
};
const AlertLinkWithUrl = ({ href, children, ariaDescribedBy }) => (React.createElement("div", { className: "kite-alert__action" },
    React.createElement(KiteLink, { href: href, type: "standalone-icon", "aria-describedby": ariaDescribedBy }, children)));
const AlertButton = ({ onClick, children, ariaDescribedBy }) => (React.createElement("div", { className: "kite-alert__action" },
    React.createElement(KiteButton, { onClick: onClick, variant: 'secondary', size: 'shrinkwrap', "aria-describedby": ariaDescribedBy }, children)));
const AlertLinkWithOnClick = ({ onClick, children, ariaDescribedBy }) => (React.createElement("div", { className: "kite-alert__action" },
    React.createElement("div", { className: "kite-link kite-link--standalone", onClick: onClick, role: "link", tabIndex: 0, style: { cursor: 'pointer' }, "aria-describedby": ariaDescribedBy },
        children,
        React.createElement("span", { className: "kite-link__icon" },
            React.createElement(KiteIcon, { icon: "ki-chevron-right", inline: true, size: "12px" })))));
const MaybeCTA = ({ variant, text, onClick, url, ariaDescribedBy }) => {
    if (allTrue(variant === 'link', !!text, !url, !!onClick))
        return (React.createElement(AlertLinkWithOnClick, { onClick: onClick, ariaDescribedBy: ariaDescribedBy }, text));
    if (allTrue(variant === 'link', !!text, !!url, !onClick))
        return (React.createElement(AlertLinkWithUrl, { href: url, ariaDescribedBy: ariaDescribedBy }, text));
    if (allTrue(variant === 'button', !!text, !url, !!onClick))
        return (React.createElement(AlertButton, { onClick: onClick, ariaDescribedBy: ariaDescribedBy }, text));
    return null;
};
/**
 * View the design specifications for [alerts](https://beam.kiteds.com/web/components/alert/design.html).
 */
const KiteAlert = (_a) => {
    var { alertRef, className, description, dismissible, id, level = 'page', linkText, linkUrl, linkVariant = 'link', onClose, onLinkClick, type, status, autoFocus = true, children, title, shouldCtaLinkDescription = false, showIcon = true } = _a, other = __rest(_a, ["alertRef", "className", "description", "dismissible", "id", "level", "linkText", "linkUrl", "linkVariant", "onClose", "onLinkClick", "type", "status", "autoFocus", "children", "title", "shouldCtaLinkDescription", "showIcon"]);
    const innerRef = useRef(null);
    useImperativeHandle(alertRef, () => innerRef.current);
    const isPassiveAutoDismiss = useMemo(() => level === 'passive' &&
        (type === 'positive' ||
            type === 'neutral' ||
            type === 'info' ||
            type === 'success' ||
            status === 'positive' ||
            status === 'neutral'), [level, type, status]);
    const ariaLabelAlertStatusMap = {
        caution: 'Caution',
        negative: 'Error',
        neutral: 'Info',
        positive: 'Success',
    };
    const ariaLabelAlertMap = {
        caution: 'Caution',
        error: 'Error',
        info: 'Info',
        success: 'Success',
        negative: 'Error',
        neutral: 'Info',
        positive: 'Success',
    };
    if (!!type) {
        if (type === 'error') {
            console.warn('Property type has been deprecated. Use status=negative');
        }
        else if (type === 'info') {
            console.warn('Property type has been deprecated. Use status=neutral');
        }
        else if (type === 'success') {
            console.warn('Property type has been deprecated. Use status=positive');
        }
    }
    const normalId = useNormalId();
    const alertMessageId = !!id ? id : `alert-message-${normalId}`;
    const alertContent = (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "kite-alert__inner", role: "alert" },
            showIcon && (React.createElement(KiteIcon, { icon: iconStatusNamesMap[status]
                    ? iconStatusNamesMap[status]
                    : iconNames[type], className: "kite-alert__icon kite-alert__title-icon", ariaHidden: false, ariaLabel: ariaLabelAlertStatusMap[status]
                    ? ariaLabelAlertStatusMap[status]
                    : ariaLabelAlertMap[type] })),
            level === 'passive' && title ? (React.createElement("div", { className: "kite-alert__message", id: `${normalId}-message` },
                React.createElement("h2", { className: "kite-alert__title" }, title),
                React.createElement("span", { id: alertMessageId, className: "kite-alert__description" }, children || description))) : (React.createElement("span", { id: alertMessageId, className: "kite-alert__message" }, children || description)),
            dismissible && (React.createElement("button", { onClick: onClose, className: "kite-alert__dismiss-button", type: "button", "aria-label": isPassiveAutoDismiss ? null : 'dismiss alert', "aria-describedby": isPassiveAutoDismiss ? null : alertMessageId },
                React.createElement(KiteIcon, { icon: "ki-x", className: "kite-alert__icon" })))),
        /** If alert is *NOT* PassiveAutoDismiss, then it is Persistent.  CTAs are only allowed on Persistent alerts. **/
        !isPassiveAutoDismiss && (React.createElement(MaybeCTA, { variant: linkVariant, text: linkText, url: linkUrl, onClick: onLinkClick, ariaDescribedBy: shouldCtaLinkDescription && alertMessageId }))));
    useEffect(() => {
        if (!innerRef || !innerRef.current || !autoFocus)
            return;
        innerRef.current.focus();
    }, [type, status]);
    // this isn't allowed under Beam spec
    if (level === 'global' && (type === 'success' || status === 'positive')) {
        return (React.createElement("p", null, "The `global` level alert doesn't have the `success` type. Please choose another type or use the `page` level alert."));
    }
    if (level === 'global') {
        return (React.createElement("header", Object.assign({ className: classNames({
                'kite-alert': true,
                'kite-alert--activated': true,
                'kite-alert--global': true,
                [`kite-alert--${!!status ? alertCSSMap[status] : alertCSSMap[type]}`]: true,
                [className]: className,
            }), id: id, role: "banner", ref: innerRef, tabIndex: -1 }, other), alertContent));
    }
    else if (level === 'passive') {
        return (React.createElement("div", Object.assign({ className: classNames('kite-alert kite-alert--activated kite-alert--passive kite-hidden', {
                [`kite-alert--${status ? alertCSSMap[status] : alertCSSMap[type]}`]: true,
                [className]: className,
            }), ref: innerRef }, other), alertContent));
    }
    return (React.createElement("div", Object.assign({ className: classNames({
            'kite-alert': true,
            'kite-alert--activated': true,
            [`kite-alert--${status ? alertCSSMap[status] : alertCSSMap[type]}`]: true,
            [className]: className,
        }), id: id, ref: innerRef, tabIndex: -1 }, other), alertContent));
};

/**
 * The element containing all column headers in KiteDataTable
 */
const KiteAlertsGroup = (_a) => {
    var { children } = _a; __rest(_a, ["children"]);
    return (React.createElement("div", { className: "kite-alert-region", role: "region", "aria-label": "alerts" }, children));
};

/**
 * Standard card element. Any child elements will be passed as content.
 *
 * View the design specifications for the [card here](https://beam.kiteds.com/web/components/card/design.html)
 */
const KiteCard = (_a) => {
    var { ariaLabel, ariaLabelledBy, children, className, onScroll, elevation } = _a, other = __rest(_a, ["ariaLabel", "ariaLabelledBy", "children", "className", "onScroll", "elevation"]);
    return (React.createElement("div", Object.assign({ className: classNames({
            'kite-card': true,
            [`kite-card--elevation-${elevation}`]: elevation,
            [className]: className,
        }), "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, onScroll: onScroll }, other), children));
};

/**
 * [Accordion design guidelines](https://beam.kiteds.com/web/components/accordion/design.html)
 */
const KiteAccordion = forwardRef((_a, ref) => {
    var { borderRule, card = false, children, className, defaultActiveRow = null, onChange } = _a, other = __rest(_a, ["borderRule", "card", "children", "className", "defaultActiveRow", "onChange"]);
    const uuid = useUID();
    const [activeRow, setActiveRow] = useState(defaultActiveRow);
    const borderRuleClassMap = {
        top: 'kite-accordion--rules-top',
        bottom: 'kite-accordion--rules-bottom',
        none: 'kite-accordion--rules-none',
    };
    const listClasses = classNames('kite-accordion', 'rk-accordion', { 'kite-accordion--in-card': card }, [borderRuleClassMap[borderRule]], className);
    const cardClasses = classNames('kite-card--reset-padding', 'kite-card--reset-height');
    useImperativeHandle(ref, () => ({
        closeAll() {
            setActiveRow(-1);
        },
    }));
    const handleRowClick = (disabled, callback) => (index) => {
        // don't call if disabled
        if (!disabled) {
            const next = activeRow === index ? null : index;
            setActiveRow(next);
            // if the consumer has passed a callback, call it.
            if (!!callback) {
                callback(next);
            }
        }
    };
    useEffect(() => {
        setActiveRow(defaultActiveRow);
    }, [defaultActiveRow]);
    useEffect(() => {
        if (onChange) {
            onChange(activeRow);
        }
    }, [activeRow]);
    const options = React.Children.map(children, (child, index) => {
        const { disabled, callback, title, label } = child.props;
        const newProps = {
            active: activeRow === index,
            callback: handleRowClick(disabled, callback),
            index,
            key: `${title}-${label}`,
        };
        return React.cloneElement(child, newProps);
    });
    const { ref: propRef } = other, nonRefAttributes = __rest(other, ["ref"]); // remove ref from other props as it otherwise throws ts error
    return card ? (React.createElement(KiteCard, { className: cardClasses },
        React.createElement("ul", Object.assign({ key: uuid, className: listClasses }, nonRefAttributes), options))) : (React.createElement("ul", Object.assign({ key: uuid, className: listClasses }, nonRefAttributes), options));
});

/**
 * [Accordion design guidelines](https://beam.kiteds.com/web/components/accordion/design.html)
 */
const KiteAccordionRow = forwardRef((_a, ref) => {
    var { active, callback, children, disabled, id, index, label, labelType, status, title } = _a, other = __rest(_a, ["active", "callback", "children", "disabled", "id", "index", "label", "labelType", "status", "title"]);
    const normalId = useNormalId();
    const contentRef = useRef(null);
    const contentWindowRef = useRef(null);
    const titleId = `kite-accordion-row__title-${normalId}`;
    const [isClosing, setIsClosing] = useState(false);
    if (!!labelType) {
        if (labelType === 'alert') {
            console.warn('Property type has been deprecated. Use status=negative');
        }
        else if (labelType === 'success') {
            console.warn('Property type has been deprecated. Use status=positive');
        }
    }
    /**
     * If applying the expand animation, complete animation after the call back to Accordion complete and sends a new active row
     */
    useLayoutEffect(() => {
        if (contentRef.current) {
            if (active) {
                applyAnimation(true);
            }
        }
    }, [active]);
    /**
     * If applying the collapse animation, complete animation before sending to call back to Accordion to set new active row
     */
    const transitionContent = () => {
        var _a;
        if (active) {
            setIsClosing(true);
            applyAnimation(false);
            callback(index);
        }
        else {
            if (contentWindowRef.current) {
                // if not active, set max-height to 0 so animation will trigger again
                (_a = contentWindowRef === null || contentWindowRef === void 0 ? void 0 : contentWindowRef.current) === null || _a === void 0 ? void 0 : _a.setAttribute('style', `--max-height: 0px;`);
            }
            callback(index);
        }
    };
    /**
     * Apply FLIP animation when expanding/collapsing the accordion
     * Content inside the accordion can change dynamically, so max-height is set to auto
     * after the animation completes. For the FLIP animation to work correctly there needs to be
     * an explicit max-height, so before the collapse animation we set the height to an explicit value.
     * @param expand - if true, apply expand animation, if false, apply collapse animation
     */
    const applyAnimation = (expand) => {
        var _a;
        const contentSizeAfter = contentRef.current.getBoundingClientRect();
        const contentHeight = contentSizeAfter.height;
        // Before collapse animation set --max-height to a value, so animation looks smooth.
        if (!expand) {
            (_a = contentWindowRef === null || contentWindowRef === void 0 ? void 0 : contentWindowRef.current) === null || _a === void 0 ? void 0 : _a.setAttribute('style', `--max-height: ${contentHeight}px;`);
        }
        contentWindowRef.current.addEventListener('transitionend', () => {
            var _a;
            contentWindowRef.current.classList.remove('kite-accordion-row__content-window--transitioning');
            if (!expand) {
                setIsClosing(false);
            }
            else {
                // Set --max-height to auto so the content height will update if content of the accordion changes dynamically
                (_a = contentWindowRef === null || contentWindowRef === void 0 ? void 0 : contentWindowRef.current) === null || _a === void 0 ? void 0 : _a.setAttribute('style', `--max-height: auto;`);
            }
        }, { once: true });
        let initialYPos;
        let endYPos;
        let windowMaxHeight;
        let opacity;
        if (expand) {
            initialYPos = `-${contentHeight}px`;
            endYPos = '0';
            windowMaxHeight = `${contentHeight}px`;
            opacity = 1;
        }
        else {
            initialYPos = '0';
            endYPos = `-${contentHeight}px`;
            windowMaxHeight = '0';
            opacity = 0;
        }
        contentWindowRef.current.classList.add('kite-accordion-row__content-window--transitioning');
        requestAnimationFrame(() => {
            var _a, _b, _c;
            (_a = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) === null || _a === void 0 ? void 0 : _a.setAttribute('style', `transform: translate(0, ${initialYPos});`);
            (_b = contentWindowRef === null || contentWindowRef === void 0 ? void 0 : contentWindowRef.current) === null || _b === void 0 ? void 0 : _b.setAttribute('style', `--max-height: ${windowMaxHeight};`);
            (_c = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) === null || _c === void 0 ? void 0 : _c.setAttribute('style', `--opacity: ${opacity};transform: translate(0, ${endYPos});`);
        });
    };
    return (React.createElement("li", { className: classNames({
            'kite-accordion-row': true,
            'kite-accordion-row--expanded': active || isClosing,
        }), id: id },
        React.createElement("button", Object.assign({ className: "kite-accordion-row__button", disabled: disabled, "aria-expanded": active, type: "button", ref: ref, onClick: transitionContent }, other),
            React.createElement("span", { className: "kite-accordion-row__button-inner" },
                React.createElement("span", { className: "kite-title", id: titleId, role: "presentation" }, title),
                label && (React.createElement("span", { className: classNames({
                        'kite-accordion-row__label': true,
                        [`kite-accordion-row__label--alert`]: labelType === 'alert',
                        [`kite-accordion-row__label--negative`]: status === 'negative',
                        [`kite-accordion-row__label--success`]: labelType === 'success',
                        [`kite-accordion-row__label--positive`]: status === 'positive',
                    }), role: "presentation" }, label)),
                React.createElement("span", { className: "kite-accordion-row__icon", "aria-hidden": "true" },
                    React.createElement(KiteIcon, { icon: "ki-chevron-down" })))),
        React.createElement("div", { ref: contentWindowRef, role: "region", className: "kite-accordion-row__content-window", "aria-hidden": !active, "aria-labelledby": titleId },
            React.createElement("div", { ref: contentRef, className: "kite-accordion-row__content" }, children))));
});

const KiteFeedback = forwardRef((_a, ref) => {
    var { errorMessage, componentName, icon, children } = _a, other = __rest(_a, ["errorMessage", "componentName", "icon", "children"]);
    return (React.createElement(React.Fragment, null, errorMessage && (React.createElement("span", Object.assign({ ref: ref, className: `${componentName}__feedback` }, other),
        icon && React.createElement(KiteIcon, { inline: true, icon: icon }),
        errorMessage))));
});

/**
 * A controlled checkbox input. The `onChange` function should update the `checked` prop.
 * [View the design specifications](https://beam.kiteds.com/web/components/checkbox/design.html)
 */
const KiteCheckbox = forwardRef((_a, ref) => {
    var { checked, className, disabled, errorMessage, id, label, name, indeterminate, onChange, parentCheckbox, ariaDescribedBy } = _a, other = __rest(_a, ["checked", "className", "disabled", "errorMessage", "id", "label", "name", "indeterminate", "onChange", "parentCheckbox", "ariaDescribedBy"]);
    const normalId = useNormalId();
    const checkboxId = id ? id : `rk-checkbox-${normalId}`;
    const [triStateValue, setTriStateValue] = useState(null);
    useEffect(() => {
        if (indeterminate) {
            setTriStateValue('mixed');
        }
        else {
            setTriStateValue('');
        }
    }, [checked, indeterminate]);
    return (React.createElement("div", { className: classNames(`kite-checkbox`, {
            [className]: className,
            'kite-checkbox--tristate': indeterminate,
            'kite-checkbox--disabled': indeterminate && disabled,
            'kite-checkbox--checked': checked && !indeterminate,
            'kite-checkbox--mixed': indeterminate,
            'kite-checkbox--error': errorMessage,
        }) },
        React.createElement("label", { className: "kite-checkbox__label", htmlFor: checkboxId, id: `${checkboxId}-label` },
            React.createElement("input", Object.assign({ type: "checkbox", id: checkboxId, className: "kite-checkbox__control", name: name, checked: checked, onChange: onChange, disabled: disabled, "aria-describedby": errorMessage || ariaDescribedBy
                    ? ariaDescribedBy || `${checkboxId}-error`
                    : null, ref: ref, "aria-checked": parentCheckbox ? triStateValue : null, value: triStateValue ? triStateValue : '' }, other)),
            React.createElement("span", { className: classNames(`kite-checkbox__indicator`, {
                    'kite-checkbox__indicator--hidden': !checked && !indeterminate,
                }) },
                indeterminate && (React.createElement(KiteIcon, { icon: "ki-minus", className: "kite-checkbox__mixed-icon" })),
                !indeterminate && (React.createElement(KiteIcon, { icon: "ki-checkmark", className: "kite-checkbox__checked-icon" }))),
            React.createElement("span", { className: "kite-checkbox__description" }, label),
            React.createElement("span", { className: "kite-assistive-text" }, parentCheckbox ? 'Select All' : '')),
        React.createElement(KiteFeedback, { id: `${id}-error`, errorMessage: errorMessage, componentName: "kite-checkbox", icon: "ki-caution-circle-f" })));
});
KiteCheckbox.displayName = 'KiteCheckbox';

function Mutator(context, ...keys) {
    return class $ {
        constructor(...args) {
            this.configuration = [this];
            this.context = {};
            this.$context = context;
            keys.forEach((x) => {
                Object.defineProperty(this.context, x, {
                    get: () => {
                        return this.$context[x];
                    },
                    set: (v) => {
                        //console.log('mutator set ', x, ' to ', v);
                        this.$context[x] = v;
                        if (this.output) {
                            this.output([[x], this.$context]);
                        }
                    },
                    enumerable: true,
                });
            });
        }
    };
}
function Enumerator(next) {
    var _$_next, _a;
    return _a = class $ {
            constructor(...args) {
                _$_next.set(this, void 0);
                this.lines = 0;
                __classPrivateFieldSet(this, _$_next, new next(...args), "f");
                __classPrivateFieldGet(this, _$_next, "f").output = (x) => (this.input = x);
                this.configuration = [...__classPrivateFieldGet(this, _$_next, "f").configuration, this];
            }
            get context() {
                return __classPrivateFieldGet(this, _$_next, "f").context;
            }
            set input(value) {
                if (this.output) {
                    // console.log('enumerator ', this, value);
                    this.output(new Array(this.lines)
                        .fill(undefined)
                        .map((v, idx) => [idx, value]));
                }
            }
            register() {
                return this.lines++;
            }
        },
        _$_next = new WeakMap(),
        _a;
}
function Filter(next) {
    var _$_next_1, _a;
    return _a = class $ {
            constructor(...args) {
                _$_next_1.set(this, void 0);
                __classPrivateFieldSet(this, _$_next_1, new next(...args), "f");
                __classPrivateFieldGet(this, _$_next_1, "f").output = (x) => (this.input = x);
                this.configuration = [...__classPrivateFieldGet(this, _$_next_1, "f").configuration, this];
            }
            get context() {
                return __classPrivateFieldGet(this, _$_next_1, "f").context;
            }
            set input(value) {
                if (this.output && this.filter) {
                    // console.log('filter ', this, value);
                    this.output(this.filter(value));
                }
            }
        },
        _$_next_1 = new WeakMap(),
        _a;
}
function Transformer(next) {
    return () => { var _$_next_2, _a; return _a = class $ {
            constructor(...args) {
                _$_next_2.set(this, void 0);
                __classPrivateFieldSet(this, _$_next_2, new next(...args), "f");
                __classPrivateFieldGet(this, _$_next_2, "f").output = (x) => (this.input = x);
                this.configuration = [...__classPrivateFieldGet(this, _$_next_2, "f").configuration, this];
            }
            get context() {
                return __classPrivateFieldGet(this, _$_next_2, "f").context;
            }
            set input(value) {
                if (this.output && this.transform) {
                    // console.log('transformer ', this, value);
                    this.output(this.transform(value));
                }
            }
        },
        _$_next_2 = new WeakMap(),
        _a; };
}
function Accumulator(next) {
    var _$_next_3, _a;
    return _a = class $ {
            constructor(...args) {
                _$_next_3.set(this, void 0);
                __classPrivateFieldSet(this, _$_next_3, new next(...args), "f");
                __classPrivateFieldGet(this, _$_next_3, "f").output = (x) => (this.input = x);
                this.configuration = [...__classPrivateFieldGet(this, _$_next_3, "f").configuration, this];
            }
            get context() {
                return __classPrivateFieldGet(this, _$_next_3, "f").context;
            }
            set input(value) {
                if (this.output && this.accumulate) {
                    // console.log('accumulator ', this, value);
                    this.output(this.accumulate(value));
                }
            }
        },
        _$_next_3 = new WeakMap(),
        _a;
}
function Selector(next) {
    return () => { var _$_next_4, _a; return _a = class $ {
            constructor(...args) {
                _$_next_4.set(this, void 0);
                __classPrivateFieldSet(this, _$_next_4, new next(...args), "f");
                __classPrivateFieldGet(this, _$_next_4, "f").output = (x) => (this.input = x);
                this.configuration = [...__classPrivateFieldGet(this, _$_next_4, "f").configuration, this];
            }
            get context() {
                return __classPrivateFieldGet(this, _$_next_4, "f").context;
            }
            set input(value) {
                if (this.output && this.select) {
                    // console.log('selector ', this, value);
                    this.output(this.select(value));
                }
            }
        },
        _$_next_4 = new WeakMap(),
        _a; };
}
// mutator
// enumerator
// filter
// transformer
// accumulator
// selector

var _ConditionalStyleOverride_target, _ConditionalStyleOverride_valueOverrides, _ConditionalStyleOverride_valueOriginals, _ConditionalStyleOverride_override;
class ConditionalStyleOverride {
    // there is an opportunity here to use mutation observer to detect when our overridden value is set to something else. Don't need it yet, but it's an opportunity should the need arise
    constructor(target, ...overrides) {
        _ConditionalStyleOverride_target.set(this, void 0);
        _ConditionalStyleOverride_valueOverrides.set(this, new Map());
        _ConditionalStyleOverride_valueOriginals.set(this, new Map());
        _ConditionalStyleOverride_override.set(this, false);
        __classPrivateFieldSet(this, _ConditionalStyleOverride_target, target, "f");
        overrides.forEach((x) => __classPrivateFieldGet(this, _ConditionalStyleOverride_valueOverrides, "f").set(x[0], x[1]));
    }
    get override() {
        return __classPrivateFieldGet(this, _ConditionalStyleOverride_override, "f");
    }
    set override(value) {
        var _a;
        if (__classPrivateFieldGet(this, _ConditionalStyleOverride_override, "f") == value) {
            return;
        }
        __classPrivateFieldSet(this, _ConditionalStyleOverride_override, value, "f");
        const overrides = Array.from(__classPrivateFieldGet(this, _ConditionalStyleOverride_valueOverrides, "f").entries());
        if (value) {
            for (let i = 0; i < overrides.length; ++i) {
                __classPrivateFieldGet(this, _ConditionalStyleOverride_valueOriginals, "f").set(overrides[i][0], __classPrivateFieldGet(this, _ConditionalStyleOverride_target, "f").style.getPropertyValue(overrides[i][0]));
                __classPrivateFieldGet(this, _ConditionalStyleOverride_target, "f").style.setProperty(overrides[i][0], overrides[i][1]);
            }
        }
        else {
            for (let i = 0; i < overrides.length; ++i) {
                __classPrivateFieldGet(this, _ConditionalStyleOverride_target, "f").style.setProperty(overrides[i][0], (_a = __classPrivateFieldGet(this, _ConditionalStyleOverride_valueOriginals, "f").get(overrides[i][0])) !== null && _a !== void 0 ? _a : null);
            }
        }
    }
}
_ConditionalStyleOverride_target = new WeakMap(), _ConditionalStyleOverride_valueOverrides = new WeakMap(), _ConditionalStyleOverride_valueOriginals = new WeakMap(), _ConditionalStyleOverride_override = new WeakMap();

createContext(undefined);
const AnimationService = () => {
    let instance;
    let listeners = [];
    const createInstance = () => {
        return {
            attach(handler) {
                // handler that returns true wants another frame
                listeners.push(handler);
                this.queue();
                return () => listeners.splice(listeners.findIndex((x) => x === handler), 1);
            },
            clear() {
                listeners = [];
            },
            queue() {
                window === null || window === void 0 ? void 0 : window.requestAnimationFrame(this.frame.bind(this));
            },
            frame(time) {
                listeners = listeners.filter((x) => x(time));
                if (listeners.length > 0) {
                    this.queue();
                }
            },
        };
    };
    return {
        getInstance: function () {
            if (!instance) {
                instance = createInstance();
            }
            return instance;
        },
    };
};

var _Spotlight_attachments;
function followAndTransform(from, next, stop, transform) {
    // meant to follow a linked list and transform nodes
    let current = next(from);
    const retVal = [];
    while (!stop(current)) {
        const result = transform(current);
        if (result != null) {
            retVal.push(result);
        }
        current = next(current);
    }
    return retVal;
}
class Spotlight {
    constructor() {
        // we assume that in this context, we do not have to worry about DOM nodes being introduced or removed between us and root.
        // we assume that, when this is being used in the manner in which we expect, we do not have to track attempts to change aria-hidden in nodes we have hidden, as they are meant to be inert.
        // We should be able to track these changes if future requirements require that.
        this.attached = false;
        _Spotlight_attachments.set(this, []);
    }
    attach(host) {
        // we're looking to aria-hidden every node in the tree that isn't this, or a direct ancestor.
        this.detach();
        __classPrivateFieldGet(this, _Spotlight_attachments, "f").push(...this._ascend(host)
            .map(this._spread)
            .reduce((acc, x) => {
            acc.push(...x);
            return acc;
        }, []));
        this.attached = true;
    }
    _ascend(from) {
        const retVal = [];
        let current = from;
        while (current != null) {
            retVal.push(current);
            current = current.parentElement;
        }
        return retVal;
    }
    _spread(current) {
        const transform = (x) => {
            if (x && x.nodeType == 1) {
                // element node
                const elmt = x, revertTo = elmt.ariaHidden;
                elmt.ariaHidden = 'true';
                return (() => (elmt.ariaHidden = revertTo));
            }
            else {
                return null;
            }
        };
        return [
            ...followAndTransform(current, (x) => { var _a; return (_a = x === null || x === void 0 ? void 0 : x.nextSibling) !== null && _a !== void 0 ? _a : null; }, (x) => x == null, transform), // track forward
            ...followAndTransform(current, (x) => { var _a; return (_a = x === null || x === void 0 ? void 0 : x.previousSibling) !== null && _a !== void 0 ? _a : null; }, (x) => x == null, transform), // track back
        ];
    }
    detach() {
        // we are looking to restore the nodes we've hidden
        if (this.attached) {
            __classPrivateFieldGet(this, _Spotlight_attachments, "f").forEach((x) => x());
            this.attached = false;
        }
    }
}
_Spotlight_attachments = new WeakMap();

var _OverlayState_memory, _OverlayProposal_memory;
class OverlayState {
    constructor() {
        _OverlayState_memory.set(this, new Float32Array(8));
    }
    get originPosition() {
        return [__classPrivateFieldGet(this, _OverlayState_memory, "f")[0], __classPrivateFieldGet(this, _OverlayState_memory, "f")[1]];
    }
    set originPosition(value) {
        [__classPrivateFieldGet(this, _OverlayState_memory, "f")[0], __classPrivateFieldGet(this, _OverlayState_memory, "f")[1]] = value;
    }
    get originSize() {
        return [__classPrivateFieldGet(this, _OverlayState_memory, "f")[2], __classPrivateFieldGet(this, _OverlayState_memory, "f")[3]];
    }
    set originSize(value) {
        [__classPrivateFieldGet(this, _OverlayState_memory, "f")[2], __classPrivateFieldGet(this, _OverlayState_memory, "f")[3]] = value;
    }
    get contentSize() {
        return [__classPrivateFieldGet(this, _OverlayState_memory, "f")[4], __classPrivateFieldGet(this, _OverlayState_memory, "f")[5]];
    }
    set contentSize(value) {
        [__classPrivateFieldGet(this, _OverlayState_memory, "f")[4], __classPrivateFieldGet(this, _OverlayState_memory, "f")[5]] = value;
    }
    get viewportSize() {
        return [__classPrivateFieldGet(this, _OverlayState_memory, "f")[6], __classPrivateFieldGet(this, _OverlayState_memory, "f")[7]];
    }
    set viewportSize(value) {
        [__classPrivateFieldGet(this, _OverlayState_memory, "f")[6], __classPrivateFieldGet(this, _OverlayState_memory, "f")[7]] = value;
    }
}
_OverlayState_memory = new WeakMap();
class OverlayProposal {
    static equals(a, b) {
        var _a, _b, _c, _d, _e, _f;
        if (__classPrivateFieldGet(a, _OverlayProposal_memory, "f").length != __classPrivateFieldGet(b, _OverlayProposal_memory, "f").length)
            return false;
        for (let i = 0; i < __classPrivateFieldGet(a, _OverlayProposal_memory, "f").length; ++i) {
            if (__classPrivateFieldGet(a, _OverlayProposal_memory, "f")[i] != __classPrivateFieldGet(b, _OverlayProposal_memory, "f")[i])
                return false;
        }
        if (((_a = a.backdrop) === null || _a === void 0 ? void 0 : _a.class) != ((_b = b.backdrop) === null || _b === void 0 ? void 0 : _b.class))
            return false;
        if (((_c = a.backdrop) === null || _c === void 0 ? void 0 : _c.lockScroll) != ((_d = b.backdrop) === null || _d === void 0 ? void 0 : _d.lockScroll))
            return false;
        if (((_e = a.container) === null || _e === void 0 ? void 0 : _e.class) != ((_f = b.container) === null || _f === void 0 ? void 0 : _f.class))
            return false;
        if (a.spotlight != b.spotlight)
            return false;
        if (a.context != b.context)
            return false;
        return true;
    }
    constructor(position, size, spotlight, container, backdrop, context) {
        this.spotlight = false;
        _OverlayProposal_memory.set(this, new Float32Array(4));
        if (position)
            this.position = position;
        if (size) {
            this.size = size;
        }
        if (backdrop)
            this.backdrop = backdrop;
        if (spotlight)
            this.spotlight = spotlight;
        if (container)
            this.container = container;
        if (context)
            this.context = context;
    }
    get position() {
        return [__classPrivateFieldGet(this, _OverlayProposal_memory, "f")[0], __classPrivateFieldGet(this, _OverlayProposal_memory, "f")[1]];
    }
    set position(value) {
        [__classPrivateFieldGet(this, _OverlayProposal_memory, "f")[0], __classPrivateFieldGet(this, _OverlayProposal_memory, "f")[1]] = value;
    }
    get size() {
        return [__classPrivateFieldGet(this, _OverlayProposal_memory, "f")[2], __classPrivateFieldGet(this, _OverlayProposal_memory, "f")[3]];
    }
    set size(value) {
        [__classPrivateFieldGet(this, _OverlayProposal_memory, "f")[2], __classPrivateFieldGet(this, _OverlayProposal_memory, "f")[3]] = value;
    }
}
_OverlayProposal_memory = new WeakMap();
class OverlayStrategy {
}
const Processor = () => Selector(Accumulator(Transformer(Filter(Enumerator(Mutator(new OverlayState(), 'originPosition', 'originSize', 'viewportSize', 'contentSize', 'context'))))()))();
const AttachedOverlay = ({ children, clickOutside, context, contextChange, initialized, origin, originChange, shouldReturnFocus = true, strategies, disableOverlay = false, }) => {
    const animationService = AnimationService().getInstance();
    const _strategies = new Map();
    const proposals = new Map();
    const overlayRef = useRef(null);
    const contentRef = useRef(null);
    const backdropRef = useRef(null);
    const containerRef = useRef(null);
    const spotlightRef = useRef(new Spotlight());
    const bodyScrollOverrideRef = useRef(typeof document !== 'undefined'
        ? new ConditionalStyleOverride(document.body, ['overflow', 'hidden'])
        : null);
    const [attached, setAttached] = useState(false);
    const [core, setCore] = useState(null);
    const [activeProposal, setActiveProposal] = useState(null);
    const [init, setInit] = useState(false);
    const [originResizeObserver, setOriginResizeObserver] = useState(null);
    const [contentResizeObserver, setContentResizeObserver] = useState(null);
    const [overlayDimensionCache, setOverlayDimensionCache] = useState(undefined);
    const [overlayDimensions, setOverlayDimensions] = useState(null);
    let debounce;
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f;
        if ((overlayDimensions &&
            overlayDimensionCache &&
            OverlayProposal.equals(overlayDimensions, overlayDimensionCache)) ||
            !overlayDimensions) {
            return () => clearTimeout(debounce);
        }
        setOverlayDimensionCache(overlayDimensions);
        if (overlayRef && overlayRef.current && overlayRef.current.style) {
            if (overlayDimensions &&
                ((overlayDimensions.spotlight && !(spotlightRef === null || spotlightRef === void 0 ? void 0 : spotlightRef.current.attached)) ||
                    (!overlayDimensions.spotlight && (spotlightRef === null || spotlightRef === void 0 ? void 0 : spotlightRef.current.attached)))) {
                if (overlayDimensions.spotlight) {
                    spotlightRef.current.attach(containerRef.current);
                }
            }
            overlayRef.current.style.transform = `translate(${(_a = overlayDimensions === null || overlayDimensions === void 0 ? void 0 : overlayDimensions.position[0]) !== null && _a !== void 0 ? _a : 0}px, ${(_b = overlayDimensions === null || overlayDimensions === void 0 ? void 0 : overlayDimensions.position[1]) !== null && _b !== void 0 ? _b : 0}px)`;
            overlayRef.current.style.width = `${(_c = overlayDimensions === null || overlayDimensions === void 0 ? void 0 : overlayDimensions.size[0]) !== null && _c !== void 0 ? _c : 0}px`;
            overlayRef.current.style.height = `${(_d = overlayDimensions === null || overlayDimensions === void 0 ? void 0 : overlayDimensions.size[1]) !== null && _d !== void 0 ? _d : 0}px`;
            if (overlayDimensions && overlayDimensions.context !== activeProposal) {
                setActiveProposal(overlayDimensions.context);
            }
            if ((overlayDimensions === null || overlayDimensions === void 0 ? void 0 : overlayDimensions.container) && overlayDimensions.container.class) {
                if (!init) {
                    // Set a debounce so if another proposal comes in, use the newer proposal instead
                    // This exists because components like popover set a classList from the proposal
                    // and those new css classes cause a new proposal to occur with updated positioning and sizing
                    debounce = setTimeout(() => {
                        setInit(true);
                    }, 10);
                }
                overlayRef.current.classList.value = '';
                if (Array.isArray(overlayDimensions.container.class)) {
                    overlayDimensions.container.class.forEach((cssClass) => {
                        overlayRef.current.classList.add(cssClass);
                    });
                }
                if (typeof overlayDimensions.container.class === 'string') {
                    overlayRef.current.classList.add(overlayDimensions.container.class);
                }
            }
            else if ((overlayDimensions === null || overlayDimensions === void 0 ? void 0 : overlayDimensions.container) &&
                overlayDimensions.container.contentClass) {
                if (!init) {
                    // Set a debounce so if another proposal comes in, use the newer proposal instead
                    // This exists because components like popover set a classList from the proposal
                    // and those new css classes cause a new proposal to occur with updated positioning and sizing
                    debounce = setTimeout(() => {
                        setInit(true);
                    }, 10);
                }
                contentRef.current.classList.value = '';
                if (Array.isArray(overlayDimensions.container.contentClass)) {
                    overlayDimensions.container.contentClass.forEach((cssClass) => {
                        contentRef.current.classList.add(cssClass);
                    });
                }
                if (typeof overlayDimensions.container.contentClass === 'string') {
                    contentRef.current.classList.add(overlayDimensions.container.contentClass);
                }
            }
            else {
                contentRef.current.classList.value = '';
                setInit(true);
            }
            if (overlayDimensions === null || overlayDimensions === void 0 ? void 0 : overlayDimensions.backdrop) {
                bodyScrollOverrideRef.current.override =
                    overlayDimensions.backdrop.lockScroll && attached;
                if (!((_e = backdropRef === null || backdropRef === void 0 ? void 0 : backdropRef.current) === null || _e === void 0 ? void 0 : _e.classList.contains(overlayDimensions.backdrop.class))) {
                    (_f = backdropRef === null || backdropRef === void 0 ? void 0 : backdropRef.current) === null || _f === void 0 ? void 0 : _f.classList.add(overlayDimensions.backdrop.class);
                }
            }
            else {
                bodyScrollOverrideRef.current.override = false;
                if (backdropRef === null || backdropRef === void 0 ? void 0 : backdropRef.current) {
                    backdropRef.current.classList.value = '';
                }
            }
        }
        return () => clearTimeout(debounce);
    }, [overlayDimensions]);
    useEffect(() => {
        if ((overlayDimensions === null || overlayDimensions === void 0 ? void 0 : overlayDimensions.position[0]) !== -1 && (overlayRef === null || overlayRef === void 0 ? void 0 : overlayRef.current)) {
            if (init) {
                overlayRef.current.style.visibility = 'visible';
                if (!!initialized) {
                    initialized();
                }
            }
        }
    }, [init]);
    const resizeHandler = () => {
        if (attached) {
            animationService.attach(() => {
                var _a, _b;
                const viewportSize = window.visualViewport;
                core.context.viewportSize = [
                    (_a = viewportSize === null || viewportSize === void 0 ? void 0 : viewportSize.width) !== null && _a !== void 0 ? _a : 0,
                    (_b = viewportSize === null || viewportSize === void 0 ? void 0 : viewportSize.height) !== null && _b !== void 0 ? _b : 0,
                ];
                const rect = origin.getBoundingClientRect();
                OriginPositionSync(rect);
                OriginSizeSync(rect);
                return false;
            });
        }
    };
    const scrollHandler = () => {
        if (!scrolling && origin) {
            scrolling = true;
            animationService.attach(() => {
                const rect = origin.getBoundingClientRect(), diff = OriginPositionSync(rect) || OriginSizeSync(rect);
                if (!diff) {
                    scrolling = false;
                }
                return diff;
            });
        }
    };
    useEffect(() => {
        window.addEventListener('resize', resizeHandler);
        window.addEventListener('scroll', scrollHandler, { capture: true });
        return () => {
            window.removeEventListener('resize', resizeHandler);
            window.removeEventListener('scroll', scrollHandler, { capture: true });
        };
    }, [attached]);
    let scrolling = false;
    const viewportIntersection = (proposal) => {
        const viewportSize = core.context.viewportSize;
        const left = Math.max(0, Math.min(proposal.position[0] + proposal.size[0], viewportSize[0]) -
            Math.max(proposal.position[0], 0));
        const right = Math.max(0, Math.min(proposal.position[1] + proposal.size[1], viewportSize[1]) -
            Math.max(proposal.position[1], 0));
        const SI = left * right;
        const SA = proposal.size[0] * proposal.size[1];
        const SB = viewportSize[0] * viewportSize[1];
        const SU = SA + SB - SI;
        return SI / SU;
    };
    const OriginSizeSync = (rect) => {
        const newSize = [rect.width, rect.height];
        if (core) {
            const diff = core.context.originSize[0] != newSize[0] ||
                core.context.originSize[1] != newSize[1];
            if (diff) {
                core.context.originSize = newSize;
            }
            return diff;
        }
        return false;
    };
    const OriginPositionSync = (rect) => {
        var _a, _b, _c, _d, _e;
        const backdropRect = (_a = backdropRef === null || backdropRef === void 0 ? void 0 : backdropRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        const newPosition = [
            rect.x - ((_b = backdropRect === null || backdropRect === void 0 ? void 0 : backdropRect.left) !== null && _b !== void 0 ? _b : 0),
            rect.y - ((_c = backdropRect === null || backdropRect === void 0 ? void 0 : backdropRect.top) !== null && _c !== void 0 ? _c : 0),
        ];
        if (core) {
            const diff = core.context.originPosition[0] - ((_d = backdropRect === null || backdropRect === void 0 ? void 0 : backdropRect.left) !== null && _d !== void 0 ? _d : 0) !=
                newPosition[0] ||
                core.context.originPosition[1] - ((_e = backdropRect === null || backdropRect === void 0 ? void 0 : backdropRect.top) !== null && _e !== void 0 ? _e : 0) !=
                    newPosition[1];
            if (diff) {
                core.context.originPosition = newPosition;
            }
            return diff;
        }
        return false;
    };
    const ContentSizeSync = (elmt) => {
        const newSize = [elmt.scrollWidth, elmt.scrollHeight], // TODO -causes error on docsite unless i do a full reload after a change
        diff = core.context.contentSize[0] != newSize[0] ||
            core.context.contentSize[1] != newSize[1];
        if (diff) {
            core.context.contentSize = newSize;
        }
    };
    const ViewportSizeSync = () => {
        var _a, _b;
        const viewportSize = window.visualViewport, newSize = [(_a = viewportSize === null || viewportSize === void 0 ? void 0 : viewportSize.width) !== null && _a !== void 0 ? _a : 0, (_b = viewportSize === null || viewportSize === void 0 ? void 0 : viewportSize.height) !== null && _b !== void 0 ? _b : 0], diff = core.context.viewportSize[0] != newSize[0] ||
            core.context.viewportSize[1] != newSize[1];
        if (diff) {
            core.context.viewportSize = newSize;
        }
    };
    useEffect(() => {
        setCore(new (Processor())());
        spotlightRef.current = new Spotlight();
        return () => {
            if (spotlightRef === null || spotlightRef === void 0 ? void 0 : spotlightRef.current.attached) {
                spotlightRef.current.detach();
            }
            if (animationService) {
                animationService.clear();
            }
            setCore(null);
        };
    }, []);
    useEffect(() => {
        if (context && core) {
            if (core.context.context != context) {
                core.context.context = context;
                if (!!contextChange) {
                    contextChange(context);
                }
            }
        }
        if ((contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) && core) {
            strategies === null || strategies === void 0 ? void 0 : strategies.forEach((x) => _strategies.set(core.configuration[1].register(), x));
        }
        if (core) {
            core.output = (x) => {
                setOverlayDimensions(x ? x[1] : undefined);
            };
            core.configuration[2].filter = (x) => x.filter((y) => {
                var _a, _b, _c;
                return (_c = (_b = (_a = _strategies.get(y[0])) === null || _a === void 0 ? void 0 : _a.reactTo) === null || _b === void 0 ? void 0 : _b.some((z) => y[1][0].includes(z))) !== null && _c !== void 0 ? _c : false;
            }); // strategy is listening for type of change provided
            core.configuration[3].transform = (x) => x.map((y) => { var _a, _b; return [y[0], (_b = (_a = _strategies.get(y[0])) === null || _a === void 0 ? void 0 : _a.propose(y[1][1])) !== null && _b !== void 0 ? _b : false]; });
            core.configuration[4].accumulate = (x) => {
                x.map((y) => {
                    if (y[1] !== undefined)
                        proposals.set(y[0], y[1]);
                    return false;
                });
                return Array.from(proposals.entries());
            };
            core.configuration[5].select = (x) => {
                const retVal = x.filter((y) => y[1] != false)
                    .map((x) => [viewportIntersection(x[1]), ...x])
                    .sort((a, b) => b[0] - a[0])
                    .sort((a, b) => b[0] - a[0])
                    .map((x) => [
                    x[1],
                    x[2],
                ])[0];
                return retVal;
            };
        }
        if (core) {
            setOriginResizeObserver(new ResizeObserver(originResizeObserved.bind(undefined)));
            setContentResizeObserver(new ResizeObserver(contentResizeObserved.bind(undefined)));
        }
    }, [context, core, contentRef]);
    useEffect(() => {
        if (originResizeObserver &&
            origin &&
            animationService &&
            !attached &&
            core) {
            attach(origin);
        }
        return () => {
            if (origin) {
                detach(origin);
            }
            if (originResizeObserver) {
                originResizeObserver.disconnect();
            }
        };
    }, [originResizeObserver]);
    const outsideClickTest = (e) => {
        var _a;
        if (e.target &&
            (contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) &&
            !((_a = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)) &&
            !!clickOutside) {
            clickOutside(e);
        }
    };
    const attach = (origin) => {
        if (!attached) {
            setAttached(true);
            animationService.attach(() => {
                if (contentRef && (contentRef === null || contentRef === void 0 ? void 0 : contentRef.current)) {
                    contentResizeObserver.observe(contentRef === null || contentRef === void 0 ? void 0 : contentRef.current);
                }
                originResizeObserver.observe(origin);
                ancestorResizeWatcher();
                const rect = origin.getBoundingClientRect();
                OriginPositionSync(rect);
                OriginSizeSync(rect);
                if (contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) {
                    ContentSizeSync(contentRef.current);
                }
                ViewportSizeSync();
                core.context.context = context; // bump it
                return false;
            });
        }
    };
    useEffect(() => {
        if (attached) {
            document.addEventListener('click', outsideClickTest);
        }
        return () => {
            document.removeEventListener('click', outsideClickTest);
        };
    }, [attached]);
    const detach = (origin) => {
        setAttached(false);
        if (origin && originResizeObserver) {
            originResizeObserver.unobserve(origin); // detach
        }
        setOverlayDimensionCache(undefined);
        bodyScrollOverrideRef.current.override = false;
        if (!shouldReturnFocus)
            return;
        origin === null || origin === void 0 ? void 0 : origin.focus();
    };
    const ancestorResizeWatcher = () => {
        if (origin !== undefined) {
            let current = origin;
            while (current.parentElement != null) {
                originResizeObserver.observe(current);
                current = current.parentElement;
            }
        }
    };
    const originResizeObserved = (entries, observer) => {
        animationService.attach(() => {
            if (origin !== undefined) {
                const rect = origin.getBoundingClientRect();
                OriginPositionSync(rect);
                OriginSizeSync(rect);
            }
            return false;
        });
    };
    const contentResizeObserved = (entries, observer) => {
        animationService.attach(() => {
            ContentSizeSync(entries[0].target);
            return false;
        });
    };
    let transform;
    return !disableOverlay ? (React.createElement("div", { ref: containerRef },
        React.createElement("div", { ref: backdropRef, style: {
                position: 'fixed',
                top: '0px',
                left: '0px',
                pointerEvents: 'auto',
            }, onClick: null }),
        React.createElement("div", { ref: overlayRef, className: "rk--attached-overlay", style: {
                position: 'fixed',
                top: '0px',
                left: '-1px',
                willChange: transform,
                visibility: 'hidden',
            } },
            React.createElement("div", { ref: contentRef }, children)))) : (React.createElement(React.Fragment, null, children));
};

const KiteComboboxSearchInput = forwardRef((_a, ref) => {
    var { ariaActiveDescendant, ariaDescribedBy, ariaRoleDescription, id, inputClassName, onBlur = () => undefined, onKeyDown, onSearch, placeholder = 'Search...', searchInputRef, value } = _a; __rest(_a, ["ariaActiveDescendant", "ariaDescribedBy", "ariaRoleDescription", "id", "inputClassName", "onBlur", "onKeyDown", "onSearch", "placeholder", "searchInputRef", "value"]);
    const searchHandler = (e) => {
        onSearch(e.target.value);
    };
    const clearSearch = (e) => {
        var _a;
        e.stopPropagation();
        (_a = searchInputRef === null || searchInputRef === void 0 ? void 0 : searchInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        onSearch('');
    };
    return (React.createElement("div", { ref: ref, className: "kite-search-input" },
        React.createElement("div", { id: "search-input", className: "kite-search-input__input-group" },
            React.createElement("div", { className: "kite-search-input__input-addon" },
                React.createElement(KiteIcon, { icon: "ki-search" })),
            React.createElement("input", { ref: searchInputRef, type: "search", role: "combobox", className: classNames('kite-search-input__input', {
                    [inputClassName]: inputClassName,
                }), id: id, onKeyDown: onKeyDown, onBlur: onBlur, placeholder: placeholder, onChange: searchHandler, value: value, "aria-roledescription": ariaRoleDescription, "aria-activedescendant": ariaActiveDescendant, "aria-describedby": ariaDescribedBy, autoComplete: "off" }),
            (value === null || value === void 0 ? void 0 : value.length) > 0 && (React.createElement("button", { type: "button", className: "kite-search-input__input-addon kite-search-input__input-addon--button kite-search-input__input-addon--clear", "aria-label": "Clear search", onClick: clearSearch },
                React.createElement(KiteIcon, { icon: "ki-x" }))),
            React.createElement("div", { className: "kite-search-input__appearance" }))));
});

const KiteComboboxTokenEnum = {
    TRIGGER_ICON: 'kite-combobox-trigger-icon-name',
};

/**
 * Given a token name, look up the default value from the tokens library.
 * @param tokenName
 */
const getDefaultToken = (tokenName, frameworkOverride) => {
    return frameworkOverride || tokens[tokenName];
};

const KiteTokensContext = React.createContext({
    tokenOverrides: {},
    getTokenValue: null,
    setTokenOverrides: () => null,
});
const useKiteTokensContext = () => {
    const { tokenOverrides, setTokenOverrides } = useContext(KiteTokensContext);
    let { getTokenValue } = useContext(KiteTokensContext);
    // If not using KiteTokensContext, use getDefaultToken to retrieve the default token value
    if (!getTokenValue) {
        getTokenValue = getDefaultToken;
    }
    return { tokenOverrides, setTokenOverrides, getTokenValue };
};

const KiteSelectTrigger = forwardRef((_a, ref) => {
    var { className, id, placeholder, onToggle, selectedString, selectionId, overflowLabel, overflowId, expanded, disabled, renderItemsSelectedLabel } = _a, other = __rest(_a, ["className", "id", "placeholder", "onToggle", "selectedString", "selectionId", "overflowLabel", "overflowId", "expanded", "disabled", "renderItemsSelectedLabel"]);
    const { tokenOverrides, getTokenValue } = useKiteTokensContext();
    const [triggerIcon] = useMemo(() => [
        getTokenValue(KiteComboboxTokenEnum.TRIGGER_ICON, 'ki-chevron-down', className),
    ], [tokenOverrides, className]);
    const toggle = (e) => {
        onToggle(!expanded);
    };
    return (React.createElement("button", Object.assign({ ref: ref, "aria-expanded": expanded, className: "kite-combobox__trigger", onClick: toggle, id: id, disabled: disabled, role: "button", type: "button" }, other),
        React.createElement("span", { id: `${id}-text`, className: classNames('kite-combobox__value', {
                'kite-combobox__value--placeholder': !selectedString && placeholder,
            }) }, renderItemsSelectedLabel ? (React.createElement("span", { className: "kite-combobox__selection", id: selectionId }, renderItemsSelectedLabel)) : (React.createElement(React.Fragment, null,
            React.createElement("span", { className: "kite-combobox__selection", id: selectionId }, selectedString ? selectedString : placeholder),
            React.createElement("span", { className: "kite-combobox__overflow-label", id: overflowId }, overflowLabel)))),
        React.createElement(KiteIcon, { icon: triggerIcon })));
});

const lerp = (x, y, a) => x * (1 - a) + y * a;

class OriginRelative extends OverlayStrategy {
    constructor(originAnchor, overlayAnchor, offset, size, predicate, contentCssClass) {
        super();
        this.originAnchor = originAnchor;
        this.overlayAnchor = overlayAnchor;
        this.offset = offset;
        this.size = size;
        this.predicate = predicate;
        this.contentCssClass = contentCssClass;
    }
    get reactTo() {
        return ['originPosition', 'originSize', 'contentSize', 'context'];
    }
    /**
     * Only do a proposal if the content and viewport x/y sizes exist. Doing a proposal before these values are non-zero
     * will result in overlay appearing in incorrect location.
     * @param state
     */
    hasContentAndViewportSize(state) {
        return (state.contentSize[0] !== 0 &&
            state.contentSize[1] !== 0 &&
            state.viewportSize[0] !== 0 &&
            state.viewportSize[1] !== 0);
    }
    propose(state) {
        if (!this.predicate(state.context) ||
            !this.hasContentAndViewportSize(state)) {
            return false;
        }
        const size = this.size(state.originSize, state.contentSize, state.viewportSize);
        let x = (lerp(state.originPosition[0], state.originPosition[0] + state.originSize[0], this.originAnchor[0]) -
            lerp(0, size[0], this.overlayAnchor[0])) |
            0, y = (lerp(state.originPosition[1], state.originPosition[1] + state.originSize[1], this.originAnchor[1]) -
            lerp(0, size[1], this.overlayAnchor[1])) |
            0;
        if (this.offset) {
            x += this.offset[0];
            y += this.offset[1];
        }
        // If the size of the overlay is equal to the viewport width, do not translate x, since that would make it go offscreen
        if (size[0] === state.viewportSize[0]) {
            x = 0;
        }
        return new OverlayProposal([x, y], size, false, {
            contentClass: this.contentCssClass,
        });
    }
}
class ViewportCenter extends OverlayStrategy {
    constructor() {
        super();
    }
    get reactTo() {
        return ['viewportSize', 'contentSize', 'context'];
    }
    propose(state) {
        const retVal = new OverlayProposal([0, 0], [state.viewportSize[0], state.viewportSize[1]], true, undefined, { class: 'kite-dialog__backdrop', lockScroll: true });
        return retVal;
    }
}

function useSearchBehavior({ data, customSearch, searchKey, debounceTimeout = 0, minCharacterCount = 0, }) {
    const [value, setValue] = useState('');
    const [searchData, setSearchData] = useState(data);
    useEffect(() => {
        let debounce;
        if (value.length >= minCharacterCount) {
            if (!value) {
                setSearchData(data);
            }
            else {
                debounce = setTimeout(() => {
                    const searchResults = customSearch
                        ? customSearch(data)
                        : defaultArraySearch(data, value, searchKey, false);
                    setSearchData(searchResults);
                }, debounceTimeout);
            }
        }
        else {
            setSearchData([]);
        }
        return () => clearTimeout(debounce);
    }, [value, data]);
    const searchHandler = useCallback((e) => {
        setValue(e);
    }, []);
    return { value, setValue, searchData, searchHandler };
}

function useMultiSelectBehavior({ data, compare, }) {
    const [selections, setSelections] = useState([]);
    const selectionChangeHandler = useCallback((e) => {
        const updatedSelections = [];
        let addToSelection = true;
        selections.forEach((selection) => {
            if (compare ? !compare(selection, e) : selection !== e) {
                updatedSelections.push(selection);
            }
            else {
                addToSelection = false;
            }
        });
        if (addToSelection) {
            updatedSelections.push(e);
        }
        setSelections(updatedSelections);
    }, [selections, compare]);
    const toggleAllHandler = useCallback(() => {
        if (selections.length === data.length) {
            setSelections([]);
        }
        else {
            setSelections(data);
        }
    }, [selections, data]);
    return {
        selections,
        setSelections,
        selectionChangeHandler,
        toggleAllHandler,
    };
}
function useSingleSelectBehavior({ compare }) {
    const [selections, setSelections] = useState([]);
    const selectionChangeHandler = useCallback((e) => {
        if (compare
            ? selections.filter((selection) => compare(selection, e)).length > 0
            : selections.indexOf(e) !== -1) {
            setSelections([]);
        }
        else {
            setSelections([e]);
        }
    }, [selections, compare]);
    return { selections, setSelections, selectionChangeHandler };
}

/** Key values */
const ARROW_DOWN$2 = 'ArrowDown';
const ARROW_UP$2 = 'ArrowUp';
const ARROW_RIGHT = 'ArrowRight';
const ARROW_LEFT = 'ArrowLeft';
const ESCAPE$3 = 'Escape';
/** Is the key an arrow key AND that within the arrows related to a direction. */
const isArrow = (key, direction = 'both') => {
    if (direction === 'both')
        return !![ARROW_DOWN$2, ARROW_LEFT, ARROW_RIGHT, ARROW_UP$2].find((k) => key === k);
    if (direction === 'horizontal')
        return !![ARROW_LEFT, ARROW_RIGHT].find((k) => k === key);
    return !![ARROW_UP$2, ARROW_DOWN$2].find((k) => k === key);
};
/** is this arrow key moving forward? */
const isForward = (direction, key) => ((direction === 'both' || direction === 'vertical') &&
    (key === ARROW_DOWN$2 || key === ARROW_RIGHT)) ||
    (direction === 'horizontal' && key === ARROW_RIGHT);
/** one of the down sides of an arrow key is that up and down scroll the page.
 * This utility prevents this from happening once.  This happens only once because in subsequent
 * arrow keys, the host app may wish the default page scrolling behavior. */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const preventPageScroll = (event) => {
    if (isArrow(event.key)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        window.removeEventListener('keydown', preventPageScroll, false);
        return false;
    }
    window.removeEventListener('keydown', preventPageScroll, false);
};
/** used to apply the prevent scroll, such that it can be removed. */
const addPreventPageScroll = () => {
    window.addEventListener('keydown', preventPageScroll, false);
};
/** normal query function if the current element doesn't match */
const queryOrSelf = (el, selector) => (el === null || el === void 0 ? void 0 : el.matches(selector)) ? el : el === null || el === void 0 ? void 0 : el.querySelector(selector);
/** normal closest function if the current element doesn't match */
const closestOrSelf = (el, selector) => {
    // Selector needs escaping as react useID can generate ids with special chars
    return !!el && !!el.matches && (el === null || el === void 0 ? void 0 : el.matches(CSS.escape(selector)))
        ? el
        : !!el && !!el.closest
            ? el.closest(CSS.escape(selector))
            : undefined;
};
const findSiblingFactory = (getSubsequent, nextFn) => (el, selector, disabledSelector) => {
    if (!!!el) {
        return undefined;
    }
    const next = getSubsequent(el);
    if (!!next) {
        if (next.matches(`${selector}${disabledSelector}`)) {
            return next;
        }
        return nextFn(next, selector, disabledSelector);
    }
    return undefined;
};
/** gets the nearest forward non-disabled sibling */
const nextSibling = findSiblingFactory((el) => el.nextElementSibling, (el, selector, disabledSelector) => nextSibling(el, selector, disabledSelector));
/** gets the nearest prior non-disabled sibling */
const previousSibling = findSiblingFactory((el) => el.previousElementSibling, (el, selector, disabledSelector) => previousSibling(el, selector, disabledSelector));
/** find a matching element as a child selector at either the first or last position */
const childAt = (el, selector, disabledSelector, at) => {
    const children = el.querySelectorAll(`${selector}${disabledSelector}`);
    if (children.length > 0) {
        let index = 0;
        if (at === 'last') {
            index = children.length - 1;
        }
        return children.item(index);
    }
    return undefined;
};
/**
 * Escape special regular expression special characters
 * @param string
 */
const escapeRegExp = (string) => {
    return string.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&');
};
/**
 * Determine if param is of type object
 * @param object
 */
const isObject = (object) => {
    return object != null && typeof object === 'object';
};
/**
 * Perform deep comparison of objects
 * @param obj1
 * @param obj2
 */
const isDeepEqual = (obj1, obj2) => {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length != keys2.length || !isObject(obj1) || !isObject(obj2))
        return false;
    for (const key of keys1) {
        const value1 = obj1[key];
        const value2 = obj2[key];
        const isObjects = isObject(value1) && isObject(value2);
        if ((isObjects && !isDeepEqual(value1, value2)) ||
            (!isObjects && value1 !== value2)) {
            return false;
        }
    }
    return true;
};
/**
 * Written specifically for preventing focus from leaving the menu, if the user clicks the menu or an element on the menu.
 * There was a bug where clicking the vertical scrollbar of the menu when inside a dialog was causing a blur
 * and closing the menu. This prevents that blur from occurring.
 * @param e
 */
const preventOnBlur = (e) => {
    e.preventDefault();
    e.stopPropagation();
};

function useComboboxBehavior({ singleSelect, customSearch, searchKey, compare, debounceTimeout = 0, minCharacterCount = 0, }) {
    const [initialData, setInitialData] = useState([]);
    const [filteredSelections, setFilteredSelections] = useState([]);
    const [resultsWithoutSelections, setResultsWithoutSelections] = useState([]);
    const { value, setValue, searchData, searchHandler } = useSearchBehavior({
        data: initialData,
        customSearch: customSearch,
        searchKey: searchKey,
        debounceTimeout,
        minCharacterCount,
    });
    const multiSelectProps = useMultiSelectBehavior({
        data: searchData,
        compare: compare,
    });
    const singleSelectProps = useSingleSelectBehavior({ compare: compare });
    let currentSelectProps;
    if (!singleSelect) {
        currentSelectProps = multiSelectProps;
    }
    else {
        currentSelectProps = singleSelectProps;
    }
    useEffect(() => {
        const _filteredSelections = [];
        const _filteredResults = [];
        searchData.forEach((searchResult) => {
            if (isInSelections(searchResult, currentSelectProps.selections)) {
                _filteredSelections.push(searchResult);
            }
            else {
                _filteredResults.push(searchResult);
            }
        });
        setFilteredSelections(_filteredSelections);
        setResultsWithoutSelections(_filteredResults);
    }, [searchData, currentSelectProps.selections]);
    /**
     * See if listItem is in selections array.
     * If compare function exists, use it to compare
     * otherwise do a deep compare for objects or indexOf for strings.
     */
    const isInSelections = useCallback((listItem, selections) => {
        let inSelections;
        if (!!compare) {
            for (let i = 0; i < selections.length; i++) {
                if (compare(listItem, selections[i])) {
                    inSelections = true;
                    break;
                }
            }
        }
        else {
            if (typeof listItem === 'object') {
                for (let i = 0; i < selections.length; i++) {
                    if (isDeepEqual(listItem, selections[i])) {
                        inSelections = true;
                        break;
                    }
                }
            }
            else {
                if (selections.indexOf(listItem) !== -1) {
                    inSelections = true;
                }
            }
        }
        return inSelections;
    }, [compare]);
    return Object.assign({ value,
        setValue,
        searchData,
        setInitialData,
        searchHandler,
        filteredSelections,
        resultsWithoutSelections }, currentSelectProps);
}
function useComboboxOverlay(overlayPositionStates = [
    'down',
    'up',
]) {
    const context = ['web', 'closed', 0];
    const strategies = [];
    if (overlayPositionStates.indexOf('down') !== -1) {
        strategies.push(new OriginRelative(// Expand down
        [0, 1], [0, 0], [0, 4], (o, c) => [o[0], c[1]], (x) => { var _a; return (_a = (x && x[0] === 'web')) !== null && _a !== void 0 ? _a : false; }));
    }
    if (overlayPositionStates.indexOf('up') !== -1) {
        strategies.push(new OriginRelative(// Expand up
        [0, 0], [0, 1], [0, -4], (o, c) => [o[0], c[1]], (x) => { var _a; return (_a = (x && x[0] === 'web')) !== null && _a !== void 0 ? _a : false; }));
    }
    return { context, strategies };
}

const KiteComboboxListItem = forwardRef((_a, ref) => {
    var { onClick, value, selected, children = '', disabled, indeterminate, singleSelect, btnClassName, id } = _a, other = __rest(_a, ["onClick", "value", "selected", "children", "disabled", "indeterminate", "singleSelect", "btnClassName", "id"]);
    const [triStateValue, setTriStateValue] = useState(false);
    useEffect(() => {
        if (selected && !indeterminate) {
            setTriStateValue(true);
        }
        else if (selected && indeterminate) {
            setTriStateValue('mixed');
        }
    }, [selected, indeterminate]);
    return (React.createElement("li", Object.assign({ ref: ref, className: "kite-picker__item", id: id }, other),
        React.createElement("button", { className: classNames('kite-picker__action', {
                'kite-picker__action--selected': selected && !indeterminate,
                'kite-picker__action-check--hidden': !selected && singleSelect,
                [btnClassName]: btnClassName,
            }), onClick: onClick, role: singleSelect ? 'option' : 'checkbox', type: "button", "aria-selected": singleSelect ? selected : null, "aria-checked": !singleSelect ? triStateValue : null, "aria-disabled": disabled, disabled: disabled, id: `${id}-button`, tabIndex: -1 },
            React.createElement("div", { className: classNames('kite-picker__action-check', {
                    'kite-picker__action-check--selected': selected && !indeterminate,
                    'kite-picker__action-check--hidden': !selected,
                    'kite-picker__action-check--mixed': indeterminate,
                }) },
                !singleSelect && (React.createElement("div", { className: classNames('kite-checkbox', {
                        'kite-checkbox--tristate': indeterminate,
                    }) },
                    React.createElement("span", { className: "kite-checkbox__indicator" },
                        selected && !indeterminate && (React.createElement(KiteIcon, { icon: "ki-checkmark", className: "kite-checkbox__checked-icon", "aria-label": "" })),
                        indeterminate && (React.createElement(KiteIcon, { icon: "ki-minus-alt", className: "kite-checkbox__mixed-icon", "aria-label": "" }))))),
                singleSelect && (React.createElement(KiteIcon, { icon: "ki-checkmark", className: "kite-checkbox__checked-icon", "aria-label": "" }))),
            React.createElement("span", { className: "kite-picker__action-label" }, children))));
});
KiteComboboxListItem.displayName = 'KiteComboboxListItem';

const getIsSafariOriOS = () => {
    // if SSR, then *NOT* Safari or iOS
    if (typeof window === 'undefined')
        return false;
    const isSafari = navigator.userAgent.includes('Safari') &&
        !navigator.userAgent.includes('Chrome');
    const isIOS = [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod',
    ].includes(navigator.platform) ||
        // iPad on iOS 13 detection
        (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    return isSafari || isIOS;
};

/**
 * A hook for managing focus state and handling onFocus and onBlur events within a component.
 * @example const hasFocus = useFocusManager(refComponentWrap, {onBlur, onFocus});
 */
const useFocusManager = (refFocusScope, options = {}) => {
    // Track whether the onFocus callback (if provided) has fired or not.  We only want it to fire once.
    const refHasFocusFired = useRef(false);
    const [hasFocus, setHasFocus] = useState(false);
    const [hasFocusVisible, setHasFocusVisible] = useState(false);
    const { onBlur, onFocus } = options;
    useEffect(() => {
        const nodeFocusScope = refFocusScope === null || refFocusScope === void 0 ? void 0 : refFocusScope.current;
        if (!nodeFocusScope)
            return noop;
        const handleFocusIn = (e) => {
            setHasFocus(true);
            setHasFocusVisible(e.target.matches(':focus-visible'));
            // Abort if onFocus callback has already fired
            if (refHasFocusFired.current)
                return;
            // Abort if onFocus callback is absent
            if (!onFocus)
                return;
            onFocus(e);
            refHasFocusFired.current = true;
        };
        let focusTimeoutId = null;
        const handleFocusOut = (e) => {
            clearTimeout(focusTimeoutId);
            // Clear Event Queue before checking new Focus location
            focusTimeoutId = setTimeout(() => {
                // Abort if we have refocused onto another element within Component Scope.
                if (nodeFocusScope.contains(document.activeElement))
                    return;
                // Abort if onBlur callback is absent
                if (!onBlur)
                    return;
                // Elvis has left the building
                onBlur(e);
                // Initialize onFocus tracker.  Ready for Focus again.
                refHasFocusFired.current = false;
                setHasFocus(false);
                setHasFocusVisible(false);
            }, 0);
        };
        nodeFocusScope.addEventListener('focusin', handleFocusIn);
        nodeFocusScope.addEventListener('focusout', handleFocusOut);
        return () => {
            nodeFocusScope.removeEventListener('focusin', handleFocusIn);
            nodeFocusScope.removeEventListener('focusout', handleFocusOut);
            clearTimeout(focusTimeoutId);
        };
    }, [refFocusScope]); // Dependency on the container ref
    // Return state as double negations to normalize Null values as False
    return { hasFocus: !!hasFocus, hasFocusVisible: !!hasFocusVisible };
};

/**
 * Component that enabled virtualization for lists. Makes it so that only a specific number of items in the list get displayed
 * and as scrolling occurs the slice of items displayed gets changed.
 */
const KiteWindow = ({ menuItemHeight, totalItemCount, maxItemInWindowCount, focusedIndex, children, enabledVirtualization, }) => {
    const scrollAreaRef = useRef(null);
    const [topItemIndex, setTopItemIndex] = React.useState(0);
    const bufferCount = 3; //  Number of items above/below the list before virtualization occurs.
    /**
     * When scrolling through list, re-calculate which slice of children to display in the list
     */
    const handleScroll = () => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (enabledVirtualization &&
            ((_b = (_a = scrollAreaRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.scrollTop) &&
            ((_d = (_c = scrollAreaRef.current) === null || _c === void 0 ? void 0 : _c.parentElement) === null || _d === void 0 ? void 0 : _d.scrollTop) +
                ((_f = (_e = scrollAreaRef.current) === null || _e === void 0 ? void 0 : _e.parentElement) === null || _f === void 0 ? void 0 : _f.clientHeight) <
                ((_h = (_g = scrollAreaRef.current) === null || _g === void 0 ? void 0 : _g.parentElement) === null || _h === void 0 ? void 0 : _h.scrollHeight)) {
            const currentScrollTop = Math.round(((_k = (_j = scrollAreaRef.current) === null || _j === void 0 ? void 0 : _j.parentElement) === null || _k === void 0 ? void 0 : _k.scrollTop) / menuItemHeight);
            // This if statement is to make it so that we only update the items displayed in the menu after the user has scrolled
            // more than the height a list item. (e.g. if each list item is 45px, only re-draw items after user has scrolled up or down 45px)
            if (currentScrollTop !== topItemIndex) {
                // Calculate the index of the item that will be displayed as the first item in the list
                setTopItemIndex(currentScrollTop);
            }
        }
    };
    /**
     * Calculate the height of the original list so that the scroll bar behaves and look correct for list size
     */
    const maxScrollHeight = useMemo(() => menuItemHeight * totalItemCount, [menuItemHeight, totalItemCount]);
    /**
     * Determine first item of the list to be rendered.
     */
    const minItemToRender = useMemo(() => Math.max(0, topItemIndex - bufferCount), [topItemIndex, totalItemCount]);
    /**
     * Slice the children to get the correct items that should be displayed in the list.
     */
    const sliceOptions = useMemo(() => {
        if (enabledVirtualization) {
            // Determine the min and max indexes to show in the virtualization window, then cut that slice of items from children.
            const max = Math.min(totalItemCount, topItemIndex + maxItemInWindowCount + bufferCount + 1);
            return React.Children.toArray(children).slice(minItemToRender, max);
        }
        else {
            return children;
        }
    }, [children, minItemToRender]);
    useEffect(() => {
        var _a;
        if (enabledVirtualization && (scrollAreaRef === null || scrollAreaRef === void 0 ? void 0 : scrollAreaRef.current)) {
            (_a = scrollAreaRef.current) === null || _a === void 0 ? void 0 : _a.parentElement.addEventListener('scroll', handleScroll, { capture: true });
        }
        return () => {
            var _a;
            (_a = scrollAreaRef.current) === null || _a === void 0 ? void 0 : _a.parentElement.removeEventListener('scroll', handleScroll);
        };
    }, [scrollAreaRef]);
    useEffect(() => {
        var _a, _b, _c;
        const _scrollTop = ((_b = (_a = scrollAreaRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.scrollTop) / menuItemHeight;
        if (focusedIndex < _scrollTop ||
            focusedIndex > _scrollTop + maxItemInWindowCount) {
            (_c = scrollAreaRef === null || scrollAreaRef === void 0 ? void 0 : scrollAreaRef.current) === null || _c === void 0 ? void 0 : _c.parentElement.scrollTo(0, focusedIndex * menuItemHeight);
            // This is so that when focus gets reset to top of list, the top index to get rendered gets set to 0.
            if (focusedIndex < 2) {
                setTopItemIndex(0);
            }
        }
    }, [focusedIndex, children]);
    useEffect(() => {
        handleScroll();
    }, [totalItemCount]);
    return (React.createElement(React.Fragment, null, enabledVirtualization ? (React.createElement("div", { ref: scrollAreaRef, style: { height: maxScrollHeight } },
        React.createElement("div", { className: "rk-kite-window--container", style: {
                top: `${minItemToRender * menuItemHeight}px`,
            } }, sliceOptions))) : (children)));
};

const KiteLabel = (_a) => {
    var { children, baseCSSClass, helperText, htmlFor, popover: popoverRaw, tooltip, id: labelId, disabled = false } = _a, other = __rest(_a, ["children", "baseCSSClass", "helperText", "htmlFor", "popover", "tooltip", "id", "disabled"]);
    const [mainChildren, popoverChildren, tooltipChildren] = useMemo(() => {
        const [nonPopoverChildren, popoverChildren] = partitionChildrenByDisplayName(children, 'KitePopover');
        const [listChildren, tooltipChildren] = partitionChildrenByDisplayName(nonPopoverChildren, 'KiteTooltip');
        return [listChildren, popoverChildren, tooltipChildren];
    }, [children]);
    // No label text, no label
    if (!children)
        return null;
    // Grab first popover if at least 1 popover is present.  Otherwise return undefined.
    const [popoverElement] = popoverChildren;
    // Grab first tooltip if at least 1 tooltip is present.  Otherwise return undefined.
    const [toolTipElement] = tooltipChildren;
    // Assign airaLabel to Popover if Popover exists
    const popover = (!!popoverElement &&
        React.cloneElement(popoverElement, {
            ariaLabel: `${mainChildren} - Helpful Hint`,
            disabled,
        })) ||
        (!!popoverRaw &&
            React.cloneElement(popoverRaw, {
                ariaLabel: `${mainChildren} - Helpful Hint`,
                disabled,
            }));
    const renderTooltip = toolTipElement || tooltip;
    // Create props for major component elements
    const labelGroupProps = {
        className: `${baseCSSClass}__label-group`,
    };
    const primaryLabelProps = Object.assign({ className: `${baseCSSClass}__label`, id: labelId, htmlFor }, other);
    const helperTextProps = {
        className: `${baseCSSClass}__helper-text`,
        id: getHelperTextIdFromLabelId(labelId),
        htmlFor,
    };
    // Create Primary Label template.  Return raw label if no popover, otherwise return both in structural markup
    const primaryLabelRaw = React.createElement("label", Object.assign({}, primaryLabelProps), mainChildren);
    const primaryLabel = !!popover || !!renderTooltip ? (React.createElement("div", { className: "kite-display-flex" },
        primaryLabelRaw,
        popover,
        renderTooltip)) : (primaryLabelRaw);
    const maybeHelperText = !!helperText && (React.createElement("label", Object.assign({}, helperTextProps), helperText));
    return (React.createElement("div", Object.assign({}, labelGroupProps),
        primaryLabel,
        maybeHelperText));
};
KiteLabel.displayName = 'KiteLabel';

const defaultComboboxLabels = {
    clearLabel: 'Clear',
    inputLabel: '',
    placeholder: 'Select options',
    selectAll: 'All Options',
    errorMessage: '',
    moreLabel: 'more',
    searchPlaceholder: 'Search...',
    noSearchResults: 'No Results',
    ariaSearchResults: ' results available',
    inputAriaDescribedBy: 'Press Enter to select. Selected items will move to the top of the list',
    loadingResults: 'Loading Results',
};
const KiteCombobox = forwardRef((_a, ref // TODO - fix
) => {
    var _b, _c;
    var { children, disabled, className, displayKey, expandPosition, onSearch, hasSearch = !!onSearch, id, labels: overrideLabels = {}, maxItemCount = dropdownMaxItemCount, onBlur = (e) => e, onFocus = (e) => e, onSelectionChange, readOnly, selections = [], singleSelect = false, toggleAll, value, renderItemsSelectedLabel, enableVirtualization } = _a, other = __rest(_a, ["children", "disabled", "className", "displayKey", "expandPosition", "onSearch", "hasSearch", "id", "labels", "maxItemCount", "onBlur", "onFocus", "onSelectionChange", "readOnly", "selections", "singleSelect", "toggleAll", "value", "renderItemsSelectedLabel", "enableVirtualization"]);
    const comboBoxRef = useRef(null);
    const focusInputRef = useRef(null);
    const overlayOriginRef = useRef(null);
    const pickerRef = useRef(null);
    const triggerRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeDescendant, setActiveDescendant] = useState('');
    // Focused index is used to keep track of which item to show focus outline, since focus stays in search input
    // -1 is the search input (when hasSearch is true), and 0+ is the list item to focus
    const focusedIndexInit = hasSearch ? -1 : 0;
    const [focusedIndex, setFocusedIndex] = useState(focusedIndexInit);
    const selectionToggleIndex = 0;
    const { strategies, context } = useComboboxOverlay(expandPosition);
    const { hasFocus: hasComponentFocus } = useFocusManager(comboBoxRef, {
        onBlur,
        onFocus,
    });
    const normalId = useNormalId();
    const componentId = id ? id : `kite-combobox-${normalId}`;
    const getElementId = (el) => `${componentId}-${el}`;
    const HtmlId = Object.freeze({
        Search: getElementId('search'),
        DummyFocus: getElementId('focus'),
        Label: getElementId('label'),
        Trigger: getElementId('combobox-trigger'),
        TextTrigger: getElementId('combobox-trigger-text'),
        Error: getElementId('error'),
        SelectionToggle: getElementId('all-options'),
        AriaDescription: getElementId('described-by'),
        Selection: getElementId('selection'),
        Overflow: getElementId('overflow'),
    });
    const [mainChildren, popoverChildren, labelChildren] = useMemo(() => {
        const [nonPopoverChildren, popoverChildren] = partitionChildrenByDisplayName(children, 'KitePopover');
        const [listChildren, labelChildren] = partitionChildrenByDisplayName(nonPopoverChildren, 'KiteLabel');
        return [listChildren, popoverChildren, labelChildren];
    }, [children]);
    // Grab first popover if at least 1 popover is present.  Otherwise return undefined.
    const [popover] = popoverChildren;
    // Grab first label if at least 1 label is present.  Otherwise return undefined.
    const [firstChildLabel] = labelChildren;
    const labels = useMemo(() => (Object.assign(Object.assign({}, defaultComboboxLabels), overrideLabels)), [overrideLabels]);
    const isSafariOriOS = getIsSafariOriOS();
    // Children Meta Info
    const [listCount, canHazListItem, canHazProgressIndicator] = useMemo(() => [
        React.Children.count(mainChildren),
        childrenContainsAtLeastOne(children, KiteComboboxListItem),
        childrenContainsAtLeastOne(children, KiteProgressIndicator),
    ], [mainChildren]);
    // Is the dropdown loading?
    const isLoading = canHazProgressIndicator;
    const canHazSelectionToggle = allTrue(!!toggleAll, !!listCount, !singleSelect, !value);
    const canHazSearch = allTrue(!readOnly, hasSearch);
    // The indexes of items in the list can be offset by the presence (or not) of the Selection Toggle.  Correct for that.
    const arrayToFocusedIndex = (index) => index + Number(!!canHazSelectionToggle);
    const focusToArrayIndex = (index) => index - Number(!!canHazSelectionToggle);
    /**
     * Manage Dropdown Collapse and Component Blur behavior
     */
    useEffect(() => {
        var _a;
        // If Component has lost focus while Dropdown is expanded -- Collapse Dropdown
        if (allTrue(!hasComponentFocus, isExpanded)) {
            setIsExpanded(false);
            return noop;
        }
        // If Component has focus when Dropdown is collapsed -- Redirect element focus to Trigger.
        if (allTrue(hasComponentFocus, !isExpanded)) {
            (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            return noop;
        }
        return noop;
    }, [isExpanded, hasComponentFocus]);
    /**
     *  Manage Dropdown Expanded Behavior
     */
    useEffect(() => {
        var _a;
        if (!isExpanded) {
            // Clear Search input on Dropdown collapse
            onSearch && onSearch('');
            return noop;
        }
        const focusNode = focusInputRef === null || focusInputRef === void 0 ? void 0 : focusInputRef.current;
        // If Expanded Dropdown contains no focus mode, return focus to Trigger.  This maintains Component focus.
        if (!focusNode) {
            (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            return noop;
        }
        let styleSetTimeoutId = null;
        let styleUnsetTimeoutId = null;
        const handleIOSFocus = () => {
            clearTimeout(styleUnsetTimeoutId);
            (focusNode === null || focusNode === void 0 ? void 0 : focusNode.style) && (focusNode.style.transform = 'translateY(-1px)');
            styleUnsetTimeoutId = setTimeout(function () {
                (focusNode === null || focusNode === void 0 ? void 0 : focusNode.style) && (focusNode.style.transform = 'none');
            }, 400);
        };
        styleSetTimeoutId = setTimeout(() => {
            if (!readOnly && !!focusNode) {
                // This workaround is for an iOS specific bug where the input cursor would not be in the correct position
                // when the focus call scrolls the safari browser. This solution will be looked at in the future to be improved upon
                // https://stackoverflow.com/questions/38017771/mobile-safari-prevent-scroll-page-when-focus-on-input
                if (isSafariOriOS)
                    focusNode.addEventListener('focus', handleIOSFocus);
                focusNode.focus();
            }
        }, 200);
        setFocusedIndex(focusedIndexInit); // Reset focus index to first list element
        return () => {
            clearTimeout(styleSetTimeoutId);
            clearTimeout(styleUnsetTimeoutId);
            focusNode.removeEventListener('focus', handleIOSFocus);
        };
    }, [isExpanded]);
    const ariaLiveMessage = useMemo(() => {
        if (isLoading)
            return labels === null || labels === void 0 ? void 0 : labels.loadingResults;
        if (listCount > 0)
            return `${listCount} ${labels === null || labels === void 0 ? void 0 : labels.ariaSearchResults}`;
        return '';
    }, [isLoading, listCount, labels]);
    /**
     * Reset Focused Index when Combobox value changes
     */
    useEffect(() => {
        setFocusedIndex(focusedIndexInit);
    }, [value]);
    /**
     * Manage Aria settings that denote Fake-Focused element
     */
    useEffect(() => {
        const pickerNode = pickerRef === null || pickerRef === void 0 ? void 0 : pickerRef.current;
        if (!pickerNode)
            return noop;
        const activeDescTimeoutId = setTimeout(() => {
            const focusVisibleEl = pickerNode.querySelector('.kite-focus-visible');
            focusVisibleEl
                ? setActiveDescendant(focusVisibleEl === null || focusVisibleEl === void 0 ? void 0 : focusVisibleEl.id)
                : setActiveDescendant(null);
        }, 0);
        return () => {
            clearTimeout(activeDescTimeoutId);
        };
    }, [focusedIndex]);
    const options = useMemo(() => {
        if (canHazListItem) {
            return React.Children.map(mainChildren, (child, index) => {
                if (!child)
                    return child;
                return React.cloneElement(child, {
                    onClick: (e) => {
                        handleListItemClick({
                            listItemProps: child.props.value,
                            index,
                            disabled: child.props.disabled,
                        });
                    },
                    btnClassName: arrayToFocusedIndex(index) === focusedIndex &&
                        'kite-focus-visible',
                    singleSelect,
                    disabled: readOnly || child.props.disabled,
                    id: getElementId(`option-${index}`),
                });
            });
        }
        else if (canHazProgressIndicator) {
            const modifiedChildren = applyPropsToChildrenOfType(mainChildren, KiteProgressIndicator, {
                size: 'sm',
            });
            return wrapChildrenOfType(modifiedChildren, KiteProgressIndicator, React.createElement("div", { className: "kite-picker__results kite-picker__no-results" }));
        }
        return mainChildren;
    }, [mainChildren, canHazListItem, canHazProgressIndicator, focusedIndex]);
    const handleTriggerToggle = useCallback(() => {
        setIsExpanded(!isExpanded);
    }, [isExpanded]);
    const handleKeyUp = useCallback((e) => {
        var _a;
        // Exit if key strike was not a predefined Action Key
        if (!Object.values(MenuActionKey).includes(e.key))
            return;
        e.stopPropagation();
        e.preventDefault();
        if (e.key === MenuActionKey.ESCAPE) {
            setIsExpanded(false);
            (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else if (e.key === MenuActionKey.SPACE && !isExpanded) {
            setIsExpanded(true);
        }
        else if (e.key === MenuActionKey.ARROW_DOWN) {
            incrementFocusedIndex();
        }
        else if (e.key === MenuActionKey.ARROW_UP) {
            decrementFocusedIndex();
        }
        else if (e.key === MenuActionKey.ENTER) {
            // Selection Toggle has focus.  Handle Selection Toggle.
            if (canHazSelectionToggle && focusedIndex === selectionToggleIndex) {
                handleSelectionToggle();
                return;
            }
            const index = focusToArrayIndex(focusedIndex);
            const child = options[index];
            // Eject if we can not identify Focus item
            if (!child)
                return;
            // A Combobox Item has focus. Handle Item Click.
            handleListItemClick({
                listItemProps: child['props'].value,
                index,
                disabled: child.props.disabled,
            });
        }
    }, [isExpanded, canHazSelectionToggle, focusedIndex, children]);
    const preventPageScroll = (e) => {
        if ([MenuActionKey.ARROW_UP, MenuActionKey.ARROW_DOWN].includes(e.key))
            e.preventDefault();
    };
    const handleSelectionToggle = () => {
        toggleAll();
        setFocusedIndex(0);
    };
    const hideFocusIndex = () => {
        var _a;
        (_a = focusInputRef === null || focusInputRef === void 0 ? void 0 : focusInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const incrementFocusedIndex = () => {
        const maxFocusedIndex = arrayToFocusedIndex(listCount - 1);
        if (focusedIndex < maxFocusedIndex) {
            setFocusedIndex(focusedIndex + 1);
        }
    };
    const decrementFocusedIndex = () => {
        if (focusedIndex > 0) {
            setFocusedIndex(focusedIndex - 1);
        }
    };
    const handleListItemClick = ({ listItemProps, index, disabled }) => {
        if (disabled)
            return;
        // Set `focusedIndex` to the index of this item.  Correct for presence of All Options, or other extra elements
        setFocusedIndex(arrayToFocusedIndex(index));
        onSelectionChange(listItemProps);
        if (singleSelect) {
            setIsExpanded(false);
        }
    };
    const ariaComboboxRoleProps = {
        role: 'combobox',
        'aria-roledescription': !singleSelect ? 'multi-select combobox' : null,
    };
    const ariaDescribedBy = HtmlId.AriaDescription;
    const ariaDescribedByMsg = readOnly
        ? 'Read Only'
        : labels.inputAriaDescribedBy;
    const ariaActiveDescendant = activeDescendant;
    const triggerAriaDescribedBy = combineIdentifiers({
        [HtmlId.Error]: labels === null || labels === void 0 ? void 0 : labels.errorMessage,
        [HtmlId.AriaDescription]: !canHazSearch,
    });
    const SearchInput = (React.createElement(KiteComboboxSearchInput, { ariaActiveDescendant: ariaActiveDescendant, ariaDescribedBy: ariaDescribedBy, ariaRoleDescription: ariaComboboxRoleProps['aria-roledescription'], id: HtmlId.Search, inputClassName: allTrue(hasSearch, focusedIndex === focusedIndexInit)
            ? 'kite-focus-visible'
            : '', onSearch: onSearch, placeholder: labels.searchPlaceholder, searchInputRef: focusInputRef, value: value }));
    const DummyFocusHolder = (React.createElement("input", { "aria-activedescendant": ariaActiveDescendant, "aria-hidden": true, className: 'kite-visually-hidden', ref: focusInputRef }));
    const labelHtmlFor = canHazSearch ? HtmlId.Search : null;
    const labelElement = useMemo(() => {
        if (firstChildLabel) {
            const labelProps = {
                id: HtmlId.Label,
                htmlFor: labelHtmlFor,
                baseCSSClass: 'kite-combobox',
            };
            if (disabled) {
                labelProps.disabled = disabled;
            }
            if (labels === null || labels === void 0 ? void 0 : labels.inputHelperText) {
                labelProps.helperText = labels === null || labels === void 0 ? void 0 : labels.inputHelperText;
            }
            return React.cloneElement(firstChildLabel, labelProps);
        }
        else {
            return null;
        }
    }, [firstChildLabel]);
    return (React.createElement("div", Object.assign({ ref: comboBoxRef, className: combineIdentifiers('kite-combobox kite-popover--form-control', {
            [className]: className,
            'kite-combobox--disabled': disabled,
            'kite-combobox--error': labels === null || labels === void 0 ? void 0 : labels.errorMessage,
            'kite-combobox--open': isExpanded,
            'kite-combobox--readonly': readOnly,
            'kite-combobox--single-select': singleSelect,
        }), onClick: hideFocusIndex, onKeyDown: preventPageScroll }, other),
        labelElement ? (labelElement) : (React.createElement(KiteLabel, { id: HtmlId.Label, helperText: labels.inputHelperText, baseCSSClass: 'kite-combobox', htmlFor: labelHtmlFor, popover: popover, disabled: disabled }, labels === null || labels === void 0 ? void 0 : labels.inputLabel)),
        React.createElement("span", { className: 'kite-combobox--trigger__wrap', ref: overlayOriginRef },
            React.createElement(KiteSelectTrigger, Object.assign({ "aria-describedby": triggerAriaDescribedBy, "aria-labelledby": combineIdentifiers(HtmlId.Label, HtmlId.Selection, HtmlId.Overflow, {
                    [getHelperTextIdFromLabelId(HtmlId.Label)]: !!labels.inputHelperText,
                }), selectionId: HtmlId.Selection, disabled: disabled, expanded: isExpanded, id: HtmlId.Trigger, ref: triggerRef, onToggle: handleTriggerToggle, overflowId: HtmlId.Overflow, overflowLabel: (selections === null || selections === void 0 ? void 0 : selections.length) > 1
                    ? ' +' + ((selections === null || selections === void 0 ? void 0 : selections.length) - 1) + ' ' + (labels === null || labels === void 0 ? void 0 : labels.moreLabel)
                    : '', placeholder: labels === null || labels === void 0 ? void 0 : labels.placeholder, renderItemsSelectedLabel: renderItemsSelectedLabel && renderItemsSelectedLabel(selections), selectedString: displayKey && selections.length
                    ? ((_b = selections[0][displayKey]) !== null && _b !== void 0 ? _b : '')
                    : ((_c = selections[0]) !== null && _c !== void 0 ? _c : '') }, (!canHazSearch && ariaComboboxRoleProps))),
            isSafariOriOS && (React.createElement("input", { "aria-hidden": true, className: "kite-combobox--trigger__ios-input", onClick: () => {
                    triggerRef.current.click();
                }, role: "button", type: "text" }))),
        React.createElement("span", { id: ariaDescribedBy, className: "kite-assistive-text kite-display-none" }, ariaDescribedByMsg),
        React.createElement(KiteFeedback, { componentName: "kite-combobox", errorMessage: labels === null || labels === void 0 ? void 0 : labels.errorMessage, id: HtmlId.Error }),
        isExpanded && (React.createElement(AttachedOverlay, { context: context, origin: overlayOriginRef.current, strategies: strategies },
            React.createElement("div", { onMouseDown: preventOnBlur, className: combineIdentifiers('kite-picker kite-picker--list kite-picker--combobox', { 'kite-picker--open': isExpanded }), ref: pickerRef, role: "application", tabIndex: readOnly ? 10 : undefined },
                React.createElement("div", { className: "kite-picker__content", onKeyUp: handleKeyUp },
                    !readOnly && (React.createElement(React.Fragment, null,
                        hasSearch ? SearchInput : DummyFocusHolder,
                        React.createElement("div", { "aria-live": "polite", className: "kite-assistive-text", role: "status" }, ariaLiveMessage))),
                    !isLoading && !listCount && (React.createElement("div", { "aria-live": "polite", className: 'kite-picker__results' }, labels.noSearchResults)),
                    isLoading ? (React.createElement(React.Fragment, null, options)) : (React.createElement("ol", { "aria-labelledby": HtmlId.Label, className: combineIdentifiers('kite-picker__list', {
                            'rk-picker__list--virtualization': enableVirtualization,
                        }), role: "listbox", style: {
                            '--max-item-count': maxItemCount,
                        } },
                        React.createElement(KiteWindow, { menuItemHeight: 45, totalItemCount: listCount + (canHazSelectionToggle ? 1 : 0), maxItemInWindowCount: maxItemCount, focusedIndex: focusedIndex, enabledVirtualization: enableVirtualization },
                            canHazSelectionToggle && !readOnly && (React.createElement(KiteComboboxListItem, { btnClassName: focusedIndex === selectionToggleIndex
                                    ? 'kite-focus-visible'
                                    : '', disabled: readOnly, id: HtmlId.SelectionToggle, indeterminate: (selections === null || selections === void 0 ? void 0 : selections.length) > 0 &&
                                    (selections === null || selections === void 0 ? void 0 : selections.length) !== listCount, onClick: handleSelectionToggle, selected: (selections === null || selections === void 0 ? void 0 : selections.length) > 0 }, labels.selectAll)),
                            options)))))))));
});
KiteCombobox.displayName = 'KiteCombobox';

/**
 *
 */
const KiteDataTable = (_a) => {
    var { className, zebraStripe = 'none', borders = 'none', spacing = 'default', responsiveness = 'fluid', title = '', caption, id, currentSortKey = '', sortDirection, textAlignment = 'center', children, onSort, stickyStartColumn, stickyEndColumn } = _a, other = __rest(_a, ["className", "zebraStripe", "borders", "spacing", "responsiveness", "title", "caption", "id", "currentSortKey", "sortDirection", "textAlignment", "children", "onSort", "stickyStartColumn", "stickyEndColumn"]);
    const uuid = useUID();
    const normalId = useNormalId(id);
    const [srLabel, setSRLabel] = useState('');
    const [applyStickyShadow, setApplyStickyShadow] = useState(false);
    const scrollAreaRef = useRef(null);
    const captionId = `kite-data-table-caption-${normalId}`;
    const tableId = `kite-data-table-${normalId}`;
    const applyResponsiveA11y = useMemo(() => responsiveness === 'responsive' || stickyStartColumn || stickyEndColumn, [responsiveness, stickyEndColumn, stickyStartColumn]);
    useEffect(() => {
        if (!!sortDirection) {
            setSRLabel(`${title} now sorted by ${currentSortKey} ${sortDirection}`);
        }
    }, [currentSortKey, sortDirection]);
    /**
     * used to dynamically show/hide the shadow of the sticky column only when the table is scrolled
     */
    const onStickyScroll = () => {
        // Check if the scrollContainer is scrolled horizontally
        if ((stickyStartColumn && scrollAreaRef.current.scrollLeft > 0) ||
            (stickyEndColumn &&
                scrollAreaRef.current.scrollLeft <
                    scrollAreaRef.current.scrollWidth - scrollAreaRef.current.clientWidth)) {
            // Add class when scrolling
            setApplyStickyShadow(true);
        }
        else {
            // Remove class if not scrolled
            setApplyStickyShadow(false);
        }
    };
    useEffect(() => {
        if (stickyEndColumn || stickyStartColumn) {
            scrollAreaRef.current.addEventListener('scroll', onStickyScroll);
        }
        if (scrollAreaRef.current) {
            // Initialize shadow, in case the shadow should appear before scroll event occurs
            onStickyScroll();
        }
        return () => {
            if (scrollAreaRef.current) {
                scrollAreaRef.current.removeEventListener('scroll', onStickyScroll);
            }
        };
    }, [scrollAreaRef]);
    if (!!title) {
        console.warn('title property has been deprecated, use caption property instead.');
    }
    const { ref: propRef } = other, nonRefAttributes = __rest(other, ["ref"]); // remove ref from other props as it otherwise throws ts error
    return (React.createElement("div", Object.assign({ id: tableId, className: classNames('kite-data-table', {
            'kite-data-table--striped': zebraStripe === 'striped',
            'kite-data-table--responsive': responsiveness === 'responsive',
            'kite-data-table--scroll': responsiveness === 'responsive',
            'kite-data-table--striped-inverse': zebraStripe === 'striped-inverse',
            'kite-data-table--cell-border': borders === 'cell',
            'kite-data-table--row-border': borders === 'row',
            'kite-data-table--comfortable': spacing === 'comfortable',
            'kite-data-table--compact': spacing === 'compact',
            'kite-data-table--right': textAlignment === 'right',
            'kite-data-table--sticky-col-start': stickyStartColumn,
            'kite-data-table--sticky-col-end': stickyEndColumn,
            'kite-data-table--sticky-shadow': applyStickyShadow,
            [className]: className,
        }) }, nonRefAttributes),
        React.createElement("div", { ref: scrollAreaRef, className: "kite-data-table__scroll-wrap", role: applyResponsiveA11y ? 'region' : null, tabIndex: applyResponsiveA11y ? 0 : -1, "aria-labelledby": applyResponsiveA11y ? captionId : null },
            React.createElement("table", null,
                React.createElement("caption", { id: captionId, className: "kite-assistive-text" }, (caption || title) +
                    ((caption || title) && !!sortDirection && sortDirection !== 'none'
                        ? ' '
                        : '') +
                    (!!sortDirection && sortDirection !== 'none'
                        ? `sorted by ${currentSortKey} ${sortDirection}`
                        : '')),
                React.Children.map(children, (child, index) => {
                    if (child.type === KiteTableHeader) {
                        return cloneElement(child, {
                            currentSortKey,
                            sortDirection,
                            onSort,
                            key: uuid + '-' + index,
                        });
                    }
                    else {
                        return child;
                    }
                }))),
        React.createElement("div", { className: "kite-data-table__shadow" }),
        srLabel && (React.createElement("span", { className: "kite-assistive-text kite-data-table__sort-update", "aria-live": "polite" }, srLabel))));
};

/**
 * The header for an individual Column in KiteDatatable
 */
const KiteColumn = (_a) => {
    var { className, children, currentSortKey, sortDirection, sortKey, onSort } = _a, other = __rest(_a, ["className", "children", "currentSortKey", "sortDirection", "sortKey", "onSort"]);
    return (React.createElement("th", Object.assign({ scope: "col", className: classNames({
            [className]: className,
            'kite-data-table__header--actionable': sortKey,
        }), "aria-sort": sortDirection }, other), !sortKey ? (React.createElement("span", null, children)) : (React.createElement("button", { type: "button", className: "kite-data-table__header-action", onClick: (e) => onSort(sortKey) },
        React.createElement("span", null, children),
        React.createElement("span", { className: classNames('', {
                'kite-data-table__sort': sortKey,
                'kite-data-table__sort--asc': sortDirection === 'ascending' && currentSortKey === sortKey,
                'kite-data-table__sort--desc': sortDirection === 'descending' && currentSortKey === sortKey,
            }) },
            React.createElement("span", { className: "kite-data-table__sort-icon-up-down" },
                React.createElement(KiteIcon, { icon: "ki-sort-up-down" })),
            React.createElement("span", { className: "kite-data-table__sort-icon-up" },
                React.createElement(KiteIcon, { icon: "ki-sort-up" })),
            React.createElement("span", { className: "kite-data-table__sort-icon-down" },
                React.createElement(KiteIcon, { icon: "ki-sort-down" })))))));
};

/**
 * The individual cells in KiteDataTable
 */
const KiteCell = (_a) => {
    var { className, id, children } = _a, other = __rest(_a, ["className", "id", "children"]);
    const normalId = useNormalId(id);
    const uuid = useUID();
    return (React.createElement("td", Object.assign({ id: `kite-data-table-td-${normalId}`, className: className }, other),
        React.createElement("span", { id: `kite-data-table-cell-${normalId}` }, React.Children.map(children, (child, index) => {
            if (typeof child !== 'string' &&
                typeof child !== 'number' &&
                child.type['displayName'] === 'KiteCheckbox') {
                return cloneElement(child, {
                    onChange: (e) => child.props.onChange(e, `kite-data-table-cell-${normalId}`),
                    key: uuid + '-' + index,
                });
            }
            else {
                return child;
            }
        }))));
};

/**
 *
 */
const KiteRow = (_a) => {
    var { className, children } = _a, other = __rest(_a, ["className", "children"]);
    return (React.createElement("tr", Object.assign({ className: className }, other), children));
};

/**
 * The element containing all column headers in KiteDataTable
 */
const KiteTableHeader = (_a) => {
    var { className, currentSortKey, sortDirection, onSort, children } = _a, other = __rest(_a, ["className", "currentSortKey", "sortDirection", "onSort", "children"]);
    const uuid = useUID();
    return (React.createElement("thead", Object.assign({ className: className }, other),
        React.createElement("tr", null, React.Children.map(children, (child, index) => {
            return cloneElement(child, {
                currentSortKey,
                sortDirection,
                onSort,
                key: uuid + '-' + index,
            });
        }))));
};

function DayOfWeekIndex(value) {
    switch (value) {
        case 'sunday':
            return 0;
        case 'monday':
            return 1;
        case 'tuesday':
            return 2;
        case 'wednesday':
            return 3;
        case 'thursday':
            return 4;
        case 'friday':
            return 5;
        case 'saturday':
            return 6;
    }
}

/**
 * Chunks all the days into chunks (ie, weeks... ie, calendar rows)
 *
 * @param array
 * @param size
 * @returns
 */
function chunk(array, size) {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size));
    }
    return result;
}
/**
 * Clamps a Date to a min/max range, and returns min/max Date, if out of range.
 *
 * @param date
 * @param min
 * @param max
 * @returns
 */
function clamp(date, min, max) {
    const time = date.getTime();
    if (min && min instanceof Date && time < min.getTime()) {
        return min;
    }
    if (max && max instanceof Date && time > max.getTime()) {
        return max;
    }
    return date;
}
function maskPhoneMMDDYYYY(value) {
    value = value.replace(/([^0-9/])/g, ''); // replace all non digits or forward slashes with empty space
    value = value.replace(/([/])/g, function (_, group, index) {
        if (index !== 2 && index !== 5) {
            return '';
        }
        else {
            return '/';
        }
    }); // Remove all "/" that are not in the correct indices for a valid mm/dd/yyy format
    value = value.replace(/^(\d{2})(\d{1,2})/g, '$1/$2'); // Add first "/" if not entered
    value = value.replace(/^(\d{2}\/)(\d{2})(\d{1,4})/g, '$1$2/$3'); // Add second "/" if not entered
    value = value.replace(/(.{10})(.+)/g, '$1'); // prevent date from being more than 10 characters long
    return value;
}
/**
 *
 * @param start
 * @param end
 * @returns Inclusive array of Dates within range
 */
function getDatesWithinRange(start, end) {
    const dates = [];
    const current = new Date(start);
    while (current <= end) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
    }
    return dates;
}
// Create array of UTC timestamps for each day of the week, starting with an arbitrary Sunday
const baselineSundayTimestamp = new Date(Date.UTC(2021, 0, 3)).getTime();
const weekdayTimestamps = Array.from(Array(7).keys()).map((day) => baselineSundayTimestamp + day * 24 * 60 * 60 * 1000);
/**
 * @param locale
 * @returns a localized array of Weekday names, starting with Sunday.
 */
const getWeekdayNames = (locale = 'en-US') => weekdayTimestamps.map((weekdayTimestamp) => new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    timeZone: 'UTC',
}).format(new Date(weekdayTimestamp)));
/**
 * @param date
 * @param locale
 * @returns The localized long name of month, eg "February".
 */
function getMonthNameLong(date, locale = 'en-US') {
    const options = { month: 'long' };
    return new Intl.DateTimeFormat(locale, options).format(date);
}
/**
 * Returns a Date object representing the first date of the month, eg May 1, 2023
 *
 * @param date
 * @returns
 */
function getStartOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}
/**
 * Returns a Date object representing the last date of the month, eg May 31, 2023
 *
 * @param date
 * @returns
 */
function getEndOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}
/**
 * @param a
 * @param b
 * @returns `true` if Dates are equal (with "day" precision)
 */
function isEqual(a, b) {
    if (a == null || b == null) {
        return false;
    }
    return isEqualMonth(a, b) && a.getDate() === b.getDate();
}
/**
 *
 * @param a
 * @param b
 * @returns `true` if Dates are within the same month
 */
function isEqualMonth(a, b) {
    if (a == null || b == null) {
        return false;
    }
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}
/**
 * Returns `true` if provided `date` is within `min` or `max`.
 *
 * @param date
 * @param min
 * @param max
 * @returns
 */
function isInRange(date, min, max) {
    return date === clamp(date, min, max);
}
function isSameMonth(date1, date2) {
    if (!date1 || !date2) {
        return false;
    }
    return ((date1 === null || date1 === void 0 ? void 0 : date1.getFullYear()) === (date2 === null || date2 === void 0 ? void 0 : date2.getFullYear()) &&
        (date1 === null || date1 === void 0 ? void 0 : date1.getMonth()) === (date2 === null || date2 === void 0 ? void 0 : date2.getMonth()));
}
function isElementInViewport(element) {
    if (!element)
        return false;
    const rclientRect = element.getBoundingClientRect();
    return (rclientRect.top >= 0 &&
        rclientRect.left >= 0 &&
        rclientRect.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) &&
        rclientRect.right <=
            (window.innerWidth || document.documentElement.clientWidth));
}

function useUpDownOverlay(overlayPositionStates = [
    'down',
    'up',
]) {
    const context = ['web', 'closed', 0];
    let strategies = [];
    const availableStrategies = {
        down: new OriginRelative(// Expand down
        [0, 1], [0, 0], [0, 4], (o, c) => [o[0], c[1]], (x) => { var _a; return (_a = (x && x[0] === 'web')) !== null && _a !== void 0 ? _a : false; }),
        up: new OriginRelative(// Expand up
        [0, 0], [0, 1], [0, -4], (o, c) => [o[0], c[1]], (x) => { var _a; return (_a = (x && x[0] === 'web')) !== null && _a !== void 0 ? _a : false; }),
    };
    let normalizedPositions;
    if (typeof overlayPositionStates === 'string') {
        normalizedPositions = [overlayPositionStates];
    }
    else {
        normalizedPositions = overlayPositionStates;
    }
    normalizedPositions.forEach((overlayState) => {
        if (availableStrategies[overlayState]) {
            strategies = [availableStrategies[overlayState], ...strategies];
        }
    });
    return { context, strategies };
}

const defaultCopy = {
    nextMonthText: 'Next month',
    previousMonthText: 'Previous month',
    dueText: 'Due',
    chooseDateText: 'Choose date',
    selectedDateIsText: 'selected date is',
    lastDateAvailableText: 'Last date available',
    firstDateAvailableText: 'First date available',
    dateNotAvailableText: 'Date not available',
    dueDateText: 'due date',
};
const homeLocale = 'en-US';
const KiteDateInput = forwardRef((_a, ref) => {
    var { ariaLive = 'off', errorMessage, autoComplete = 'off', allowDisabledClick, block = (date) => false, due = undefined, min = undefined, max = undefined, firstDayOfWeek = 'sunday', helperText, isDisabled, id, inputRef, label, locale: dateLocale = 'en-US', copyOverrides, required = undefined, onBlur, onChange = (e) => e, onSelect = (e) => e, onFocus, onValueChange = (e) => e, value = '', hideInput, openPosition = 'down', children } = _a; __rest(_a, ["ariaLive", "errorMessage", "autoComplete", "allowDisabledClick", "block", "due", "min", "max", "firstDayOfWeek", "helperText", "isDisabled", "id", "inputRef", "label", "locale", "copyOverrides", "required", "onBlur", "onChange", "onSelect", "onFocus", "onValueChange", "value", "hideInput", "openPosition", "children"]);
    const uuid = useUID();
    const normalId = useNormalId();
    const componentBaseId = id || normalId;
    const calendarRef = useRef(null);
    const overlayTargetRef = useRef(null);
    const dateInputRef = useRef(null);
    const triggerRef = useRef(null);
    const firstFocusableRef = useRef(null);
    const dateInitialized = useRef(false);
    const [cursor, setCursor] = useState(null);
    const initialRender = useRef(false);
    const [valueLengthIncreased, setValueLengthIncreased] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [dayNames, setDayNames] = useState(null);
    const [isActiveFocus, setIsActiveFocus] = useState(null);
    const [days, setDays] = useState(null);
    const [weeks, setWeeks] = useState(null);
    const [dateValue, setDateValue] = useState('');
    const [focusedDate, setFocusedDate] = useState(new Date());
    const [monthName, setMonthName] = useState(null);
    const [nextMonthOutOfRange, setNextMonthOutOfRange] = useState(null);
    const [prevMonthOutOfRange, setPrevMonthOutOfRange] = useState(null);
    const today = new Date();
    const dateFormatOptions = {
        year: 'numeric',
        day: '2-digit',
        month: '2-digit',
    };
    const componentCopy = useMemo(() => (Object.assign(Object.assign({}, defaultCopy), copyOverrides)), [copyOverrides]);
    const [, labelChildren] = useMemo(() => {
        return partitionChildrenByDisplayName(children, 'KiteLabel');
    }, [children]);
    // Grab first label if at least 1 popover is present.  Otherwise return undefined.
    const [firstChildLabel] = labelChildren;
    const { strategies, context } = useUpDownOverlay(openPosition);
    useImperativeHandle(inputRef, () => dateInputRef.current);
    useEffect(() => {
        const _days = getCalendarViewMonth(new Date());
        setDays(_days);
        setWeeks(chunk(_days, 7));
    }, []);
    useEffect(() => {
        setDateValue(value);
    }, [value]);
    useEffect(() => {
        setPrevMonthOutOfRange(isPrevMonthOutOfRange());
        setNextMonthOutOfRange(isNextMonthOutOfRange());
    }, [monthName]);
    useEffect(() => {
        var _a;
        if (initialRender.current &&
            (prevMonthOutOfRange || nextMonthOutOfRange)) {
            (_a = firstFocusableRef === null || firstFocusableRef === void 0 ? void 0 : firstFocusableRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        if (prevMonthOutOfRange !== null && nextMonthOutOfRange !== null) {
            initialRender.current = true;
        }
    }, [prevMonthOutOfRange, nextMonthOutOfRange]);
    useEffect(() => {
        if (dateInitialized.current === true) {
            valueChangeHandler();
        }
        else {
            dateInitialized.current = true;
        }
    }, [dateValue]);
    useEffect(() => {
        setDayNames(getDayNames(dateLocale));
    }, [firstDayOfWeek]);
    useEffect(() => {
        if (days) {
            setWeeks(chunk(days, 7));
        }
    }, [days]);
    useEffect(() => {
        if (hideInput) {
            updateFocusedDate(typeof dateValue !== 'string' ? dateValue : new Date());
        }
    }, [hideInput, dateValue]);
    useEffect(() => {
        if (isActiveFocus) {
            setTimeout(() => {
                var _a;
                (_a = document
                    .getElementById(`${focusedDate.toLocaleDateString()}-${componentBaseId}`)) === null || _a === void 0 ? void 0 : _a.focus();
            }, 0);
        }
    }, [focusedDate]);
    useEffect(() => {
        var _a, _b;
        // If there is a mask that adds a  '/' increase cursor placement by one
        if (dateValue &&
            isNaN(Number(dateValue[cursor - 1])) &&
            valueLengthIncreased) {
            (_a = dateInputRef === null || dateInputRef === void 0 ? void 0 : dateInputRef.current) === null || _a === void 0 ? void 0 : _a.setSelectionRange(cursor + 1, cursor + 1);
        }
        else {
            // set cursor placement on re-render, because of mask
            (_b = dateInputRef === null || dateInputRef === void 0 ? void 0 : dateInputRef.current) === null || _b === void 0 ? void 0 : _b.setSelectionRange(cursor, cursor);
        }
    }, [dateInputRef, cursor, dateValue]);
    /**
     * Create custom event for users when date value changes. Provides formatted string, Date value, if the date is valid,
     * and if the date is invalid, provide a reason if the date is invalid
     */
    const valueChangeHandler = () => {
        let event = {
            stringValue: '',
            dateValue: null,
            validDate: true,
            invalidReason: null,
        };
        if (typeof dateValue === 'string') {
            event = Object.assign(Object.assign({}, event), { stringValue: dateValue, validDate: false, invalidReason: 'INVALID_FORMAT' });
        }
        else if (block(dateValue)) {
            event = Object.assign(Object.assign({}, event), { stringValue: dateValue.toLocaleDateString(homeLocale, dateFormatOptions), dateValue: dateValue, validDate: false, invalidReason: 'INVALID_BLOCKED_DATE' });
        }
        else if (!isInRange(dateValue, min, null)) {
            event = Object.assign(Object.assign({}, event), { stringValue: dateValue.toLocaleDateString(homeLocale, dateFormatOptions), dateValue: dateValue, validDate: false, invalidReason: 'INVALID_MIN_RANGE' });
        }
        else if (!isInRange(dateValue, null, max)) {
            event = Object.assign(Object.assign({}, event), { stringValue: dateValue.toLocaleDateString(), dateValue: dateValue, validDate: false, invalidReason: 'INVALID_MAX_RANGE' });
        }
        else {
            event = Object.assign(Object.assign({}, event), { stringValue: dateValue.toLocaleDateString(homeLocale, dateFormatOptions), dateValue: dateValue });
        }
        onValueChange(event);
    };
    /**
     * If the date entered in input is a valid format date, convert to Date and store as DateValue.
     * If string is not formatted as mm/dd/yyyy
     * @param e
     */
    const changeHandler = (e) => {
        var _a, _b;
        const val = (_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.value;
        const validMMDDYYY = new RegExp('^(0[1-9]|1[012])[- /.](0[1-9]|[12][0-9]|3[01])[- /.](19|20)\\d\\d$');
        if (typeof val === 'string' && validMMDDYYY.test(val)) {
            setDateValue(new Date(val));
        }
        else {
            setDateValue(maskPhoneMMDDYYYY(val));
        }
        setValueLengthIncreased(((_b = e === null || e === void 0 ? void 0 : e.nativeEvent) === null || _b === void 0 ? void 0 : _b.data) !== null);
        setCursor(e.target.selectionStart);
        onChange(e);
    };
    /**
     * Toggle the calendar to hide/display
     * @param e
     */
    const onToggle = (e) => {
        e.preventDefault();
        isOpen ? hide() : show();
        onSelect(e);
    };
    const show = () => {
        setIsOpen(true);
        updateFocusedDate(typeof dateValue !== 'string' ? dateValue : new Date());
    };
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                var _a, _b;
                // Initial focus will be handled differently depending on if the next/prev months are displayed
                if (firstFocusableRef === null || firstFocusableRef === void 0 ? void 0 : firstFocusableRef.current) {
                    (_a = firstFocusableRef === null || firstFocusableRef === void 0 ? void 0 : firstFocusableRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }
                else {
                    (_b = document
                        .getElementById(`${focusedDate.toLocaleDateString()}-${componentBaseId}`)) === null || _b === void 0 ? void 0 : _b.focus();
                }
            }, 200);
        }
    }, [isOpen]);
    const hide = () => {
        var _a;
        // if either calendar or trigger is within viewport we focus on input on hidtqe
        const overlayTargetVisible = isElementInViewport(overlayTargetRef === null || overlayTargetRef === void 0 ? void 0 : overlayTargetRef.current);
        const calendarVisible = isElementInViewport(calendarRef === null || calendarRef === void 0 ? void 0 : calendarRef.current);
        setIsOpen(false);
        if (overlayTargetVisible || calendarVisible) {
            (_a = dateInputRef === null || dateInputRef === void 0 ? void 0 : dateInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    const onFirstFocusableKeydown = (e) => {
        var _a;
        // This ensures focus is trapped inside the calendar
        if (!hideInput && e.code === 'Tab' && e.shiftKey) {
            (_a = document
                .getElementById(`${focusedDate.toLocaleDateString()}-${componentBaseId}`)) === null || _a === void 0 ? void 0 : _a.focus();
            e.preventDefault();
        }
        else if (e.code === 'Escape') {
            hide();
        }
    };
    const onNextMonthKeyDown = (e) => {
        if (e.code === 'Escape') {
            hide();
        }
    };
    const onPrevMonthClick = (e) => {
        e.preventDefault();
        addMonths(-1);
        onSelect(e);
    };
    const onNextMonthClick = (e) => {
        e.preventDefault();
        addMonths(1);
        onSelect(e);
    };
    const addDays = (days) => {
        const next = new Date(focusedDate);
        next.setDate(focusedDate.getDate() + days);
        updateFocusedDate(next);
    };
    const addMonths = (months) => {
        // Simply adding a month to the `focusDate` will possibly overflow.
        // `1/30/2025 + 1 month` => `2/30/2025 (invalid)` => `3/2/2025`
        const targetDate = new Date(focusedDate.getFullYear(), focusedDate.getMonth() + months, focusedDate.getDate());
        // Safely construct `nextMin` using date parts
        const nextMin = new Date(focusedDate.getFullYear(), focusedDate.getMonth() + months, 1);
        // Safely derive `nextMax` from `nextMin`
        const nextMax = getEndOfMonth(nextMin);
        // If `targetDate` is less than or equal to `nextMax`, it has not overflowed.  next date is `targetDate`.
        // Otherwise, next date `nextMax`, which is the closest valid date to the overflowed date.
        const next = new Date(targetDate <= nextMax ? targetDate : nextMax);
        updateFocusedDate(clamp(next, nextMin, nextMax));
    };
    const isPrevMonthOutOfRange = () => {
        if (!min) {
            return false;
        }
        const next = new Date(focusedDate);
        next.setMonth(focusedDate.getMonth() - 1);
        const nextMonthMax = getEndOfMonth(next);
        return nextMonthMax.getTime() <= min.getTime();
    };
    const isNextMonthOutOfRange = () => {
        if (!max) {
            return false;
        }
        const next = new Date(focusedDate);
        next.setMonth(focusedDate.getMonth() + 1);
        const nextMonthMin = getStartOfMonth(next);
        return nextMonthMin.getTime() >= max.getTime();
    };
    const addYears = (years) => {
        // Simply adding a year to the `focusDate` will possibly overflow.
        // `2/29/2024 + 1 year` => `2/29/2025 (invalid)` => `3/1/2025`
        const targetDate = new Date(focusedDate.getFullYear() + years, focusedDate.getMonth(), focusedDate.getDate());
        // Safely construct `nextMin` using date parts
        const nextMin = new Date(focusedDate.getFullYear() + years, focusedDate.getMonth(), 1);
        // Safely derive `nextMax` from `nextMin`
        const nextMax = getEndOfMonth(nextMin);
        // If `targetDate` is less than or equal to `nextMax`, it has not overflowed.  next date is `targetDate`.
        // Otherwise, next date `nextMax`, which is the closest valid date to the overflowed date.
        const next = new Date(targetDate <= nextMax ? targetDate : nextMax);
        updateFocusedDate(clamp(next, nextMin, nextMax));
    };
    const goToStartOfWeek = () => {
        const start = getStartOfWeek(focusedDate);
        updateFocusedDate(start);
    };
    const goToEndOfWeek = () => {
        const end = getEndOfWeek(focusedDate);
        setFocusedDate(end);
    };
    const getDayNames = (locale = 'en-US') => {
        const names = getWeekdayNames(locale);
        const offset = names.map((name, i) => {
            const adjusted = (i + DayOfWeekIndex(firstDayOfWeek)) % names.length;
            return names[adjusted];
        });
        return offset;
    };
    const getCalendarViewMonth = (date, first = DayOfWeekIndex(firstDayOfWeek)) => {
        const start = getStartOfWeek(getStartOfMonth(date), first);
        const end = getEndOfWeek(getEndOfMonth(date), first);
        return getDatesWithinRange(start, end);
    };
    const getStartOfWeek = (date, first = DayOfWeekIndex(firstDayOfWeek)) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = (day < first ? 7 : 0) + day - first;
        d.setDate(d.getDate() - diff);
        return d;
    };
    const getEndOfWeek = (date, first = DayOfWeekIndex(firstDayOfWeek)) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = (day < first ? -7 : 0) + 6 - (day - first);
        d.setDate(d.getDate() + diff);
        return d;
    };
    const onDaySelect = (e, date) => {
        if (allowDisabledClick || (!block(date) && isInRange(date, min, max))) {
            setDateValue(date);
            hide();
        }
        else {
            // In the case that a user sets the day (manually?) that has been disallowed,
            // we should change the calendar view to that month, for consistency
            updateFocusedDate(date);
        }
        onSelect(e);
    };
    const isDateDue = (date) => {
        if (due == undefined) {
            return false;
        }
        return isEqual(date, due);
    };
    const isDateFocused = (date) => {
        return isEqual(date, focusedDate);
    };
    const isDateInRange = (date) => {
        return isInRange(date, min, max);
    };
    const isDateSelected = (date) => {
        if (typeof dateValue === 'string') {
            return false;
        }
        return isEqual(date, dateValue);
    };
    const isDateToday = (date) => {
        return isEqual(date, today);
    };
    const isDatePrevMonth = (date) => {
        const dy = date.getFullYear();
        const dm = date.getMonth();
        const ay = focusedDate.getFullYear();
        const am = focusedDate.getMonth();
        // If year is less than active, or month is less than active within the same year
        return dy < ay || (dy === ay && dm < am);
    };
    const isDateNextMonth = (date) => {
        const dy = date.getFullYear();
        const dm = date.getMonth();
        const ay = focusedDate.getFullYear();
        const am = focusedDate.getMonth();
        // If year > active, or month > active within the same year
        return dy > ay || (dy === ay && dm > am);
    };
    const onDayKeyboardNavigation = (event) => {
        // If there are no prev/next month buttons, lock focus to date
        if (!hideInput && isSameMonth(min, max) && event.code === 'Tab') {
            event.preventDefault();
            return;
        }
        // Handle TAB separately because we don't want it to tab through days,
        // so it's different than other keyboard interactions
        if (!hideInput && event.code === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            firstFocusableRef === null || firstFocusableRef === void 0 ? void 0 : firstFocusableRef.current.focus();
            return;
        }
        // Shift + Tab should disable active day focus and return to arrows
        if (!hideInput && event.code === 'Tab' && event.shiftKey) {
            disableActiveFocus();
            return;
        }
        let handled = true;
        switch (event.code) {
            case 'Escape':
                hide();
                break;
            case 'ArrowRight':
                addDays(1);
                break;
            case 'ArrowLeft':
                addDays(-1);
                break;
            case 'ArrowDown':
                addDays(7);
                break;
            case 'ArrowUp':
                addDays(-7);
                break;
            case 'PageDown':
                if (event.shiftKey) {
                    addYears(1);
                }
                else {
                    addMonths(1);
                }
                break;
            case 'PageUp':
                if (event.shiftKey) {
                    addYears(-1);
                }
                else {
                    addMonths(-1);
                }
                break;
            case 'Home':
                goToStartOfWeek();
                break;
            case 'End':
                goToEndOfWeek();
                break;
            default:
                handled = false;
                break;
        }
        if (handled) {
            event.preventDefault();
            // Set a flag/state that means the calendar / day is actively focused
            enableActiveFocus();
        }
        return;
    };
    const getAccessibleLabel = (date, min, max) => {
        let accessibleLabel = `${block(date) && dateValue != date
            ? componentCopy.dateNotAvailableText
            : ''} ${date.getDate()} ${getMonthNameLong(date, dateLocale)} ${date.getFullYear()} ${isDateDue(date) ? componentCopy.dueDateText : ''}`;
        if ((date === null || date === void 0 ? void 0 : date.setHours(0, 0, 0, 0)) === (min === null || min === void 0 ? void 0 : min.setHours(0, 0, 0, 0))) {
            accessibleLabel = `${componentCopy.firstDateAvailableText} ${accessibleLabel}`;
        }
        else if ((date === null || date === void 0 ? void 0 : date.setHours(0, 0, 0, 0)) === (max === null || max === void 0 ? void 0 : max.setHours(0, 0, 0, 0))) {
            accessibleLabel = `${componentCopy.lastDateAvailableText} ${accessibleLabel}`;
        }
        return accessibleLabel;
    };
    const getAriaCurrent = (date) => {
        if (isDateSelected(date)) {
            return 'true';
        }
        else if (isDateToday(date)) {
            return 'date';
        }
        else {
            return null;
        }
    };
    const handleBlur = (e) => {
        var _a, _b;
        const relatedTarget = e['relatedTarget'];
        let relatedTargetInMenu = true;
        if (relatedTarget) {
            relatedTargetInMenu =
                ((_a = calendarRef === null || calendarRef === void 0 ? void 0 : calendarRef.current) === null || _a === void 0 ? void 0 : _a.contains(relatedTarget)) ||
                    ((_b = calendarRef === null || calendarRef === void 0 ? void 0 : calendarRef.current) === null || _b === void 0 ? void 0 : _b.isSameNode(relatedTarget)) ||
                    relatedTarget.classList.contains('kite-date-input__input-addon--button');
        }
        if (isOpen && (!relatedTarget || !relatedTargetInMenu)) {
            hide();
        }
    };
    const disableActiveFocus = () => {
        setIsActiveFocus(false);
    };
    const enableActiveFocus = () => {
        if (!isActiveFocus) {
            setIsActiveFocus(true);
        }
    };
    const updateFocusedDate = (day) => {
        const newFocusedDate = clamp(day, min, max);
        setFocusedDate(newFocusedDate);
        setDays(getCalendarViewMonth(newFocusedDate));
        setMonthName(getMonthNameLong(newFocusedDate, dateLocale));
    };
    /**
     * Return the formatted string value that is in the date input.
     */
    const getFormattedDate = () => {
        return typeof dateValue === 'string'
            ? dateValue
            : dateValue.toLocaleDateString(homeLocale, dateFormatOptions);
    };
    const labelElement = useMemo(() => {
        const labelProps = {
            id: `label-${componentBaseId}`,
            baseCSSClass: 'kite-date-input',
            htmlFor: id ? id : `kite-date-input-${normalId}`,
        };
        if (isDisabled) {
            labelProps.disabled = isDisabled;
        }
        if (helperText) {
            labelProps.helperText = helperText;
        }
        if (firstChildLabel) {
            return React.cloneElement(firstChildLabel, labelProps);
        }
        else {
            return null;
        }
    }, [firstChildLabel]);
    return (React.createElement("div", { className: classNames({
            'kite-date-input': !hideInput,
            'kite-date-input--open': isOpen && !hideInput,
            'kite-date-input--disabled': isDisabled,
            'kite-date-input--error': errorMessage,
        }), ref: ref },
        !hideInput && labelElement
            ? labelElement
            : label &&
                !hideInput && (React.createElement(KiteLabel, { id: `label-${componentBaseId}`, baseCSSClass: 'kite-date-input', htmlFor: id ? id : `kite-date-input-${normalId}`, helperText: helperText, disabled: isDisabled }, label)),
        !hideInput && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "kite-date-input__input-group", ref: overlayTargetRef },
                React.createElement("input", { className: "kite-date-input__input", pattern: "d{2}/d{2}/d{4}", placeholder: "mm/dd/yyyy", type: "text", id: id ? id : `kite-date-input-${normalId}`, autoComplete: autoComplete, disabled: isDisabled, required: required ? true : undefined, value: typeof dateValue === 'string'
                        ? dateValue
                        : dateValue.toLocaleDateString(homeLocale, dateFormatOptions), onChange: changeHandler, onBlur: onBlur, onFocus: onFocus, "aria-describedby": errorMessage
                        ? id
                            ? `${id}-error`
                            : `kite-date-input-error-${normalId}`
                        : null, ref: dateInputRef }),
                React.createElement("button", { className: "kite-date-input__input-addon kite-date-input__input-addon--button", type: "button", "aria-controls": `kite-date-picker-${normalId}`, "aria-expanded": isOpen, "aria-label": componentCopy.chooseDateText +
                        (getFormattedDate()
                            ? `, ${componentCopy.selectedDateIsText} ${getFormattedDate()}`
                            : ''), disabled: isDisabled, onClick: onToggle, ref: triggerRef },
                    React.createElement(KiteIcon, { icon: "ki-calendar", size: "1.5rem" })),
                React.createElement("div", { className: "kite-date-input__appearance" })),
            errorMessage && (React.createElement("span", { className: "kite-date-input__feedback", id: id ? `${id}-error` : `kite-date-input-error-${normalId}`, "aria-live": ariaLive }, errorMessage)))),
        (isOpen || hideInput) && (React.createElement(AttachedOverlay, { context: context, origin: overlayTargetRef.current, strategies: strategies, disableOverlay: hideInput, shouldReturnFocus: false },
            React.createElement("div", { id: `kite-date-picker-${uuid}`, ref: calendarRef, className: classNames({
                    'kite-picker': !hideInput,
                    'kite-picker--overlay': !hideInput,
                    'kite-picker--open': !hideInput && isOpen,
                }), onBlur: handleBlur, role: "region", "aria-roledescription": "Calendar", "aria-label": componentCopy.chooseDateText +
                    (getFormattedDate()
                        ? `, ${componentCopy.selectedDateIsText} ${getFormattedDate()}`
                        : '') },
                React.createElement("div", { className: classNames({
                        'kite-picker__content': !hideInput,
                    }) },
                    React.createElement("div", { className: classNames('kite-calendar', {
                            'kite-calendar--standalone': hideInput,
                        }) },
                        React.createElement("div", { className: "kite-calendar__header", onFocus: disableActiveFocus },
                            React.createElement("button", { className: classNames('kite-calendar__prev', {
                                    'kite-calendar__prev--hidden': prevMonthOutOfRange,
                                }), "aria-label": componentCopy.previousMonthText, onClick: onPrevMonthClick, onKeyDown: onFirstFocusableKeydown, ref: !prevMonthOutOfRange ? firstFocusableRef : null },
                                React.createElement(KiteIcon, { icon: "ki-chevron-left" })),
                            React.createElement("span", { className: "kite-calendar__month-year", "aria-live": "polite", "aria-atomic": "true" },
                                monthName,
                                " ", focusedDate === null || focusedDate === void 0 ? void 0 :
                                focusedDate.getFullYear()),
                            React.createElement("button", { className: classNames('kite-calendar__next', {
                                    'kite-calendar__next--hidden': nextMonthOutOfRange,
                                }), "aria-label": componentCopy.nextMonthText, onClick: onNextMonthClick, onKeyDown: prevMonthOutOfRange && !nextMonthOutOfRange
                                    ? onFirstFocusableKeydown
                                    : onNextMonthKeyDown, ref: prevMonthOutOfRange && !nextMonthOutOfRange
                                    ? firstFocusableRef
                                    : null },
                                React.createElement(KiteIcon, { icon: "ki-chevron-right" }))),
                        React.createElement("div", { role: "application" },
                            React.createElement("table", { className: "kite-calendar__table" },
                                React.createElement("thead", null,
                                    React.createElement("tr", null, dayNames === null || dayNames === void 0 ? void 0 : dayNames.map((dayName, i) => (React.createElement("th", { className: "kite-calendar__table-header", scope: "col", key: `kite-calendar-header-${i}-${uuid}` },
                                        React.createElement("span", { "aria-hidden": "true" }, dayName.substr(0, 1)),
                                        React.createElement("span", { className: "kite-assistive-text" }, dayName)))))),
                                React.createElement("tbody", null, weeks === null || weeks === void 0 ? void 0 : weeks.map((week, i) => (React.createElement("tr", { className: "kite-calendar__row", key: `kite-calendar-row-${i}-${uuid}` }, week === null || week === void 0 ? void 0 : week.map((date, j) => (React.createElement("td", { className: "kite-calendar__cell", key: `kite-calendar-column-${i}-${j}-${uuid}` }, !isDatePrevMonth(date) &&
                                    !isDateNextMonth(date) && (React.createElement("button", { type: "button", className: classNames('kite-calendar__day', {
                                        'kite-calendar__day--disabled': !allowDisabledClick && block(date),
                                        'kite-calendar__day--out-range': !allowDisabledClick &&
                                            !isInRange(date, min, max),
                                        'kite-calendar__day--unavailable': (allowDisabledClick &&
                                            block(date)) ||
                                            !isInRange(date, min, max),
                                        'kite-calendar__day--today': isDateToday(date),
                                        'kite-calendar__day--selected': isDateSelected(date),
                                    }), disabled: !allowDisabledClick &&
                                        !isDateInRange(date)
                                        ? true
                                        : undefined, tabIndex: isDateFocused(date) ? 0 : -1, "aria-current": getAriaCurrent(date), "aria-disabled": isDateSelected(date) || block(date)
                                        ? true
                                        : undefined, "aria-label": getAccessibleLabel(date, min, max), onClick: (e) => onDaySelect(e, date), onFocus: enableActiveFocus, onKeyDown: onDayKeyboardNavigation, id: `${date.toLocaleDateString()}-${componentBaseId}` },
                                    React.createElement("span", { className: "kite-calendar__day-number", "aria-hidden": "true" }, date.getDate()),
                                    isDateDue(date) && (React.createElement("span", { className: "kite-calendar__day-due" }, componentCopy.dueText))))))))))))))))))));
});

const KiteCalendar = forwardRef(({ block = (date) => false, due = undefined, min = undefined, max = undefined, firstDayOfWeek = 'sunday', isDisabled, id, onValueChange = (e) => e, locale = 'en-US', onSelect = (e) => e, value = '', allowDisabledClick, }, ref) => {
    return (React.createElement(KiteDateInput, { block: block, due: due, min: min, max: max, firstDayOfWeek: firstDayOfWeek, isDisabled: isDisabled, id: id, locale: locale, onValueChange: onValueChange, onSelect: onSelect, value: value, ref: ref, allowDisabledClick: allowDisabledClick, hideInput: true }));
});

const RenderForm = ({ props, children }) => !!props ? React.createElement("form", Object.assign({}, props), children) : React.createElement(React.Fragment, null, children);
/**
 * View the design specifications for [dialog](https://beam.kiteds.com/web/components/dialog/design.html)
 - Dialog will automatically render as a child of the `<body>` element to allow use of inert attribute for a11y and focus trapping
 - https://github.com/WICG/inert - Makes dom element and children inaccessible to readers and tabbing (useful for overlays and dialogs)
 */
const KiteDialogBase = (_a) => {
    var { allowClickAway, buttonPanel, children, className, icon, id, renderProps, open, onClose, suppressClose, title, returnFocusOnDeactivate } = _a, other = __rest(_a, ["allowClickAway", "buttonPanel", "children", "className", "icon", "id", "renderProps", "open", "onClose", "suppressClose", "title", "returnFocusOnDeactivate"]);
    const TITLE_ID = `${id}_title`;
    const [isScrolled, setIsScrolled] = useState(false);
    const containerRef = useRef(null);
    const contentClasses = classNames('kite-dialog__content', {
        'kite-dialog__content--scrolled': isScrolled,
    });
    const handleClickAway = (e) => {
        if (allowClickAway) {
            const target = e.target;
            if (Array.from(target.classList).find((c) => c === 'kite-dialog')) {
                onClose(e);
            }
        }
    };
    const onKeyDown = (e) => {
        if (e.key === 'Escape' && !suppressClose) {
            onClose(e);
        }
    };
    const handleScroll = (e) => {
        setIsScrolled(e.target['scrollTop'] > 0);
    };
    const strategies = [new ViewportCenter()];
    return (React.createElement(React.Fragment, null, open &&
        ReactDOM.createPortal(React.createElement(AttachedOverlay, { strategies: strategies, origin: document.body },
            React.createElement("div", { className: classNames('kite-dialog', 'rk-dialog', {
                    'rk-dialog__visible': open,
                }), id: `rk_dialog_${id}`, onClick: handleClickAway }, React.createElement(FocusTrap, { focusTrapOptions: {
                    clickOutsideDeactivates: allowClickAway,
                    returnFocusOnDeactivate: returnFocusOnDeactivate,
                    escapeDeactivates: !suppressClose,
                    fallbackFocus: () => containerRef.current,
                    tabbableOptions: {
                        displayCheck: 'none',
                    },
                } },
                React.createElement("div", Object.assign({ id: id + '_container', role: "dialog", tabIndex: -1, "aria-labelledby": TITLE_ID, className: classNames('kite-dialog__container', 'kite-card', className), ref: containerRef, onKeyDown: onKeyDown }, other),
                    React.createElement("div", { className: "kite-dialog__container-document", role: "document" },
                        React.createElement("h2", { className: "kite-dialog__title", id: TITLE_ID },
                            icon && React.createElement(KiteIcon, { icon: icon }),
                            title),
                        !suppressClose && (React.createElement("button", { type: "button", tabIndex: 0, className: "kite-dialog__dismiss-button", onClick: onClose, "aria-label": "Close" },
                            React.createElement(KiteIcon, { icon: "ki-x" }))),
                        React.createElement("div", { className: contentClasses, onScrollCapture: handleScroll },
                            React.createElement(RenderForm, { props: renderProps },
                                children,
                                !!buttonPanel && (React.createElement("div", { className: "kite-button-group" }, buttonPanel))))))))), document.body)));
};

const useButtonPanel = ({ primaryBtnLabel, onPrimaryBtnClick, primaryBtnVariant, primaryBtnColorVariant, primaryBtnLoading, secondaryBtnLabel, onSecondaryBtnClick, secondaryBtnVariant, secondaryBtnColorVariant, secondaryBtnLoading, tertiaryBtnLabel, onTertiaryBtnClick, tertiaryBtnVariant, tertiaryBtnColorVariant, tertiaryBtnLoading, reverseButtonOrder, }, isForm = false) => {
    // Hierarchical button data.  Contains Tiers and Aesthetic props.
    const buttonsPropsHierarchical = [
        {
            tier: 'primary',
            variant: primaryBtnVariant,
            colorVariant: primaryBtnColorVariant,
        },
        {
            tier: 'secondary',
            variant: secondaryBtnVariant,
            colorVariant: secondaryBtnColorVariant,
        },
        {
            tier: 'tertiary',
            variant: tertiaryBtnVariant,
            colorVariant: tertiaryBtnColorVariant,
        },
    ];
    // Functional Button Props, in Hierarchical Order.  Some props could be null.
    const buttonsPropsFunctional = [
        Object.assign(Object.assign({ loading: primaryBtnLoading, label: primaryBtnLabel }, (isForm && { type: 'submit' })), (!isForm && { onClick: onPrimaryBtnClick })),
        {
            onClick: onSecondaryBtnClick,
            loading: secondaryBtnLoading,
            label: secondaryBtnLabel,
        },
        {
            onClick: onTertiaryBtnClick,
            loading: tertiaryBtnLoading,
            label: tertiaryBtnLabel,
        },
    ];
    // Filter out null button props.  Results in list of actual button props to be rendered
    const actualButtonsPropsFunctional = buttonsPropsFunctional.filter(({ label }) => !!label);
    // Merge Absolute Button Tiers with Actual Button props into a lookup map.  Combine Functional and Aesthetic props into single set.
    const buttonTierPropsMap = actualButtonsPropsFunctional.reduce((acc, functionalProps, i) => (Object.assign(Object.assign({}, acc), { [buttonsPropsHierarchical[i].tier]: Object.assign(Object.assign({}, functionalProps), { variant: buttonsPropsHierarchical[i].variant, colorVariant: buttonsPropsHierarchical[i].colorVariant }) })), {});
    // Base ordering of button tiers, assuming 3 buttons are present
    const renderTiers = ['secondary', 'primary', 'tertiary'];
    // Primary and Secondary swap positions if `reverseButtonOrder` is set to true
    [renderTiers[0], renderTiers[1]] = reverseButtonOrder
        ? [renderTiers[1], renderTiers[0]]
        : [renderTiers[0], renderTiers[1]];
    // Extract the Tiers from the button map.  These are the actual button Tiers being used.
    const actualButtonTiers = Object.keys(buttonTierPropsMap);
    // Filter Render Tiers down to just those buttons that will be rendered
    const actualRenderTiers = renderTiers.filter((tier) => actualButtonTiers.includes(tier));
    // Map each Actual Render tier to its associated set of button props.  This results in button props in render order.
    const buttonsData = actualRenderTiers.map((tier) => buttonTierPropsMap[tier]);
    return buttonsData.map((_a) => {
        var { label } = _a, rest = __rest(_a, ["label"]);
        const { variant } = rest;
        return (React.createElement(KiteButton, Object.assign({ size: 'lg', key: `${variant}-${label}` }, rest), label));
    });
};

const defaultColorVariant = 'default';

/**
 * View the design specifications for [dialog](https://beam.kiteds.com/web/components/dialog/design.html)
 - Dialog will automatically render as a child of the `<body>` element to allow use of inert attribute for a11y and focus trapping
 - https://github.com/WICG/inert - Makes dom element and children inacessible to readers and tabbing (useful for overlays and dialogs)
 */
const KiteDialog = forwardRef((_a, ref) => {
    var { children, className, icon, id = 'defaultRKModalId', onClose, open, returnFocusOnDeactivate = true, title, primaryBtnLabel, onPrimaryBtnClick, primaryBtnVariant = 'primary', primaryBtnColorVariant = defaultColorVariant, primaryBtnLoading, suppressClose, allowClickAway = !suppressClose, secondaryBtnLabel, onSecondaryBtnClick, secondaryBtnVariant = 'secondary', secondaryBtnColorVariant = defaultColorVariant, secondaryBtnLoading, tertiaryBtnLabel, onTertiaryBtnClick, tertiaryBtnVariant = 'borderless', tertiaryBtnColorVariant = defaultColorVariant, tertiaryBtnLoading, reverseButtonOrder = false } = _a, other = __rest(_a, ["children", "className", "icon", "id", "onClose", "open", "returnFocusOnDeactivate", "title", "primaryBtnLabel", "onPrimaryBtnClick", "primaryBtnVariant", "primaryBtnColorVariant", "primaryBtnLoading", "suppressClose", "allowClickAway", "secondaryBtnLabel", "onSecondaryBtnClick", "secondaryBtnVariant", "secondaryBtnColorVariant", "secondaryBtnLoading", "tertiaryBtnLabel", "onTertiaryBtnClick", "tertiaryBtnVariant", "tertiaryBtnColorVariant", "tertiaryBtnLoading", "reverseButtonOrder"]);
    const buttonPanel = useButtonPanel({
        primaryBtnLabel,
        onPrimaryBtnClick,
        primaryBtnVariant,
        primaryBtnColorVariant,
        primaryBtnLoading,
        secondaryBtnLabel,
        onSecondaryBtnClick,
        secondaryBtnVariant,
        secondaryBtnColorVariant,
        secondaryBtnLoading,
        tertiaryBtnLabel,
        onTertiaryBtnClick,
        tertiaryBtnVariant,
        tertiaryBtnColorVariant,
        tertiaryBtnLoading,
        reverseButtonOrder,
    });
    return (React.createElement(KiteDialogBase, Object.assign({ allowClickAway: allowClickAway, id: id, className: className, icon: icon, open: open, onClose: onClose, title: title, buttonPanel: buttonPanel, suppressClose: suppressClose, returnFocusOnDeactivate: returnFocusOnDeactivate }, other), children));
});

/**
 * View the design specifications for [dialog](https://beam.kiteds.com/web/components/dialog/design.html)
  - Dialog will automatically render as a child of the `<body>` element to allow use of inert attribute for a11y and focus trapping
  - https://github.com/WICG/inert - Makes dom element and children inacessible to readers and tabbing (useful for overlays and dialogs)
 */
function KiteFormDialog({ children, className, formProps, icon, id, onClose, onSubmit, open, returnFocusOnDeactivate = true, suppressClose, allowClickAway = !suppressClose, title, submitBtnLabel, submitBtnVariant = 'primary', primaryBtnColorVariant = 'default', primaryBtnLoading, secondaryBtnLabel, onSecondaryBtnClick, secondaryBtnVariant = 'secondary', secondaryBtnColorVariant = 'default', secondaryBtnLoading, tertiaryBtnLabel, onTertiaryBtnClick, tertiaryBtnVariant = 'borderless', tertiaryBtnColorVariant = 'default', tertiaryBtnLoading, reverseButtonOrder = false, }, ref) {
    const handleSubmit = (e) => {
        onSubmit(e);
        return true;
    };
    const buttonPanel = useButtonPanel({
        primaryBtnLabel: submitBtnLabel,
        primaryBtnVariant: submitBtnVariant,
        primaryBtnColorVariant,
        primaryBtnLoading,
        secondaryBtnLabel,
        onSecondaryBtnClick,
        secondaryBtnVariant,
        secondaryBtnColorVariant,
        secondaryBtnLoading,
        tertiaryBtnLabel,
        onTertiaryBtnClick,
        tertiaryBtnVariant,
        tertiaryBtnColorVariant,
        tertiaryBtnLoading,
        reverseButtonOrder,
    }, true);
    return (React.createElement(KiteDialogBase, { allowClickAway: allowClickAway, id: id, icon: icon, open: open, onClose: onClose, title: title, renderProps: Object.assign(Object.assign({}, formProps), { ref, onSubmit: handleSubmit }), buttonPanel: buttonPanel, returnFocusOnDeactivate: returnFocusOnDeactivate, className: className, suppressClose: suppressClose }, children));
}
var KiteFormDialog$1 = forwardRef(KiteFormDialog);

/**
 * Find the best quadrant to render  a given element.  Determines based on width of the element, and the window screen, where the subject element would best be positioned.
 * @param subjectEl
 * @returns
 */
const findBestSide = (subjectEl, containerEl, mobileWidth) => {
    if (!!!subjectEl) {
        return 'bottom-right';
    }
    const rect = subjectEl.getBoundingClientRect();
    const container = containerEl === null || containerEl === void 0 ? void 0 : containerEl.getBoundingClientRect();
    const right = window.innerWidth - rect.right;
    let horiz;
    if (window.innerWidth <= mobileWidth ||
        (rect.width > right && rect.width > rect.left)) {
        if (rect.x - (container === null || container === void 0 ? void 0 : container.width) / 2 < 0) {
            horiz = 'right';
        }
        else if (rect.x + (container === null || container === void 0 ? void 0 : container.width) / 2 > window.innerWidth) {
            horiz = 'left';
        }
        else {
            horiz = 'center';
        }
    }
    else if (right >= rect.left) {
        horiz = 'right';
    }
    else {
        horiz = 'left';
    }
    const vert = window.innerHeight - rect.bottom >= rect.top ? 'bottom' : 'top';
    return `${vert}-${horiz}`;
};

/**
 * resolve the arrow offset for a dynamically positioned element
 * @param param0
 * @returns
 */
/**
 * resolve styles for a given elements  position
 * @param param0
 */
const calcElementPosition = ({ 
/** the quadrant to target the subject */
position, 
/** strategy - how to resolve */
strategy, 
/** container */
containerEl, 
/** the subject element to render */
subjectEl, 
/** the element to target positioning of the subject */
targetEl, 
/** the horizontal offset */
offsetX = 0, 
/** the vertical offset */
offsetY = 0, }) => {
    if (!!!containerEl || !!!targetEl || !!!subjectEl) {
        return undefined;
    }
    const targetRect = targetEl.getBoundingClientRect();
    const subjectRect = subjectEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();
    const horiz = position.split('-')[1];
    const vert = position.split('-')[0];
    let top;
    let bottom;
    let left;
    let right;
    {
        if (vert === 'top') {
            top =
                targetRect.bottom - containerRect.top - subjectRect.height + offsetY;
        }
        else if (vert === 'bottom') {
            top = targetRect.top - containerRect.top - offsetY;
        }
        if (horiz === 'center') {
            left = -(containerRect.left -
                (window.innerWidth - subjectRect.width) * 0.5);
        }
        else if (horiz === 'left') {
            left = targetRect.left - containerRect.left - subjectRect.width - offsetX;
        }
        else if (horiz === 'right') {
            left = targetRect.right - containerRect.left + offsetX;
        }
    }
    return {
        top,
        bottom,
        left,
        right,
        horiz,
        vert,
    };
};
/**
 * Get offset needed to translate the component off the screen
 * @param ele
 * @param direction
 */
const getDirectionOffset = (ele, direction = 'down') => {
    const position = ele.getBoundingClientRect();
    let offsetX = '0px';
    let offsetY = '0px';
    if (direction === 'down') {
        offsetY = `-${position.top + position.height}px`;
    }
    else if (direction === 'up') {
        offsetY = `calc(100vh - ${position.top}px)`;
    }
    else if (direction === 'right') {
        offsetX = `-${position.left + position.width}px`;
    }
    else if (direction === 'left') {
        offsetX = `calc(100vw - ${position.left}px)`;
    }
    return [offsetX, offsetY];
};

const KiteDynamicHintName = Symbol('KiteDynamicHint');
/**
 * A tooltip-type information bubble that is intended to be shown/hidden on focus/blur of an input field, providing a list of criteria that the field needs to pass to be valid.  However, the showing/hiding of the dynamic-hint is controlled by a prop, and the HTML element it aligns to is passed in as a prop, so it can point at and be shown anything you'd like. View the design standard [here](https://company-14496.frontify.com/d/xETwq0XBaQWU/kite-web-ui-guidelines#/components/dynamic-hints)
 */
function KiteDynamicHint(_a) {
    var { children, id, inputRef, name, show, title, position, __typeName = KiteDynamicHintName } = _a, other = __rest(_a, ["children", "id", "inputRef", "name", "show", "title", "position", "__typeName"]);
    const normalId = useNormalId();
    const resolvedId = id || `hint-${normalId}`;
    const hintRef = useRef(null);
    const [visited, setVisited] = useState(false);
    const [blurred, setBlurred] = useState(false);
    const [resolvedPosition, setResolvedPosition] = useState(null);
    useEffect(() => {
        setResolvedPosition(!position ? findBestSide(inputRef.current) : position);
    }, [position, inputRef, show]);
    useEffect(() => {
        if (show) {
            setVisited(true);
        }
        if (!show && visited) {
            setBlurred(true);
        }
    }, [show]);
    const classes = classNames(`type:${__typeName.toString()}`, 'kite-dynamic-hint', {
        'kite-dynamic-hint--show': show,
        'kite-dynamic-hint--left': (resolvedPosition === null || resolvedPosition === void 0 ? void 0 : resolvedPosition.indexOf('left')) !== -1,
        'kite-dynamic-hint--right': (resolvedPosition === null || resolvedPosition === void 0 ? void 0 : resolvedPosition.indexOf('right')) !== -1,
        'kite-dynamic-hint--align-end': (resolvedPosition === null || resolvedPosition === void 0 ? void 0 : resolvedPosition.indexOf('top')) !== -1,
        'kite-dynamic-hint--align-start': (resolvedPosition === null || resolvedPosition === void 0 ? void 0 : resolvedPosition.indexOf('bottom')) !== -1,
        'kite-dynamic-hint--inline': (resolvedPosition === null || resolvedPosition === void 0 ? void 0 : resolvedPosition.indexOf('center')) !== -1 && show,
    });
    const [positionStyle, setPositionStyle] = useState(null);
    useEffect(() => {
        if (resolvedPosition && (hintRef === null || hintRef === void 0 ? void 0 : hintRef.current) && (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current)) {
            const container = inputRef && inputRef.current
                ? inputRef.current.parentElement
                : undefined;
            const elementPosition = calcElementPosition({
                position: resolvedPosition,
                strategy: 'contained',
                subjectEl: hintRef.current,
                targetEl: inputRef.current,
                containerEl: container,
                offsetX: 15,
                offsetY: 15,
            });
            // if our resolved position indicates mobile then we want to allow vanilla to position.
            if ((elementPosition === null || elementPosition === void 0 ? void 0 : elementPosition.horiz) === 'center') {
                setPositionStyle({});
            }
            else {
                setPositionStyle(elementPosition);
            }
        }
    }, [resolvedPosition, hintRef, inputRef, show]);
    let hasValids = false, //
    hasInvalids = false;
    const rules = React.Children.map(children, (child) => {
        if (child.props.valid) {
            hasValids = true;
        }
        if (!child.props.valid) {
            hasInvalids = true;
        }
        return React.cloneElement(child, {});
    });
    const ariaProps = {
        'aria-label': hasValids && !hasInvalids && blurred
            ? `all criteria have been met.`
            : undefined,
        'aria-hidden': hasInvalids ? true : undefined,
    };
    // const context = ['web', 'closed', 0, 'bottom-center']; // TODO - commented out until working on the actual attached overlay integration tickets
    // const strategies = [
    //   // bottom-left
    //   new OriginRelative<Context>(
    //     [-1, -0.5],
    //     [0, 0],
    //     [-12, 4],
    //     (o, c) => [o[0], Math.max(80, c[1])],
    //     (x) => (x && x[0] === 'web') ?? false,
    //     'kite-dynamic-hint--bottom-left'
    //   ),
    //   //
    //   // //top-left
    //   new OriginRelative<Context>(
    //     [-1, 1],
    //     [0, 1],
    //     [-12, 16],
    //     (o, c) => [o[0], Math.max(80, c[1])],
    //     (x) => (x && x[0] === 'web') ?? false,
    //     'kite-dynamic-hint--top-left'
    //   ),
    //   //
    //   // //bottom-right
    //   new OriginRelative<Context>(
    //     [1, -0.5],
    //     [0, 0],
    //     [12, 4],
    //     (o, c) => [Math.max(o[0], c[0]), Math.max(80, c[1])],
    //     (x) => (x && x[0] === 'web') ?? false,
    //     'kite-dynamic-hint--bottom-right'
    //   ),
    //   //
    //   // //top-right
    //   new OriginRelative<Context>(
    //     [1, 1],
    //     [0, 1],
    //     [12, 16],
    //     (o, c) => [Math.max(o[0], c[0]), c[1]],
    //     (x) => (x && x[0] === 'web') ?? false,
    //     'kite-dynamic-hint--top-right'
    //   ),
    //
    //   //top
    //   new OriginRelative<Context>(
    //     [0, -0.5],
    //     [0, 1],
    //     [0, 0],
    //     (o, c) => [Math.max(o[0], c[0]), c[1]],
    //     (x) => (x && x[0] === 'web') ?? false,
    //     'kite-dynamic-hint--top-middle'
    //   ),
    //
    //   // bottom
    //   new OriginRelative<Context>(
    //     [0, 0],
    //     [0, 0],
    //     [0, 40],
    //     (o, c) => [Math.max(o[0], c[0]), c[1]],
    //     (x) => (x && x[0] === 'web') ?? false,
    //     'kite-dynamic-hint--bottom-middle'
    //   ),
    // ];
    return (
    // inputRef.current && // TODO - commented out until working on the actual attached overlay integration tickets
    // show && (
    //   <AttachedOverlay
    //     origin={inputRef?.current}
    //     strategies={strategies}
    //     context={context}
    //   >
    React.createElement("div", Object.assign({ id: resolvedId, className: classes, style: positionStyle, ref: hintRef }, ariaProps, other),
        React.createElement("p", { className: "kite-dynamic-hint__title", "aria-hidden": !hasInvalids && hasValids, "aria-label": "Remaining criteria:" }, title),
        React.createElement("ul", { className: "kite-dynamic-hint__criteria-list", "aria-hidden": !hasInvalids && hasValids }, rules))
    // </AttachedOverlay>
    // )
    );
}
KiteDynamicHint.displayName = 'KiteDynamicHint';

function KiteDynamicHintRule(_a) {
    var { children, id, valid } = _a, other = __rest(_a, ["children", "id", "valid"]);
    const normalId = useNormalId(id);
    const numberHintId = `number-hint-${normalId}`;
    const classes = classNames('kite-dynamic-hint__criteria-item', {
        'kite-dynamic-hint__criteria-item--error': !valid,
        'kite-dynamic-hint__criteria-item--success': valid,
    });
    const iconClasses = classNames({
        'kite-dynamic-hint__success-icon': valid,
        'kite-dynamic-hint__error-icon': !valid,
    });
    const icon = valid ? 'ki-checkmark-circle-f' : 'ki-caution-circle-f';
    const ariaProps = {
        'aria-hidden': valid,
    };
    return (React.createElement("li", Object.assign({ className: classes }, ariaProps, other),
        React.createElement("span", { className: iconClasses },
            React.createElement(KiteIcon, { icon: icon, ariaLabel: valid ? 'success' : 'incomplete' })),
        React.createElement("span", { className: "kite-assistive-text" }),
        React.createElement("span", { id: numberHintId, className: "kite-dynamic-hint__criteria-text" }, children)));
}

/**
 * [Grid design guidelines](https://beam.kiteds.com/web/styles/grid/design.html)
 */
const KiteGrid = (_a) => {
    var { noGutters, className, children } = _a, other = __rest(_a, ["noGutters", "className", "children"]);
    return (React.createElement("div", Object.assign({ className: classNames({
            'kite-grid': true,
            'kite-grid--no-gutters': noGutters,
            [className]: className,
        }) }, other), children));
};

/**
 * **xs:** 0-479px, **sm:** 480-599px, **md:** 600-839px, **lg:** 840-959px, **xl:** 960-1279px, **xxl:** 1280px+.
 *
 * [Grid design guidelines](https://beam.kiteds.com/web/styles/grid/design.html)
 *
 */
const KiteGridCell = (_a) => {
    var { ariaLabel = '', children, className, col = 12, xs, sm, md, lg, xl, xxl } = _a, other = __rest(_a, ["ariaLabel", "children", "className", "col", "xs", "sm", "md", "lg", "xl", "xxl"]);
    return (React.createElement("div", Object.assign({ "aria-label": ariaLabel, role: "region", className: classNames('kite-cell', {
            [`kite-cell-${col}`]: col,
            [`kite-cell-${xs}-xs`]: xs,
            [`kite-cell-${sm}-gt-xs`]: sm,
            [`kite-cell-${md}-gt-sm`]: md,
            [`kite-cell-${lg}-gt-md`]: lg,
            [`kite-cell-${xl}-gt-lg`]: xl,
            [`kite-cell-${xxl}-gt-xl`]: xxl,
            [className]: className,
        }) }, other), children));
};

const KiteImage = forwardRef((_a, ref) => {
    var { src, height, width, className: incomingClassName } = _a, other = __rest(_a, ["src", "height", "width", "className"]);
    const overRideProps = Object.assign(Object.assign({}, (!!height && { height })), (!!width && { width }));
    const style = Object.entries(overRideProps).reduce((acc, [dimension, value]) => (Object.assign(Object.assign({}, acc), { [`--max-${dimension}`]: value })), {});
    return (React.createElement("div", { className: classNames('kite-image', incomingClassName), style },
        React.createElement("div", { className: "kite-image__container" },
            React.createElement("img", Object.assign({ ref: ref, 
                // height={height}
                // width={width}
                src: src, alt: "Image" }, other)))));
});

/* 🤖 this file was generated by svg-to-ts */
const americanExpress = {
    name: 'american_express',
    data: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3296_47)"><g clip-path="url(#clip1_3296_47)"><path d="M0 0.429785C0 0.208871 0.179086 0.0297852 0.4 0.0297852H23.5404C23.7613 0.0297852 23.9404 0.208871 23.9404 0.429785V12.8365C23.9404 12.9129 23.9185 12.9877 23.8773 13.0521L22.9018 14.5764C22.8134 14.7144 22.8182 14.8924 22.9139 15.0255L23.8652 16.3483C23.9141 16.4163 23.9404 16.498 23.9404 16.5818V23.5702C23.9404 23.7911 23.7613 23.9702 23.5404 23.9702H0.4C0.179086 23.9702 0 23.7911 0 23.5702V11.9366C0 11.8402 0.0348601 11.747 0.0981579 11.6742L0.507243 11.2037C0.640326 11.0507 0.637805 10.8223 0.501377 10.6722L0.104024 10.2351C0.0370887 10.1615 0 10.0655 0 9.96603V0.429785Z" fill="#016FD0"/><path d="M4.64734 16.7122V12.9536H8.62703L9.054 13.5102L9.4951 12.9536H23.9404V16.453C23.9404 16.453 23.5626 16.7084 23.1257 16.7122H15.1271L14.6457 16.1197V16.7122H13.0681V15.7008C13.0681 15.7008 12.8526 15.842 12.3868 15.842H11.8498V16.7122H9.46134L9.03497 16.1436L8.60206 16.7122H4.64734Z" fill="white"/><path d="M0 10.1207L0.897435 8.02844H2.44946L2.95876 9.20043V8.02844H4.88806L5.19125 8.87552L5.48519 8.02844H14.1458V8.4543C14.1458 8.4543 14.601 8.02844 15.3493 8.02844L18.1593 8.03826L18.6598 9.19491V8.02844H20.2744L20.7187 8.69284V8.02844H22.3481V11.7871H20.7187L20.2929 11.1205V11.7871H17.9208L17.6822 11.1946H17.0445L16.8098 11.7871H15.2011C14.5573 11.7871 14.1458 11.3699 14.1458 11.3699V11.7871H11.7202L11.2388 11.1946V11.7871H2.21953L1.98115 11.1946H1.34547L1.10877 11.7871H0V10.1207Z" fill="white"/><path d="M1.21508 8.49176L0.00463867 11.3061H0.7927L1.01604 10.7425H2.31443L2.53662 11.3061H3.34204L2.13275 8.49176H1.21508ZM1.66292 9.14674L2.05869 10.1315H1.266L1.66292 9.14674Z" fill="#016FD0"/><path d="M3.42535 11.3057V8.49133L4.54523 8.49549L5.19659 10.31L5.83235 8.49133H6.94328V11.3057H6.23969V9.23195L5.49388 11.3057H4.87683L4.12894 9.23195V11.3057H3.42535Z" fill="#016FD0"/><path d="M7.42468 11.3057V8.49133H9.72059V9.12086H8.13567V9.60226H9.68356V10.1948H8.13567V10.6947H9.72059V11.3057H7.42468Z" fill="#016FD0"/><path d="M10.1279 8.49176V11.3061H10.8315V10.3063H11.1278L11.9714 11.3061H12.8312L11.9054 10.2692C12.2853 10.2372 12.6773 9.91108 12.6773 9.4048C12.6773 8.81257 12.2124 8.49176 11.6936 8.49176H10.1279ZM10.8315 9.12128H11.6358C11.8287 9.12128 11.9691 9.2722 11.9691 9.41753C11.9691 9.6045 11.7872 9.71378 11.6462 9.71378H10.8315V9.12128Z" fill="#016FD0"/><path d="M13.6829 11.3057H12.9645V8.49133H13.6829V11.3057Z" fill="#016FD0"/><path d="M15.3863 11.3057H15.2312C14.4809 11.3057 14.0254 10.7146 14.0254 9.91008C14.0254 9.0857 14.4758 8.49133 15.4233 8.49133H16.201V9.15789H15.3949C15.0102 9.15789 14.7382 9.45805 14.7382 9.91702C14.7382 10.462 15.0493 10.691 15.4974 10.691H15.6825L15.3863 11.3057Z" fill="#016FD0"/><path d="M16.9173 8.49176L15.7068 11.3061H16.4949L16.7183 10.7425H18.0166L18.2388 11.3061H19.0442L17.835 8.49176H16.9173ZM17.3651 9.14674L17.7609 10.1315H16.9682L17.3651 9.14674Z" fill="#016FD0"/><path d="M19.1264 11.3057V8.49133H20.0209L21.1631 10.2596V8.49133H21.8667V11.3057H21.0011L19.83 9.49117V11.3057H19.1264Z" fill="#016FD0"/><path d="M5.12878 16.2307V13.4164H7.42469V14.0459H5.83978V14.5273H7.38766V15.1198H5.83978V15.6197H7.42469V16.2307H5.12878Z" fill="#016FD0"/><path d="M16.3787 16.2307V13.4164H18.6746V14.0459H17.0897V14.5273H18.6302V15.1198H17.0897V15.6197H18.6746V16.2307H16.3787Z" fill="#016FD0"/><path d="M7.5138 16.2307L8.63166 14.8409L7.48718 13.4164H8.37361L9.0552 14.297L9.73912 13.4164H10.5908L9.46139 14.8236L10.5813 16.2307H9.69503L9.03322 15.364L8.38749 16.2307H7.5138Z" fill="#016FD0"/><path d="M10.6649 13.4169V16.2312H11.387V15.3425H12.1276C12.7543 15.3425 13.2293 15.01 13.2293 14.3635C13.2293 13.8279 12.8568 13.4169 12.2191 13.4169H10.6649ZM11.387 14.0534H12.167C12.3694 14.0534 12.5141 14.1775 12.5141 14.3774C12.5141 14.5652 12.3702 14.7014 12.1647 14.7014H11.387V14.0534Z" fill="#016FD0"/><path d="M13.5347 13.4164V16.2307H14.2383V15.2309H14.5346L15.3782 16.2307H16.238L15.3122 15.1939C15.6921 15.1618 16.0841 14.8357 16.0841 14.3294C16.0841 13.7372 15.6192 13.4164 15.1004 13.4164H13.5347ZM14.2383 14.0459H15.0426C15.2355 14.0459 15.3759 14.1968 15.3759 14.3422C15.3759 14.5291 15.194 14.6384 15.053 14.6384H14.2383V14.0459Z" fill="#016FD0"/><path d="M19.0005 16.2307V15.6197H20.4086C20.617 15.6197 20.7072 15.5071 20.7072 15.3836C20.7072 15.2653 20.6173 15.1457 20.4086 15.1457H19.7723C19.2192 15.1457 18.9112 14.8087 18.9112 14.3028C18.9112 13.8516 19.1933 13.4164 20.0152 13.4164H21.3853L21.0891 14.0496H19.9041C19.6776 14.0496 19.6078 14.1685 19.6078 14.282C19.6078 14.3986 19.694 14.5273 19.8671 14.5273H20.5336C21.1502 14.5273 21.4177 14.877 21.4177 15.335C21.4177 15.8274 21.1196 16.2307 20.5 16.2307H19.0005Z" fill="#016FD0"/><path d="M21.5828 16.2307V15.6197H22.9909C23.1993 15.6197 23.2895 15.5071 23.2895 15.3836C23.2895 15.2653 23.1995 15.1457 22.9909 15.1457H22.3546C21.8015 15.1457 21.4935 14.8087 21.4935 14.3028C21.4935 13.8516 21.7756 13.4164 22.5975 13.4164H23.9676L23.6713 14.0496H22.4864C22.2598 14.0496 22.1901 14.1685 22.1901 14.282C22.1901 14.3986 22.2763 14.5273 22.4493 14.5273H23.1159C23.7324 14.5273 24 14.877 24 15.335C24 15.8274 23.7019 16.2307 23.0823 16.2307H21.5828Z" fill="#016FD0"/></g></g><defs><clipPath id="clip0_3296_47"><rect width="24" height="24" fill="white"/></clipPath><clipPath id="clip1_3296_47"><rect width="24" height="23.9404" fill="white" transform="translate(0 0.0297852)"/></clipPath></defs></svg>`,
};

/* 🤖 this file was generated by svg-to-ts */
const bankDark = {
    name: 'bank_dark',
    data: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3296_65)"><g clip-path="url(#clip1_3296_65)"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.9367 0.147644C12.4263 0.147644 12.9069 0.278717 13.3286 0.527254L24 6.70652V9.9965H20.3093V18.3058H22.1441V20.1617H1.85589V18.3058H3.69069V9.9965H0V6.70652L10.5448 0.527254C10.9666 0.278717 11.4472 0.147644 11.9367 0.147644ZM11.0088 9.9965H5.4833V18.3058H11.0088V9.9965ZM12.8647 18.3058H18.3902V9.9965H12.8647V18.3058ZM1.79262 8.1617H22.0808V7.761L12.4007 2.13007C12.2583 2.05254 12.0988 2.01192 11.9367 2.01192C11.7746 2.01192 11.6151 2.05254 11.4728 2.13007L1.79262 7.761V8.1617ZM0 21.9965H23.9789V23.8524H0V21.9965Z" fill="white"/></g></g><defs><clipPath id="clip0_3296_65"><rect width="24" height="24" fill="white"/></clipPath><clipPath id="clip1_3296_65"><rect y="0.147644" width="24" height="23.7047" rx="1" fill="white"/></clipPath></defs></svg>`,
};

/* 🤖 this file was generated by svg-to-ts */
const bank = {
    name: 'bank',
    data: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3296_26)"><g clip-path="url(#clip1_3296_26)"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.9367 0.147644C12.4263 0.147644 12.9069 0.278717 13.3286 0.527254L24 6.70652V9.9965H20.3093V18.3058H22.1441V20.1617H1.85589V18.3058H3.69069V9.9965H0V6.70652L10.5448 0.527254C10.9666 0.278717 11.4472 0.147644 11.9367 0.147644ZM11.0088 9.9965H5.4833V18.3058H11.0088V9.9965ZM12.8647 18.3058H18.3902V9.9965H12.8647V18.3058ZM1.79262 8.1617H22.0808V7.761L12.4007 2.13007C12.2583 2.05254 12.0988 2.01192 11.9367 2.01192C11.7746 2.01192 11.6151 2.05254 11.4728 2.13007L1.79262 7.761V8.1617ZM0 21.9965H23.9789V23.8524H0V21.9965Z" fill="black"/></g></g><defs><clipPath id="clip0_3296_26"><rect width="24" height="24" fill="white"/></clipPath><clipPath id="clip1_3296_26"><rect y="0.147644" width="24" height="23.7047" rx="1" fill="white"/></clipPath></defs></svg>`,
};

/* 🤖 this file was generated by svg-to-ts */
const discover = {
    name: 'discover',
    data: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3296_40)"><path d="M0.263489 4.79041V18.5596C0.263489 18.8495 0.500617 19.0866 0.79044 19.0866H15.4081C19.4998 19.0866 22.8196 15.7694 22.8196 11.675C22.8196 7.58325 19.5025 4.26346 15.4081 4.26346H0.79044C0.500617 4.26346 0.263489 4.50059 0.263489 4.79041Z" fill="white"/><path d="M2.04458 10.0099H1.11188V13.2717H2.04195C2.53465 13.2717 2.89297 13.1558 3.20651 12.8949C3.57801 12.5867 3.79933 12.123 3.79933 11.6434C3.79933 10.6818 3.08004 10.0099 2.04722 10.0099H2.04458ZM2.78758 12.4602C2.58734 12.6394 2.32914 12.7184 1.91811 12.7184H1.74685V10.5606H1.91811C2.32914 10.5606 2.57944 10.6343 2.78758 10.824C3.0089 11.019 3.14064 11.3246 3.14064 11.6355C3.14064 11.9464 3.0089 12.2626 2.78758 12.4576V12.4602Z" fill="#231F20"/><path d="M4.72146 10.0099H4.08649V13.2717H4.72146V10.0099Z" fill="#231F20"/><path d="M6.27862 11.2614C5.89658 11.1191 5.78592 11.0269 5.78592 10.8504C5.78592 10.6449 5.98616 10.4894 6.26018 10.4894C6.44988 10.4894 6.60797 10.5684 6.77396 10.7529L7.10593 10.3181C6.83192 10.0784 6.50521 9.95718 6.14688 9.95718C5.56987 9.95718 5.12987 10.3577 5.12987 10.8925C5.12987 11.3431 5.33538 11.5723 5.93347 11.7857C6.18113 11.8727 6.31024 11.9333 6.37347 11.9701C6.49994 12.0518 6.56318 12.1704 6.56318 12.3074C6.56318 12.5709 6.35239 12.7685 6.06784 12.7685C5.76484 12.7685 5.51981 12.6157 5.37227 12.3337L4.96124 12.7289C5.2537 13.1584 5.60676 13.3508 6.09155 13.3508C6.75288 13.3508 7.21659 12.9107 7.21659 12.281C7.21659 11.762 7.00318 11.5275 6.27862 11.264V11.2614Z" fill="#231F20"/><path d="M7.41681 11.6434C7.41681 12.6025 8.17035 13.3455 9.13731 13.3455C9.41132 13.3455 9.64581 13.2928 9.93564 13.1558V12.4075C9.68007 12.6631 9.45611 12.7658 9.16629 12.7658C8.52604 12.7658 8.07023 12.3021 8.07023 11.6408C8.07023 11.0137 8.53922 10.521 9.13731 10.521C9.4403 10.521 9.66953 10.629 9.93564 10.8872V10.139C9.65635 9.99668 9.42713 9.93872 9.15311 9.93872C8.18879 9.93872 7.41681 10.6975 7.41681 11.646V11.6434Z" fill="#231F20"/><path d="M14.9812 12.1994L14.1118 10.0099H13.4162L14.7994 13.3534H15.142L16.5516 10.0099H15.8613L14.9812 12.1994Z" fill="#231F20"/><path d="M16.8387 13.2717H18.6436V12.7184H17.4764V11.8384H18.6014V11.2851H17.4764V10.5632H18.6436V10.0099H16.8387V13.2717Z" fill="#231F20"/><path d="M21.1623 10.9716C21.1623 10.3603 20.7408 10.0099 20.0083 10.0099H19.0651V13.2717H19.7V11.9622H19.7844L20.6644 13.2717H21.4469L20.4193 11.8964C20.8989 11.7989 21.1623 11.4722 21.1623 10.9716ZM19.8871 11.5117H19.7V10.5237H19.895C20.2902 10.5237 20.5063 10.6897 20.5063 11.0085C20.5063 11.3273 20.2902 11.5117 19.8845 11.5117H19.8871Z" fill="#231F20"/><path d="M21.6709 10.1811C21.6709 10.1232 21.6314 10.0916 21.5629 10.0916H21.4706V10.3787H21.5391V10.2681L21.6208 10.3787H21.7051L21.6103 10.2602C21.6498 10.2496 21.6735 10.2207 21.6735 10.1811H21.6709ZM21.5497 10.2207H21.5391V10.1442H21.5523C21.5866 10.1442 21.605 10.1574 21.605 10.1811C21.605 10.2048 21.5866 10.2207 21.5523 10.2207H21.5497Z" fill="#231F20"/><path d="M21.576 9.98352C21.4363 9.98352 21.3257 10.0942 21.3257 10.2338C21.3257 10.3735 21.439 10.4841 21.576 10.4841C21.713 10.4841 21.8237 10.3708 21.8237 10.2338C21.8237 10.0968 21.713 9.98352 21.576 9.98352ZM21.5734 10.442C21.4627 10.442 21.3731 10.3498 21.3731 10.2365C21.3731 10.1232 21.4627 10.0309 21.5734 10.0309C21.684 10.0309 21.771 10.1258 21.771 10.2365C21.771 10.3471 21.6814 10.442 21.5734 10.442Z" fill="#231F20"/><path d="M13.598 11.6355C13.598 12.5893 12.826 13.3639 11.8696 13.3639C10.9132 13.3639 10.1412 12.592 10.1412 11.6355C10.1412 10.6791 10.9132 9.90714 11.8696 9.90714C12.826 9.90714 13.598 10.6791 13.598 11.6355Z" fill="#F47922"/><path d="M23.2096 4H0.790427C0.353057 4 0 4.35306 0 4.79043V18.5597C0 18.997 0.353057 19.3501 0.790427 19.3501H23.2096C23.6469 19.3501 24 18.997 24 18.5597V4.79043C24 4.35306 23.6469 4 23.2096 4ZM22.8196 11.675C22.8196 15.7668 19.5025 19.0866 15.4081 19.0866H0.790427C0.500604 19.0866 0.263476 18.8495 0.263476 18.5597V4.79043C0.263476 4.5006 0.500604 4.26348 0.790427 4.26348H15.4081C19.4998 4.26348 22.8196 7.58063 22.8196 11.675Z" fill="#F47922"/></g><defs><clipPath id="clip0_3296_40"><rect y="4" width="24" height="15.3501" rx="1" fill="white"/></clipPath></defs></svg>`,
};

/* 🤖 this file was generated by svg-to-ts */
const genericCardDark = {
    name: 'generic_card_dark',
    data: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3296_74)"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.5666 3.42856H21.4139C22.0998 3.42856 22.7576 3.70102 23.2425 4.18601C23.7275 4.671 24 5.32879 24 6.01467V18.0048C24 18.3435 23.933 18.6789 23.8028 18.9915C23.6726 19.3042 23.4818 19.588 23.2414 19.8266C23.001 20.0652 22.7157 20.2538 22.4021 20.3816C22.0884 20.5095 21.7526 20.5739 21.4139 20.5714H7.47824e-05V12.0098V6.85712V6.01467C-0.00251068 5.67599 0.0619683 5.34014 0.18979 5.02649C0.317612 4.71284 0.506248 4.42759 0.74483 4.18719C0.983411 3.94679 1.26722 3.75598 1.57988 3.62578C1.89255 3.49557 2.2279 3.42855 2.5666 3.42856ZM1.77018 5.68478C1.72686 5.78937 1.70456 5.90147 1.70456 6.01467V6.85712H22.2759V6.01467C22.2759 5.90147 22.2536 5.78937 22.2103 5.68478C22.167 5.5802 22.1035 5.48517 22.0234 5.40512C21.9434 5.32507 21.8484 5.26157 21.7438 5.21825C21.6392 5.17493 21.5271 5.15263 21.4139 5.15263H2.5666C2.4534 5.15263 2.3413 5.17493 2.23671 5.21825C2.13212 5.26157 2.03709 5.32507 1.95705 5.40512C1.877 5.48517 1.8135 5.5802 1.77018 5.68478ZM22.2759 8.58119H1.70456V10.2857H22.2759V8.58119ZM1.70456 18.8669H21.4139C21.6425 18.8669 21.8618 18.7761 22.0234 18.6144C22.1851 18.4527 22.2759 18.2335 22.2759 18.0048V12.0098H1.70456V18.8669ZM2.56661 15.4383H11.9903V17.1624H2.56661V15.4383Z" fill="white"/></g><defs><clipPath id="clip0_3296_74"><rect y="3.42856" width="24" height="17.1429" rx="1" fill="white"/></clipPath></defs></svg>`,
};

/* 🤖 this file was generated by svg-to-ts */
const genericCardDisabledDark = {
    name: 'generic_card_disabled_dark',
    data: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3296_88)"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.5666 3.42856H21.4139C22.0998 3.42856 22.7576 3.70102 23.2425 4.18601C23.7275 4.671 24 5.32879 24 6.01467V18.0048C24 18.3435 23.933 18.6789 23.8028 18.9915C23.6726 19.3042 23.4818 19.588 23.2414 19.8266C23.001 20.0652 22.7157 20.2538 22.4021 20.3816C22.0884 20.5095 21.7526 20.5739 21.4139 20.5714H7.47824e-05V12.0098V6.85712V6.01467C-0.00251068 5.67599 0.0619683 5.34014 0.18979 5.02649C0.317612 4.71284 0.506248 4.42759 0.74483 4.18719C0.983411 3.94679 1.26722 3.75598 1.57988 3.62578C1.89255 3.49557 2.2279 3.42855 2.5666 3.42856ZM1.77018 5.68478C1.72686 5.78937 1.70456 5.90147 1.70456 6.01467V6.85712H22.2759V6.01467C22.2759 5.90147 22.2536 5.78937 22.2103 5.68478C22.167 5.5802 22.1035 5.48517 22.0234 5.40512C21.9434 5.32507 21.8484 5.26157 21.7438 5.21825C21.6392 5.17493 21.5271 5.15263 21.4139 5.15263H2.5666C2.4534 5.15263 2.3413 5.17493 2.23671 5.21825C2.13212 5.26157 2.03709 5.32507 1.95705 5.40512C1.877 5.48517 1.8135 5.5802 1.77018 5.68478ZM22.2759 8.58119H1.70456V10.2857H22.2759V8.58119ZM1.70456 18.8669H21.4139C21.6425 18.8669 21.8618 18.7761 22.0234 18.6144C22.1851 18.4527 22.2759 18.2335 22.2759 18.0048V12.0098H1.70456V18.8669ZM2.56661 15.4383H11.9903V17.1624H2.56661V15.4383Z" fill="#63738A"/></g><defs><clipPath id="clip0_3296_88"><rect y="3.42856" width="24" height="17.1429" rx="1" fill="white"/></clipPath></defs></svg>`,
};

/* 🤖 this file was generated by svg-to-ts */
const genericCardDisabled = {
    name: 'generic_card_disabled',
    data: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3296_56)"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.5666 3.42856H21.4139C22.0998 3.42856 22.7576 3.70102 23.2425 4.18601C23.7275 4.671 24 5.32879 24 6.01467V18.0048C24 18.3435 23.933 18.6789 23.8028 18.9915C23.6726 19.3042 23.4818 19.588 23.2414 19.8266C23.001 20.0652 22.7157 20.2538 22.4021 20.3816C22.0884 20.5095 21.7526 20.5739 21.4139 20.5714H7.47824e-05V12.0098V6.85712V6.01467C-0.00251068 5.67599 0.0619683 5.34014 0.18979 5.02649C0.317612 4.71284 0.506248 4.42759 0.74483 4.18719C0.983411 3.94679 1.26722 3.75598 1.57988 3.62578C1.89255 3.49557 2.2279 3.42855 2.5666 3.42856ZM1.77018 5.68478C1.72686 5.78937 1.70456 5.90147 1.70456 6.01467V6.85712H22.2759V6.01467C22.2759 5.90147 22.2536 5.78937 22.2103 5.68478C22.167 5.5802 22.1035 5.48517 22.0234 5.40512C21.9434 5.32507 21.8484 5.26157 21.7438 5.21825C21.6392 5.17493 21.5271 5.15263 21.4139 5.15263H2.5666C2.4534 5.15263 2.3413 5.17493 2.23671 5.21825C2.13212 5.26157 2.03709 5.32507 1.95705 5.40512C1.877 5.48517 1.8135 5.5802 1.77018 5.68478ZM22.2759 8.58119H1.70456V10.2857H22.2759V8.58119ZM1.70456 18.8669H21.4139C21.6425 18.8669 21.8618 18.7761 22.0234 18.6144C22.1851 18.4527 22.2759 18.2335 22.2759 18.0048V12.0098H1.70456V18.8669ZM2.56661 15.4383H11.9903V17.1624H2.56661V15.4383Z" fill="#9BA9BD"/></g><defs><clipPath id="clip0_3296_56"><rect y="3.42856" width="24" height="17.1429" rx="1" fill="white"/></clipPath></defs></svg>`,
};

/* 🤖 this file was generated by svg-to-ts */
const genericCard = {
    name: 'generic_card',
    data: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3296_35)"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.5666 3.42856H21.4139C22.0998 3.42856 22.7576 3.70102 23.2425 4.18601C23.7275 4.671 24 5.32879 24 6.01467V18.0048C24 18.3435 23.933 18.6789 23.8028 18.9915C23.6726 19.3042 23.4818 19.588 23.2414 19.8266C23.001 20.0652 22.7157 20.2538 22.4021 20.3816C22.0884 20.5095 21.7526 20.5739 21.4139 20.5714H7.47824e-05V12.0098V6.85712V6.01467C-0.00251068 5.67599 0.0619683 5.34014 0.18979 5.02649C0.317612 4.71284 0.506248 4.42759 0.74483 4.18719C0.983411 3.94679 1.26722 3.75598 1.57988 3.62578C1.89255 3.49557 2.2279 3.42855 2.5666 3.42856ZM1.77018 5.68478C1.72686 5.78937 1.70456 5.90147 1.70456 6.01467V6.85712H22.2759V6.01467C22.2759 5.90147 22.2536 5.78937 22.2103 5.68478C22.167 5.5802 22.1035 5.48517 22.0234 5.40512C21.9434 5.32507 21.8484 5.26157 21.7438 5.21825C21.6392 5.17493 21.5271 5.15263 21.4139 5.15263H2.5666C2.4534 5.15263 2.3413 5.17493 2.23671 5.21825C2.13212 5.26157 2.03709 5.32507 1.95705 5.40512C1.877 5.48517 1.8135 5.5802 1.77018 5.68478ZM22.2759 8.58119H1.70456V10.2857H22.2759V8.58119ZM1.70456 18.8669H21.4139C21.6425 18.8669 21.8618 18.7761 22.0234 18.6144C22.1851 18.4527 22.2759 18.2335 22.2759 18.0048V12.0098H1.70456V18.8669ZM2.56661 15.4383H11.9903V17.1624H2.56661V15.4383Z" fill="black"/></g><defs><clipPath id="clip0_3296_35"><rect y="3.42856" width="24" height="17.1429" rx="1" fill="white"/></clipPath></defs></svg>`,
};

/* 🤖 this file was generated by svg-to-ts */
const mastercard = {
    name: 'mastercard',
    data: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3296_17)"><path fill-rule="evenodd" clip-rule="evenodd" d="M14.8317 12.0002C14.8317 14.3647 13.7236 16.4726 11.9984 17.8314C10.2748 16.4726 9.16667 14.3647 9.16667 12.0002C9.16667 9.63571 10.2748 7.52783 11.9984 6.16906C13.7236 7.52783 14.8317 9.63571 14.8317 12.0002Z" fill="#FF5F00"/><path fill-rule="evenodd" clip-rule="evenodd" d="M11.9984 17.8314C10.7376 18.8237 9.14502 19.4167 7.4154 19.4167C3.31831 19.4167 0 16.0969 0 11.9998C0 7.90417 3.31831 4.58289 7.4154 4.58289C9.14502 4.58289 10.7376 5.1752 11.9984 6.16906C10.2733 7.52635 9.16667 9.63571 9.16667 12.0002C9.16667 14.3662 10.2733 16.4726 11.9984 17.8314Z" fill="#EB001B"/><path fill-rule="evenodd" clip-rule="evenodd" d="M24 12.0002C24 16.0958 20.6787 19.4171 16.5831 19.4171C14.852 19.4171 13.2593 18.8237 11.9984 17.8314C13.7221 16.4726 14.8317 14.3647 14.8317 12.0002C14.8317 9.63571 13.7221 7.52783 11.9984 6.16906C13.2593 5.17668 14.852 4.58333 16.5831 4.58333C20.6787 4.58333 24 7.90461 24 12.0002Z" fill="#F79E1B"/><path fill-rule="evenodd" clip-rule="evenodd" d="M23.7676 16.5959V16.3081H23.6919L23.6059 16.5054L23.5198 16.3081H23.4442V16.5959H23.4976V16.3793L23.5792 16.5662H23.6341L23.7142 16.3793V16.5959H23.7676ZM23.2914 16.5959V16.357H23.3878V16.3096H23.1431V16.357H23.2395V16.5959H23.2914Z" fill="#F79E1B"/></g><defs><clipPath id="clip0_3296_17"><rect y="4.58289" width="24" height="14.8342" rx="1" fill="white"/></clipPath></defs></svg>`,
};

/* 🤖 this file was generated by svg-to-ts */
const visaDark = {
    name: 'visa_dark',
    data: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3296_79)"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.6941 8.0003C16.2906 7.99333 16.8824 8.10682 17.434 8.33397L17.1718 10.0142L16.993 9.91887C16.5372 9.71091 16.0401 9.60904 15.5392 9.62096C14.7885 9.62096 14.431 9.96654 14.431 10.2883C14.431 10.61 14.8481 10.896 15.5154 11.2535C16.6356 11.8017 17.1599 12.4452 17.148 13.3508C17.148 14.9715 15.8133 16.0082 13.7756 16.0082C13.0317 16.0064 12.2953 15.8608 11.6068 15.5793L11.8808 13.8394L12.1311 13.9705C12.6957 14.2551 13.3223 14.3943 13.9543 14.3757C14.5144 14.3757 15.146 14.1373 15.146 13.613C15.146 13.2794 14.8957 13.041 14.1331 12.6597C13.3704 12.2784 12.429 11.6587 12.4409 10.5505C12.4171 9.04896 13.7875 8.0003 15.6941 8.0003ZM11.6306 8.16714L10.4389 15.9129H8.47269L9.66435 8.16714H11.6306ZM22.427 8.16714L24 15.9129H22.2006C22.2006 15.9129 22.0218 15.0192 21.9623 14.7213H19.4717L19.0665 15.9129H17.0288L19.9126 8.76297C19.9802 8.55462 20.1184 8.37636 20.3032 8.25891C20.4881 8.14145 20.7082 8.09214 20.9255 8.11947H22.427V8.16714ZM3.15789 8.16714C3.35863 8.14524 3.56031 8.19908 3.72343 8.31811C3.88654 8.43714 3.99934 8.61278 4.03972 8.81063L4.69514 12.2903V12.3737L4.89772 13.4462L6.79245 8.16714H8.85402L5.79146 15.901H3.77756L2.04965 9.18004C1.40984 8.79977 0.719919 8.51097 0 8.32205V8.16714H3.15789ZM21.0328 10.2645L21.1639 10.9318C21.1639 10.9318 21.5333 12.7789 21.6167 13.1721H20.0556C20.2105 12.7193 20.8183 11.0033 20.8183 11.0033C20.8183 11.0033 20.9374 10.5505 21.0804 10.2645H21.0328Z" fill="white"/></g><defs><clipPath id="clip0_3296_79"><rect y="8" width="24" height="8.00825" rx="1" fill="white"/></clipPath></defs></svg>`,
};

/* 🤖 this file was generated by svg-to-ts */
const visa = {
    name: 'visa',
    data: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3296_12)"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.6941 7.99618C16.2906 7.98921 16.8824 8.1027 17.434 8.32985L17.1718 10.0101L16.993 9.91475C16.5372 9.70679 16.0401 9.60493 15.5392 9.61684C14.7885 9.61684 14.431 9.96242 14.431 10.2842C14.431 10.6059 14.8481 10.8919 15.5154 11.2494C16.6356 11.7976 17.1599 12.4411 17.148 13.3467C17.148 14.9674 15.8133 16.0041 13.7756 16.0041C13.0317 16.0023 12.2953 15.8566 11.6068 15.5751L11.8808 13.8353L12.1311 13.9664C12.6957 14.251 13.3223 14.3902 13.9543 14.3716C14.5144 14.3716 15.146 14.1332 15.146 13.6089C15.146 13.2752 14.8957 13.0369 14.1331 12.6556C13.3704 12.2742 12.429 11.6546 12.4409 10.5463C12.4171 9.04484 13.7875 7.99618 15.6941 7.99618ZM11.6306 8.16302L10.4389 15.9088H8.47269L9.66435 8.16302H11.6306ZM22.427 8.16302L24 15.9088H22.2006C22.2006 15.9088 22.0218 15.0151 21.9623 14.7171H19.4717L19.0665 15.9088H17.0288L19.9126 8.75885C19.9802 8.5505 20.1184 8.37225 20.3032 8.25479C20.4881 8.13733 20.7082 8.08802 20.9255 8.11535H22.427V8.16302ZM3.15789 8.16302C3.35863 8.14112 3.56031 8.19496 3.72343 8.31399C3.88654 8.43302 3.99934 8.60866 4.03972 8.80651L4.69513 12.2862V12.3696L4.89772 13.4421L6.79245 8.16302H8.85402L5.79146 15.8969H3.77756L2.04965 9.17592C1.40984 8.79565 0.719919 8.50685 0 8.31793V8.16302H3.15789ZM21.0328 10.2603L21.1639 10.9277C21.1639 10.9277 21.5333 12.7747 21.6167 13.168H20.0556C20.2105 12.7152 20.8183 10.9992 20.8183 10.9992C20.8183 10.9992 20.9374 10.5463 21.0804 10.2603H21.0328Z" fill="#1434CB"/></g><defs><clipPath id="clip0_3296_12"><rect y="7.99588" width="24" height="8.00825" rx="1" fill="white"/></clipPath></defs></svg>`,
};

/* 🤖 this file was generated by svg-to-ts */

var PaymentLogos = /*#__PURE__*/Object.freeze({
    __proto__: null,
    americanExpress: americanExpress,
    bank: bank,
    bankDark: bankDark,
    discover: discover,
    genericCard: genericCard,
    genericCardDark: genericCardDark,
    genericCardDisabled: genericCardDisabled,
    genericCardDisabledDark: genericCardDisabledDark,
    mastercard: mastercard,
    visa: visa,
    visaDark: visaDark
});

const KitePaymentLogo = forwardRef((_a, ref) => {
    var { logo, ariaHidden, ariaLabel = undefined, className, id } = _a, other = __rest(_a, ["logo", "ariaHidden", "ariaLabel", "className", "id"]);
    const [logoInfo, setLogoInfo] = useState(null);
    const [darkModeLogoInfo, setDarkModeLogoInfo] = useState(null);
    // If the user passes in a value from RkPaymentLogos, map the value to the associated png and display that png
    const paymentLogosMap = {
        visa: {
            src: 'visa',
            ariaLabel: 'visa icon',
        },
        visa_dark: {
            src: 'visaDark',
            ariaLabel: 'visa icon',
        },
        discover: {
            src: 'discover',
            ariaLabel: 'discover icon',
        },
        discover_dark: {
            src: 'discover',
            ariaLabel: 'discover icon',
        },
        american_express: {
            src: 'americanExpress',
            ariaLabel: 'american express icon',
        },
        american_express_dark: {
            src: 'americanExpress',
            ariaLabel: 'american express icon',
        },
        master_card: {
            src: 'mastercard',
            ariaLabel: 'master card icon',
        },
        master_card_dark: {
            src: 'mastercard',
            ariaLabel: 'master card icon',
        },
        generic: {
            src: 'genericCard',
            ariaLabel: 'generic credit card icon',
        },
        generic_dark: {
            src: 'genericCardDark',
            ariaLabel: 'generic credit card icon',
        },
        bank: {
            src: 'bank',
            ariaLabel: 'bank icon',
        },
        bank_dark: {
            src: 'bankDark',
            ariaLabel: 'bank icon',
        },
        generic_disabled: {
            src: 'genericCardDisabled',
            ariaLabel: 'generic credit card disabled icon',
        },
        generic_disabled_dark: {
            src: 'genericCardDisabledDark',
            ariaLabel: 'generic credit card disabled icon',
        },
    };
    useEffect(() => {
        let tempLogoInfo;
        if (Object.keys(paymentLogosMap).indexOf(`${logo}_dark`) !== -1) {
            tempLogoInfo = PaymentLogos[paymentLogosMap[`${logo}_dark`].src].data;
            tempLogoInfo = tempLogoInfo.replace('<svg', `<svg aria-hidden=${true}`);
            setDarkModeLogoInfo(tempLogoInfo);
        }
        if (Object.keys(paymentLogosMap).indexOf(logo) !== -1) {
            tempLogoInfo = PaymentLogos[paymentLogosMap[logo].src].data;
            // Apply aria attributes to the svg tag
            tempLogoInfo = tempLogoInfo.replace('<svg', `<svg aria-label=${ariaLabel !== null && ariaLabel !== void 0 ? ariaLabel : paymentLogosMap[logo].ariaLabel} aria-hidden=${!!ariaHidden}`);
            setLogoInfo(tempLogoInfo);
        }
    }, []);
    return (logoInfo && (React.createElement("div", Object.assign({ ref: ref, id: id, className: classNames('kite-payment-logo', `rk-payment-logo--${logo}`, {
            [className]: className,
        }) }, other),
        React.createElement("div", { className: "kite-payment-logo__inner" },
            React.createElement("div", { className: "kite-light-mode-payment-logo", dangerouslySetInnerHTML: {
                    __html: logoInfo,
                } }),
            React.createElement("div", { className: "kite-dark-mode-payment-logo", "aria-hidden": true, dangerouslySetInnerHTML: {
                    __html: darkModeLogoInfo,
                } })))));
});

const KiteTitle = forwardRef((_a, ref) => {
    var { id, children, bodyTitle } = _a, other = __rest(_a, ["id", "children", "bodyTitle"]);
    return (React.createElement("span", Object.assign({ ref: ref, id: id, className: classNames('kite-title', {
            'kite-title--body': bodyTitle,
        }) }, other), children));
});

function getViewportSize$1() {
    var _a, _b;
    const width = (_a = window === null || window === void 0 ? void 0 : window.innerWidth) !== null && _a !== void 0 ? _a : null;
    const height = (_b = window === null || window === void 0 ? void 0 : window.innerHeight) !== null && _b !== void 0 ? _b : null;
    return {
        width,
        height,
    };
}
const isSmallBreakPoint = () => {
    const menuBreak = 600;
    const { width } = getViewportSize$1();
    return width < menuBreak;
};

// A `Position` is how a popover is placed within a container.  `top`, `bottom`, `left`, or `right`
// An `Alignment` is how the popover container is aligned with respect to it's pointing carat.  `start` or `end`
// A `Placement` is a combination of a `Position` and an `Alignment`
// A `Strategy` is a configuration defined for AttachedOverlay, that defines a single candidate placement for a popover
// Lookup Map - maps a `Position` and an `Alignment` to a predefined `Strategy`.
const mapPlacementToStrategy = {
    top: {
        start: new OriginRelative([0, 0], [0, 1], [-12, -8], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint(), [
            'kite-popover__content',
            'kite-popover__content--expanded',
            'kite-popover__content--align-start',
            'kite-popover__content--top',
        ]),
        center: new OriginRelative([0, 0], [0, 1], [0, -8], (o, c, v) => [v[0], c[1]], (x) => isSmallBreakPoint(), [
            'kite-popover__content',
            'kite-popover__content--expanded',
            'kite-popover__content--align-center',
            'kite-popover__content--top',
        ]),
        end: new OriginRelative([1, 0], [1, 1], [14, -8], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint(), [
            'kite-popover__content',
            'kite-popover__content--expanded',
            'kite-popover__content--align-end',
            'kite-popover__content--top',
        ]),
    },
    bottom: {
        start: new OriginRelative([0, 1], [0, 0], [-12, 8], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint(), [
            'kite-popover__content',
            'kite-popover__content--expanded',
            'kite-popover__content--align-start',
            'kite-popover__content--bottom',
        ]),
        center: new OriginRelative([0, 1], [0, 0], [0, 8], (o, c, v) => [v[0], c[1]], (x) => isSmallBreakPoint(), [
            'kite-popover__content',
            'kite-popover__content--expanded',
            'kite-popover__content--align-center',
            'kite-popover__content--bottom',
        ]),
        end: new OriginRelative([1, 1], [1, 0], [14, 8], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint(), [
            'kite-popover__content',
            'kite-popover__content--expanded',
            'kite-popover__content--align-end',
            'kite-popover__content--bottom',
        ]),
    },
    left: {
        start: new OriginRelative([0, 0], [1, 0], [-7, -12], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint(), [
            'kite-popover__content',
            'kite-popover__content--expanded',
            'kite-popover__content--align-start',
            'kite-popover__content--left',
        ]),
        end: new OriginRelative([0, 1], [1, 1], [-7, 12], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint(), [
            'kite-popover__content',
            'kite-popover__content--expanded',
            'kite-popover__content--align-end',
            'kite-popover__content--left',
        ]),
    },
    right: {
        start: new OriginRelative([1, 0], [0, 0], [10, -12], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint(), [
            'kite-popover__content',
            'kite-popover__content--expanded',
            'kite-popover__content--align-start',
            'kite-popover__content--right',
        ]),
        end: new OriginRelative([1, 1], [0, 1], [10, 12], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint(), [
            'kite-popover__content',
            'kite-popover__content--expanded',
            'kite-popover__content--align-end',
            'kite-popover__content--right',
        ]),
    },
};
/**
 * @description `getStrategies` accepts an array of `Position` values and an array of `Alignment` values. It takes these
 * values and returns an array of each strategy that matches each combination of `Position` and `Alignment`
 */
const getStrategies = (positions, alignments) => {
    // Resolve combinations of elements in `positions` and `alignments` arrays into single `Strategies` array
    const mainStrategies = positions.reduce((accStrategies, position) => accStrategies.concat(alignments.map((alignment) => mapPlacementToStrategy[position][alignment])), []);
    // Default center-aligned strategies for mobile breakpoints.
    const mobileStrategies = [].concat(!!arrayIntersect(positions, ['top', 'left', 'right']).length
        ? mapPlacementToStrategy.top.center
        : [], !!arrayIntersect(positions, ['bottom']).length
        ? mapPlacementToStrategy.bottom.center
        : []);
    // Return calculated strategies plus default mobile strategies
    return mainStrategies.concat(mobileStrategies);
};
const allPositions = ['top', 'left', 'right', 'bottom'];
const allAlignments = ['start', 'end'];
const context$1 = ['web', 'closed', 0];
const usePopoverOverlay = (positions = allPositions, alignments = allAlignments) => {
    const strategies = getStrategies(positions, alignments);
    return { context: context$1, strategies };
};

const KitePopoverName = Symbol('KitePopover');
const ESCAPE$2 = 'Escape';
/**
 * An question-mark icon that when clicked will open a small floating-yet-connected info-bubble, containing any content that is passed in as children.
 * The position of the popover relative to the icon is calculated on each hover event, and depends on the position of the popover in the window, as outlined in the guidelines.
 * [Popover design guidelines](https://beam.kiteds.com/web/components/popover/design.html)
 */
function KitePopover(_a) {
    var _b;
    var { ariaLabel, ariaLabelledBy, assistiveText, children, className, id, linkLabel, position: positionRaw, alignment: alignmentRaw, href, onlyTop = false, target, title, disabled, __typeName = KitePopoverName } = _a, other = __rest(_a, ["ariaLabel", "ariaLabelledBy", "assistiveText", "children", "className", "id", "linkLabel", "position", "alignment", "href", "onlyTop", "target", "title", "disabled", "__typeName"]);
    const [active, setActive] = useState(false);
    if (onlyTop) {
        console.warn(`Value 'onlyTop' has been deprecated. Use \`position = 'top'\` and \`alignment = 'start'\` instead.`);
    }
    // potentially resolve deprecated behavior
    const onlyTopResolved = onlyTop && !!!positionRaw && !!!alignmentRaw;
    const position = onlyTopResolved ? 'top' : positionRaw;
    const alignment = onlyTopResolved ? 'start' : alignmentRaw;
    // Normalize singular/plural hybrid value to plural, and dedupe
    const positionsRaw = !!position ? arrayDedupe([].concat(position)) : [];
    const alignmentsRaw = !!alignment ? arrayDedupe([].concat(alignment)) : [];
    // treat empty `positions` and `alignments` arrays as undefined.  Undefined resolves to all values.
    const positions = !!positionsRaw.length ? positionsRaw : undefined;
    const alignments = !!alignmentsRaw.length ? alignmentsRaw : undefined;
    const { strategies, context } = usePopoverOverlay(positions, alignments);
    let leftOffset;
    const buttonClasses = classNames('kite-popover__trigger', {
        'kite-active': active,
    });
    const normalId = useNormalId();
    const popoverId = !!id ? id : `popover-${normalId}`;
    const popoverContentId = `${popoverId}-content`;
    const classes = classNames('kite-popover', 'kite-popover--inline', className, popoverId, disabled ? 'kite-popover--disabled' : '');
    const buttonRef = useRef(null);
    const containerRef = useRef(null);
    const popoverRef = useRef(null);
    const dismissRef = useRef(null);
    // set the popover arrow left offset for small breakpoints <= 600, where the popover width takes up
    // 100% of the viewport width
    if (((_b = buttonRef === null || buttonRef === void 0 ? void 0 : buttonRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) && isSmallBreakPoint()) {
        leftOffset = `${buttonRef.current.offsetLeft + 12}px`;
    }
    const handleBlur = (e) => {
        if (!!!closestOrSelf(e['relatedTarget'], '.kite-popover') ||
            !!!closestOrSelf(e['relatedTarget'], `.${popoverId}`)) {
            closePopover();
        }
    };
    const closePopover = () => {
        setActive(false);
    };
    const onKeyDown = (e) => {
        var _a;
        if (e.key === ESCAPE$2) {
            (_a = buttonRef === null || buttonRef === void 0 ? void 0 : buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            closePopover();
        }
    };
    const linkProps = {
        target,
    };
    /**
     * Wrap children that are string in <p> tag to get <p> css props.
     */
    const wrappedStrings = React.Children.map(children, (child) => {
        if (child instanceof Object) {
            return child;
        }
        else {
            return React.createElement("p", { className: "kite-popover__content-text" }, child);
        }
    });
    useEffect(() => {
        window.addEventListener('resize', closePopover);
        window.addEventListener('scroll', closePopover, { capture: true });
        return () => {
            window.removeEventListener('resize', closePopover);
            window.removeEventListener('scroll', closePopover, { capture: true });
        };
    }, []);
    return (React.createElement("span", { className: classes, ref: containerRef, id: popoverId },
        React.createElement("span", { className: "kite-popover__group", style: { '--arrow-offset': leftOffset } },
            React.createElement("button", Object.assign({ className: buttonClasses, "aria-expanded": active, ref: buttonRef, type: "button", onClick: (e) => {
                    !disabled && setActive(!active);
                }, onKeyDown: onKeyDown, onBlur: handleBlur, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, "aria-disabled": disabled }, other),
                React.createElement("span", { className: "kite-icon" },
                    React.createElement("span", { className: "kite-icon__inner" },
                        React.createElement(KiteSvgIcon, { className: "kite-popover__icon-default", icon: "ki-question-circle", ariaHidden: true }),
                        React.createElement(KiteSvgIcon, { className: "kite-popover__icon-emphasized", icon: "ki-question-circle-f", ariaHidden: true })))),
            !disabled && active && (buttonRef === null || buttonRef === void 0 ? void 0 : buttonRef.current) && (React.createElement(AttachedOverlay, { origin: buttonRef === null || buttonRef === void 0 ? void 0 : buttonRef.current, strategies: strategies, context: context },
                React.createElement("span", { 
                    // TODO: Enable prop passthrough on AttachedOverlay so these props can be added to it.  Prevents layout context breaking.
                    className: "rk-popover__content--interior", id: popoverContentId, ref: popoverRef, onKeyDown: onKeyDown, onBlur: handleBlur, tabIndex: -1 },
                    React.createElement("button", { type: "button", ref: dismissRef, className: "kite-popover__dismiss-button", onClick: (e) => setActive(false) },
                        React.createElement(KiteSvgIcon, { icon: 'ki-x' }),
                        React.createElement("span", { className: "kite-assistive-text" }, "Close Popover")),
                    !!title && (React.createElement("h6", { className: "kite-popover__content-title", id: `kite-popover__title-${normalId}` }, title)),
                    wrappedStrings,
                    !!href && (React.createElement("a", Object.assign({ href: href }, linkProps, { className: "kite-link kite-link--standalone", type: "button" }),
                        !!linkLabel ? linkLabel : React.createElement(React.Fragment, null, "Learn more"),
                        React.createElement("span", { className: "kite-link__icon" },
                            React.createElement("span", { className: "kite-icon kite-icon--inline" },
                                React.createElement("span", { className: "kite-icon__inner" },
                                    React.createElement(KiteSvgIcon, { icon: "ki-chevron-right" }))))))))))));
}
KitePopover.displayName = 'KitePopover';

const KiteInteractiveTitle = forwardRef((_a, ref) => {
    var { id, children } = _a, other = __rest(_a, ["id", "children"]);
    let titleText = '';
    /**
     * Add a11y attributes to KitePopover
     */
    const applyA11yLogic = () => {
        return React.Children.map(children, (child) => {
            if (typeof child !== 'string' && child['type'] === KitePopover) {
                return React.cloneElement(child, Object.assign(Object.assign({}, child.props), { ariaLabel: titleText + ' more info' }));
            }
            else if (typeof child === 'string') {
                titleText = child;
                return child;
            }
            else {
                return child;
            }
        });
    };
    return (React.createElement("span", Object.assign({ ref: ref, id: id, className: "kite-title kite-title-interactive" }, other), applyA11yLogic()));
});

const KiteSubtitle = forwardRef((_a, ref) => {
    var { id, children } = _a, other = __rest(_a, ["id", "children"]);
    return (React.createElement("span", Object.assign({ id: id, ref: ref, className: "kite-subtitle" }, other), children));
});

const labelTypeClasses$1 = {
    active: 'active',
    confirmation: 'positive',
    caution: 'caution',
    error: 'negative',
    info: 'neutral',
    success: 'positive',
    positive: 'positive',
    negative: 'negative',
    neutral: 'neutral',
    muted: 'muted',
};
const mapStatusToDeprecationMessage = {
    confirmation: getDeprecationMessage('status', 'confirmation', 'positive'),
    success: getDeprecationMessage('status', 'success', 'positive'),
    error: getDeprecationMessage('status', 'error', 'negative'),
    info: getDeprecationMessage('status', 'info', 'neutral'),
};
const mapStatusToStatusIcon = {
    active: 'ki-checkmark',
    caution: 'ki-caution-alert-f',
    positive: 'ki-checkmark-circle-f',
    negative: 'ki-caution-circle-f',
    neutral: 'ki-checkmark',
    muted: 'ki-checkmark',
};
const KiteIndicatorIcon = ({ icon, ariaLabel, ariaHidden, }) => React.createElement(KiteIcon, { icon, ariaLabel, ariaHidden });
const maybeGetStatusIcon = (status, iconBoolOrString, statusAria = {}) => {
    if (!iconBoolOrString)
        return undefined;
    const { ariaLabel, ariaHidden = true } = statusAria;
    const iconProps = {
        ariaHidden,
        ariaLabel,
        icon: typeof iconBoolOrString === 'boolean' && iconBoolOrString
            ? mapStatusToStatusIcon[labelTypeClasses$1[status]]
            : iconBoolOrString,
    };
    return React.createElement(KiteIndicatorIcon, Object.assign({}, iconProps));
};
const maybeGetVariantIcon = (variant, linkIcon) => {
    // Submap of `mapVariantToVariantIcon`.  Determines `variantIcon` icon when `variant` is "link"
    const mapLinkIconToIcon = {
        internal: 'ki-chevron-right',
        linkout: 'ki-linkout',
    };
    const mapVariantToVariantIcon = {
        // `link` value varies, based on value of `linkIcon` prop
        link: mapLinkIconToIcon[linkIcon] || linkIcon,
        expandable: 'ki-chevron-down',
    };
    if (!Object.keys(mapVariantToVariantIcon).includes(variant))
        return undefined;
    const variantIconProps = {
        icon: mapVariantToVariantIcon[variant],
        ariaHidden: true,
    };
    return React.createElement(KiteIndicatorIcon, Object.assign({}, variantIconProps));
};
const KiteIndicator = forwardRef((_a, ref) => {
    var { indicatorAsContent, icon, iconAriaProps, linkIcon = 'internal', status = 'active', linkIndicator, children, className: incomingClassName, variant: variantRaw } = _a, other = __rest(_a, ["indicatorAsContent", "icon", "iconAriaProps", "linkIcon", "status", "linkIndicator", "children", "className", "variant"]);
    Object.keys(mapStatusToDeprecationMessage).includes(status) &&
        console.warn(mapStatusToDeprecationMessage[status]);
    const innerRef = useRef(null);
    useImperativeHandle(ref, () => innerRef.current);
    const maybeStatusIcon = maybeGetStatusIcon(status, icon, iconAriaProps);
    // Variant primarily set through the `variant` prop (aliased to `variantRaw`), with a fallback to deprecated boolean linkIndicator prop
    const variant = variantRaw !== null && variantRaw !== void 0 ? variantRaw : (linkIndicator ? 'link' : undefined);
    const maybeVariantIcon = maybeGetVariantIcon(variant, linkIcon);
    return (React.createElement(React.Fragment, null,
        React.createElement("span", Object.assign({ ref: innerRef, className: classNames('kite-list-item__label', incomingClassName, {
                [`kite-list-item__label--${labelTypeClasses$1[status]}`]: labelTypeClasses$1[status],
            }) }, other),
            maybeStatusIcon,
            children),
        maybeVariantIcon));
});

/**
 * View the design specifications for [interactive list](https://beam.kiteds.com/web/components/interactive-list/design.html)
 */
const KiteInteractiveList = forwardRef((_a, ref) => {
    var { card, children, className, variant = 'link' } = _a, other = __rest(_a, ["card", "children", "className", "variant"]);
    const interactiveList = (React.createElement("ul", Object.assign({ ref: ref, className: classNames({
            'kite-list': true,
            [className]: className,
        }) }, other), React.Children.map(children, (child, index) => {
        return cloneElement(child, {
            variant,
        });
    })));
    if (card) {
        return (React.createElement(KiteCard, { className: "kite-card--reset-height kite-card--reset-padding" }, interactiveList));
    }
    return interactiveList;
});

const labelTypeClasses = {
    active: 'active',
    confirmation: 'positive',
    error: 'negative',
    info: 'neutral',
    success: 'positive',
};
const labelStatusClasses = {
    negative: 'negative',
    neutral: 'neutral',
    positive: 'positive',
};
/**
 * View the design specifications for [interactive list](https://beam.kiteds.com/web/components/interactive-list/design.html)
 */
const KiteInteractiveListItem = forwardRef((_a, ref) => {
    var { disabled, href, label, labelType, status, onClick, subtitle, title, titleIcon, variant = 'link' } = _a, other = __rest(_a, ["disabled", "href", "label", "labelType", "status", "onClick", "subtitle", "title", "titleIcon", "variant"]);
    if (!!labelType) {
        if (labelType === 'confirmation' || labelType === 'success') {
            console.warn('Property type has been deprecated. Use status=positive');
        }
        else if (labelType === 'info') {
            console.warn('Property type has been deprecated. Use status=neutral');
        }
        else if (labelType === 'error') {
            console.warn('Property type has been deprecated. Use status=negative');
        }
    }
    const ListItemContentRender = (props) => {
        return (React.createElement("span", { className: "kite-list-item__inner", role: "presentation" },
            React.createElement("div", { className: "kite-list-item__start" }, titleIcon && React.createElement(KiteIcon, { icon: titleIcon, size: "16px" })),
            React.createElement("div", { className: "kite-list-item__center" },
                React.createElement("span", { className: "kite-title" }, title),
                subtitle && React.createElement("span", { className: "kite-subtitle" }, subtitle)),
            React.createElement("div", { className: "kite-list-item__end" },
                label && (React.createElement("span", { className: classNames({
                        'kite-list-item__label': true,
                        [`kite-list-item__label--${labelTypeClasses[labelType]}`]: labelType,
                        [`kite-list-item__label--${labelStatusClasses[status]}`]: status,
                    }) }, label)),
                React.createElement(KiteIcon, { icon: "ki-chevron-right", ariaHidden: true }))));
    };
    return (React.createElement("li", Object.assign({ ref: ref, className: classNames({
            'kite-list-item': true,
            'kite-list-item--multiline': subtitle,
            'kite-list-item--disabled': disabled,
            [`kite-list-item--${labelTypeClasses[labelType]}`]: labelType,
            [`kite-list-item--${labelStatusClasses[status]}`]: status,
        }) }, other), variant === 'link' ? (React.createElement("a", { href: !disabled ? href : null, "aria-disabled": disabled, className: "kite-list-item__action" },
        React.createElement(ListItemContentRender, null))) : (React.createElement("button", { onClick: onClick, disabled: disabled, className: "kite-list-item__action" },
        React.createElement(ListItemContentRender, null)))));
});

/**
 * if element is child of a KiteCard, apply classNames needed to reset padding
 * @param ref
 */
const resetCardPadding = (ref) => {
    var _a, _b;
    if (typeof ref === 'object') {
        const insideCard = (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.parentElement.classList.contains('kite-card');
        if (insideCard) {
            (_b = ref === null || ref === void 0 ? void 0 : ref.current) === null || _b === void 0 ? void 0 : _b.parentElement.classList.add('kite-card--reset-height', 'kite-card--reset-padding');
        }
    }
};
const boldSubstring = (text, shouldBeBold, uid) => {
    if (text) {
        const textArray = [];
        const trimmedShouldBeBold = shouldBeBold.trim().toLowerCase();
        let currentPosition = text
            .toLowerCase()
            .search(escapeRegExp(trimmedShouldBeBold));
        while (currentPosition !== -1 && trimmedShouldBeBold.length > 0) {
            if (currentPosition === 0) {
                textArray.push({
                    bold: text.substring(0, currentPosition + trimmedShouldBeBold.length),
                });
                text = text.slice(currentPosition + trimmedShouldBeBold.length);
            }
            else {
                textArray.push({ text: text.substring(0, currentPosition) });
                text = text.slice(currentPosition);
            }
            currentPosition = text
                .toLowerCase()
                .search(escapeRegExp(trimmedShouldBeBold));
        }
        textArray.push({ text: text });
        return (React.createElement("span", null, textArray.map((item, index) => (React.createElement(React.Fragment, { key: uid + '-content-bold-' + index }, item['bold'] ? React.createElement("b", null, item['bold']) : item['text'])))));
    }
    else {
        return text;
    }
};

/**
 * View the design specifications for [interactive list](https://beam.kiteds.com/web/components/list/#overview)
 */
const KiteList = forwardRef((_a, ref) => {
    var { alignItems, children, className } = _a, other = __rest(_a, ["alignItems", "children", "className"]);
    const innerRef = useRef(null);
    useImperativeHandle(ref, () => innerRef.current);
    useEffect(() => {
        // If in a card, apply specific css to reset padding
        resetCardPadding(innerRef);
    }, [innerRef]);
    return (React.createElement("ul", Object.assign({ ref: innerRef, className: classNames({
            'kite-list': true,
            'kite-list--align-items': alignItems,
            [className]: className,
        }) }, other), children));
});

const KiteListItemContent = (_a) => {
    var { children, className: incomingClassName } = _a, other = __rest(_a, ["children", "className"]);
    const kiteListItemContentProps = {
        className: incomingClassName,
    };
    return (React.createElement("div", Object.assign({}, kiteListItemContentProps, other), children));
};

const processChildren = (children) => {
    const childrenArray = React.Children.toArray(children);
    const [contentChild, otherChildren] = childrenArray.reduce(([foundChild, processedChildren], incomingChild) => incomingChild.type === KiteListItemContent
        ? // Content Child found! If `foundChild` is null, set it to `incomingChild`. Leave `processedChildren` untouched.
            [foundChild !== null && foundChild !== void 0 ? foundChild : incomingChild, processedChildren]
        : // No Content Child found. Leave `foundChild` untouched. Add `incomingChild` to `processedChildren` array.
            [foundChild, [...processedChildren, incomingChild]], [null, []]);
    return [contentChild, otherChildren];
};

/**
 * View the design specifications for [list](https://beam.kiteds.com/web/components/list/#overview)
 */
const KiteListItemEnd = forwardRef((_a, ref) => {
    var { children, className: incomingClassName } = _a, other = __rest(_a, ["children", "className"]);
    return (React.createElement("div", Object.assign({ ref: ref, className: classNames('kite-list-item__end kite-list-item__end-centered', incomingClassName) }, other), children));
});

/**
 * View the design specifications for [list](https://beam.kiteds.com/web/components/list/#overview)
 */
const KiteListItemCenter = forwardRef((_a, ref) => {
    var { children, className: incomingClassName } = _a, other = __rest(_a, ["children", "className"]);
    return (React.createElement("div", Object.assign({ ref: ref, className: classNames('kite-list-item__center', incomingClassName) }, other), children));
});

class MenuVerticalOffset extends OverlayStrategy {
    activate(state) {
        this.active = true;
    }
    deactivate(state) {
        this.active = false;
    }
    constructor(originAnchor, overlayAnchor, offset, predicate) {
        super();
        this.originAnchor = originAnchor;
        this.overlayAnchor = overlayAnchor;
        this.offset = offset;
        this.predicate = predicate;
        this.active = false;
    }
    get reactTo() {
        return ['originPosition', 'originSize', 'viewportSize', 'contentSize'];
    }
    propose(state) {
        if (this.predicate(state)) {
            const initialMaxWidth = 336;
            let calculatedWidth = initialMaxWidth;
            let x;
            if (state.originPosition[0] < state.viewportSize[0] / 2) {
                if (initialMaxWidth >=
                    state.viewportSize[0] - state.originSize[0] - 8) {
                    calculatedWidth = Math.min(state.viewportSize[0] - state.originPosition[0], initialMaxWidth);
                }
                x = state.originPosition[0];
            }
            else {
                if (state.originPosition[0] + state.originSize[0] - initialMaxWidth <
                    state.viewportSize[0]) {
                    calculatedWidth = Math.min(state.originPosition[0] + state.originSize[0], initialMaxWidth);
                }
                x =
                    state.originPosition[0] - state.contentSize[0] + state.originSize[0];
            }
            const y = (lerp(state.originPosition[1], state.originPosition[1] + state.originSize[1], this.originAnchor[1]) -
                lerp(0, state.contentSize[1], this.overlayAnchor[1])) |
                0;
            return new OverlayProposal([x + this.offset[0], y + this.offset[1]], [calculatedWidth, state.contentSize[1]], false, undefined, undefined);
        }
        else {
            return false;
        }
    }
}

const context = ['web', 'closed', 0];
const mapPositionToStrategy = {
    top: {
        start: new OriginRelative([0, 0], [0, 1], [0, -4], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint()),
        end: new OriginRelative([1, 0], [1, 1], [0, -4], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint()),
    },
    bottom: {
        start: new OriginRelative([0, 1], [0, 0], [0, 4], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint()),
        end: new OriginRelative([1, 1], [1, 0], [0, 4], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint()),
    },
    left: {
        start: new OriginRelative([0, 0], [1, 0], [-4, 0], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint()),
        end: new OriginRelative([0, 1], [1, 1], [-4, 0], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint()),
    },
    right: {
        start: new OriginRelative([1, 0], [0, 0], [4, 0], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint()),
        end: new OriginRelative([1, 1], [0, 1], [4, 0], (o, c) => [c[0], c[1]], (x) => !isSmallBreakPoint()),
    },
};
function getViewportSize() {
    var _a, _b;
    const width = (_a = window === null || window === void 0 ? void 0 : window.innerWidth) !== null && _a !== void 0 ? _a : null;
    const height = (_b = window === null || window === void 0 ? void 0 : window.innerHeight) !== null && _b !== void 0 ? _b : null;
    return {
        width,
        height,
    };
}
function useMenuOverlay(isLabelVariant, directions, alignments, overlayPositionStates) {
    let strategies = [];
    const menuBreak = 600;
    const isSmallBreakPoint = () => {
        const { width } = getViewportSize();
        return width <= menuBreak;
    };
    if (!!overlayPositionStates) {
        strategies = getDeprecatedPositionStrategies(overlayPositionStates, isLabelVariant);
    }
    else {
        directions.forEach((dir) => {
            alignments.forEach((ali) => {
                strategies.push(mapPositionToStrategy[dir][ali]);
            });
        });
    }
    // mobile breakpoint positioning
    strategies.push(new MenuVerticalOffset([0, 1], [0, 0], [0, 4], (x) => isSmallBreakPoint()));
    strategies.push(new MenuVerticalOffset([0, 0], [0, 1], [0, -4], (x) => isSmallBreakPoint()));
    return { context, strategies };
}
const getDeprecatedPositionStrategies = (overlayPositionStates, isLabelVariant) => {
    const strategies = [];
    if ((overlayPositionStates === null || overlayPositionStates === void 0 ? void 0 : overlayPositionStates.indexOf('bottom-left')) !== -1 && !isLabelVariant) {
        strategies.push(mapPositionToStrategy['left']['start']);
    }
    if ((overlayPositionStates === null || overlayPositionStates === void 0 ? void 0 : overlayPositionStates.indexOf('bottom-right')) !== -1 &&
        !isLabelVariant) {
        strategies.push(mapPositionToStrategy['right']['start']);
    }
    if ((overlayPositionStates === null || overlayPositionStates === void 0 ? void 0 : overlayPositionStates.indexOf('top-left')) !== -1 && !isLabelVariant) {
        strategies.push(mapPositionToStrategy['left']['end']);
    }
    if ((overlayPositionStates === null || overlayPositionStates === void 0 ? void 0 : overlayPositionStates.indexOf('top-right')) !== -1 && !isLabelVariant) {
        strategies.push(mapPositionToStrategy['right']['end']);
    }
    if ((overlayPositionStates === null || overlayPositionStates === void 0 ? void 0 : overlayPositionStates.indexOf('below-left')) !== -1 && isLabelVariant) {
        strategies.push(mapPositionToStrategy['bottom']['end']);
    }
    if ((overlayPositionStates === null || overlayPositionStates === void 0 ? void 0 : overlayPositionStates.indexOf('below-right')) !== -1 && isLabelVariant) {
        strategies.push(mapPositionToStrategy['bottom']['start']);
    }
    return strategies;
};

const ARROW_DOWN$1 = 'ArrowDown';
const ARROW_UP$1 = 'ArrowUp';
const CLICK$1 = 'click';
const ESCAPE$1 = 'Escape';
const ENTER$1 = 'Enter';
const SPACE$1 = ' ';
function KiteMenuBase(_a) {
    var { ariaLabel = '', ariaLabeledBy, ariaDescribedBy, buttonClasses, children, iconProps, inline = false, labelVariant = false, label, id, maxItemCount, menuIcon, onChange, onToggle, placeholder, position, positionClasses, primaryClasses, showLabel, showValue, triggerRef, openPosition, alignment = ['end', 'start'], direction = labelVariant ? ['bottom'] : ['left', 'right', 'bottom', 'top'], value } = _a, other = __rest(_a, ["ariaLabel", "ariaLabeledBy", "ariaDescribedBy", "buttonClasses", "children", "iconProps", "inline", "labelVariant", "label", "id", "maxItemCount", "menuIcon", "onChange", "onToggle", "placeholder", "position", "positionClasses", "primaryClasses", "showLabel", "showValue", "triggerRef", "openPosition", "alignment", "direction", "value"]);
    const listRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [valueInitialized, setValueInitialized] = useState(false);
    const [internalValue, setValue] = useState('');
    const [internalResolvedValue, setInternalResolvedValue] = useState('');
    // const [defaultAriaLabelledBy, setDefaultAriaLabelledBy] = useState<string>(!ariaLabel && ariaLabeledBy ? ariaLabeledBy : '');
    const menuRef = useRef(null);
    const menuId = useNormalId(id);
    const buttonId = `button-${menuId}`;
    const menuLabelId = `label-${menuId}`;
    const valueId = `value-${menuId}`;
    const positionClass = positionClasses[position];
    // Normalize singular/plural hybrid value to plural, and dedupe
    const directionsRaw = !!direction ? arrayDedupe([].concat(direction)) : null;
    const alignmentsRaw = !!alignment ? arrayDedupe([].concat(alignment)) : null;
    const { strategies, context } = useMenuOverlay(labelVariant, directionsRaw, alignmentsRaw, openPosition);
    // maps the deprecated RkMenuOverlayStates to the new direction and alignment options
    const deprecationMap = {
        'bottom-left': {
            direction: 'left',
            alignment: 'start',
        },
        'top-left': {
            direction: 'left',
            alignment: 'end',
        },
        'bottom-right': {
            direction: 'right',
            alignment: 'start',
        },
        'top-right': {
            direction: 'right',
            alignment: 'end',
        },
        'below-left': {
            direction: 'bottom',
            alignment: 'end',
        },
        'below-right': {
            direction: 'bottom',
            alignment: 'start',
        },
    };
    if (!!openPosition) {
        const openPositions = arrayDedupe([].concat(openPosition));
        openPositions.forEach((openPos) => {
            if (deprecationMap[openPos]) {
                console.warn(`Property openPosition has been deprecated. Use alignment and direction instead. Example: alignment=${deprecationMap[openPos]['alignment']} direction=${deprecationMap[openPos]['direction']}`);
            }
        });
    }
    // build a map to resolve labels for values
    const valueToLabel = [
        { label: undefined, value: undefined },
        ...React.Children.map(children, (child, index) => ({
            label: child.props.children,
            value: child.props.value,
        })),
    ].reduce((final, item) => {
        return Object.assign(Object.assign({}, final), { [item.value]: item.label });
    });
    const preventArrowScroll = (e) => {
        if (e.key === ARROW_DOWN$1 || e.key === ARROW_UP$1) {
            e.preventDefault();
        }
    };
    const handleClick = (e) => {
        if (e.type === CLICK$1) {
            toggle(e, internalValue);
        }
    };
    const handleButtonKeyUp = (e) => {
        if (e.key === ESCAPE$1) {
            setExpanded(false);
            return;
        }
        else if (e.key === SPACE$1) {
            if (!expanded) {
                toggle(e, internalValue);
            }
        }
        else if (e.key === ARROW_DOWN$1 && expanded) {
            listRef.current.firstChild.firstChild.focus();
        }
        else if (e.key === ARROW_DOWN$1) {
            if (!expanded) {
                toggle(e, internalValue);
            }
        }
    };
    const handleButtonKeyDown = (e) => {
        preventArrowScroll(e);
    };
    const handleKeyUp = (e, selectedValue) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        if (e.key === ESCAPE$1) {
            setExpanded(false);
            (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            return;
        }
        else if ((e.key === SPACE$1 || e.key === ENTER$1) &&
            !((_b = e === null || e === void 0 ? void 0 : e.currentTarget) === null || _b === void 0 ? void 0 : _b.disabled)) {
            e.preventDefault();
            toggle(e, selectedValue);
        }
        else if (e.key === ARROW_DOWN$1 || e.key === ARROW_UP$1) {
            const subsequentElement = e.key === ARROW_DOWN$1
                ? (_c = e.currentTarget) === null || _c === void 0 ? void 0 : _c.parentElement.nextElementSibling
                : (_d = e.currentTarget) === null || _d === void 0 ? void 0 : _d.parentElement.previousElementSibling;
            const subsequentChild = e.key === ARROW_DOWN$1
                ? (_f = (_e = e.target) === null || _e === void 0 ? void 0 : _e.closest('.kite-picker__list')) === null || _f === void 0 ? void 0 : _f.firstChild
                : (_h = (_g = e.target) === null || _g === void 0 ? void 0 : _g.closest('.kite-picker__list')) === null || _h === void 0 ? void 0 : _h.lastChild;
            let nextSibling = subsequentElement;
            if (!nextSibling) {
                nextSibling = subsequentChild;
            }
            (_j = nextSibling === null || nextSibling === void 0 ? void 0 : nextSibling.children[0]) === null || _j === void 0 ? void 0 : _j.focus();
        }
    };
    const toggle = (e = null, v = null) => {
        var _a;
        const next = !expanded;
        setExpanded(next);
        setValue(v);
        setInternalResolvedValue(valueToLabel[v]);
        if (!!onToggle) {
            onToggle(next);
        }
        (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const classes = classNames(primaryClasses, positionClass, {
        'kite-menu--open': expanded,
    });
    const options = React.Children.map(children, (child, index) => {
        const _handleClick = (e) => {
            if (!!child.props.onClick) {
                child.props.onClick(e);
            }
            if (child.props.onSelect) {
                child.props.onSelect(child.props.value);
            }
            toggle(undefined, child.props.value);
        };
        const _handleKeyUp = (e) => {
            if (!!child.props.onClick && (e.key === SPACE$1 || e.key === ENTER$1)) {
                child.props.onClick(e);
                toggle(undefined, child.props.value);
            }
            if (!!child.props.onSelect && (e.key === SPACE$1 || e.key === ENTER$1)) {
                e.preventDefault();
                child.props.onSelect(child.props.value);
            }
            handleKeyUp(e, child.props.value);
        };
        const _handleKeyDown = (e) => {
            if (e.key === SPACE$1) {
                e.preventDefault();
            }
            preventArrowScroll(e);
        };
        return React.cloneElement(child, {
            onClick: _handleClick,
            onKeyup: _handleKeyUp,
            onKeyDown: _handleKeyDown,
            className: child.props.className,
            selected: internalValue === child.props.value,
        });
    });
    /**
     * If the menu is in a data table, grab the id of the first <tr> element in the same row
     * and use that for aria-labelledby.
     */
    const ifInsideDataTable = () => {
        var _a;
        let tableEl = menuRef === null || menuRef === void 0 ? void 0 : menuRef.current.closest('tr');
        if (tableEl) {
            tableEl = (_a = tableEl === null || tableEl === void 0 ? void 0 : tableEl.firstChild) === null || _a === void 0 ? void 0 : _a.firstChild;
            return tableEl === null || tableEl === void 0 ? void 0 : tableEl.id;
        }
        else {
            return null;
        }
    };
    /**
     * Determine what aria-labelledby to use. If the user passes an ariaLabeledBy, use that prop value.
     * Otherwise if there is no ariaLabel, determine if the menu is inside a data table and
     * use the id of the first <td> in that same <tr>
     */
    const defaultAriaLabelledBy = useMemo(() => {
        if (!initialized) {
            return '';
        }
        if (ariaLabeledBy) {
            return ariaLabeledBy;
        }
        else if (!ariaLabel) {
            return ifInsideDataTable();
        }
        else {
            return '';
        }
    }, [initialized]);
    useEffect(() => {
        if (!initialized) {
            ReactDOM.unstable_batchedUpdates(() => {
                if (!!value) {
                    // consumer has passed an initial value
                    setValue(value);
                    setInternalResolvedValue(valueToLabel[value]);
                }
                setInitialized(true);
            });
        }
    }, [initialized]);
    useEffect(() => {
        setInternalResolvedValue(valueToLabel[value]);
        setValue(value);
    }, [value]);
    useEffect(() => {
        if (!!onChange && initialized) {
            if (valueInitialized) {
                onChange(internalValue);
            }
            else {
                setValueInitialized(true);
            }
        }
    }, [internalValue]);
    const handleBlur = (e) => {
        var _a, _b;
        const relatedTarget = e['relatedTarget'];
        if (!((_a = menuRef === null || menuRef === void 0 ? void 0 : menuRef.current) === null || _a === void 0 ? void 0 : _a.contains(relatedTarget)) &&
            !((_b = menuRef === null || menuRef === void 0 ? void 0 : menuRef.current) === null || _b === void 0 ? void 0 : _b.isSameNode(relatedTarget))) {
            setExpanded(false);
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("span", { ref: menuRef, className: classes },
            showLabel && showValue && !inline && (React.createElement("label", { id: menuLabelId, className: "kite-menu__label", htmlFor: buttonId }, label)),
            React.createElement("button", Object.assign({ className: buttonClasses, type: "button", id: buttonId, "aria-labelledby": defaultAriaLabelledBy, "aria-describedby": ariaDescribedBy, "aria-label": !label && ariaLabel ? ariaLabel : null }, other, { onClick: handleClick, onKeyUp: handleButtonKeyUp, onKeyDown: handleButtonKeyDown, onBlur: handleBlur, ref: triggerRef, "aria-expanded": expanded }),
                showLabel && showValue && inline && (React.createElement("label", { id: menuLabelId, className: "kite-menu__label", htmlFor: buttonId }, label)),
                showLabel && !showValue && (React.createElement("span", { className: classNames({
                        'kite-menu__value': !labelVariant,
                        'kite-menu__trigger-label': labelVariant,
                        'kite-menu__value--placeholder': false,
                    }), id: menuLabelId }, label)),
                !!internalValue && (React.createElement("span", { id: valueId, className: "kite-menu__value" }, internalResolvedValue)),
                React.createElement(KiteIcon, Object.assign({ icon: menuIcon }, iconProps))),
            expanded && (React.createElement(AttachedOverlay, { origin: expanded ? triggerRef.current : undefined, strategies: strategies, context: context, shouldReturnFocus: false },
                React.createElement("div", { onMouseDown: preventOnBlur, className: "kite-picker kite-picker--list kite-picker--open" },
                    React.createElement("div", { className: "kite-picker__content" },
                        React.createElement("ul", { className: "kite-picker__list", id: menuId, "aria-labelledby": buttonId, onBlur: handleBlur, ref: listRef, style: { '--max-item-count': maxItemCount } }, options))))))));
}

const positionClasses$1 = {
    'top-left': 'kite-menu--left kite-menu--top',
    'top-right': 'kite-menu--right kite-menu--top',
    'bottom-left': 'kite-menu--left kite-menu--bottom',
    'bottom-right': 'kite-menu--right kite-menu--bottom',
};
/** KiteMenu component */
function KiteMenu(_a) {
    var { ariaLabel, ariaLabelledBy, ariaDescribedBy, children, disabled, id, label, size, onToggle = () => undefined, position = 'top-right', menuIcon = 'ki-menu-dots', triggerRef, variant = 'contextual', maxItemCount = dropdownMaxItemCount, openPosition, alignment, direction } = _a, other = __rest(_a, ["ariaLabel", "ariaLabelledBy", "ariaDescribedBy", "children", "disabled", "id", "label", "size", "onToggle", "position", "menuIcon", "triggerRef", "variant", "maxItemCount", "openPosition", "alignment", "direction"]);
    const trigRef = useRef(null);
    const labelVariant = !!label;
    const normalId = useNormalId();
    const menuId = !!id ? id : `menu-${normalId}`;
    const classes = classNames('kite-menu kite-menu--contextual', {
        ['kite-menu--' + size]: !!size,
        'kite-menu--block': variant === 'flow' || (!labelVariant && !!!position),
        'kite-menu--contextual-label': labelVariant,
        'kite-menu--disabled': disabled,
    });
    return (React.createElement(KiteMenuBase, Object.assign({ label: label, id: menuId, menuIcon: labelVariant ? 'ki-chevron-down' : menuIcon, ariaLabel: ariaLabel, ariaLabeledBy: ariaLabelledBy, ariaDescribedBy: ariaDescribedBy, buttonClasses: 'kite-menu__trigger', disabled: disabled, labelVariant: labelVariant, maxItemCount: maxItemCount, onToggle: onToggle, position: position, positionClasses: positionClasses$1, primaryClasses: classes, showLabel: labelVariant, showValue: false, openPosition: openPosition, alignment: alignment, direction: direction, triggerRef: triggerRef ? triggerRef : trigRef }, other), children));
}

/**
 * [View the design specifications](https://beam.kiteds.com/web/components/switch/design.html)
 */
const KiteSwitch = forwardRef((_a, ref) => {
    var { ariaDescribedBy, ariaLabelledBy, className, disabled, label, onClick, selected } = _a, other = __rest(_a, ["ariaDescribedBy", "ariaLabelledBy", "className", "disabled", "label", "onClick", "selected"]);
    const key = `kite-switch-${useUID()}`;
    const innerRef = useRef(null);
    useImperativeHandle(ref, () => innerRef.current);
    const SwitchContentRender = () => {
        return (React.createElement("span", { className: "kite-switch__inner" },
            React.createElement("span", { className: "kite-switch__label" }, label),
            React.createElement("span", { className: "kite-switch__track" },
                React.createElement("span", { className: "kite-switch__thumb", "aria-hidden": "true" }))));
    };
    return (React.createElement("button", Object.assign({ className: classNames({
            'kite-switch': true,
            'kite-switch--on': selected,
            [className]: className,
        }), key: key, "aria-describedby": ariaDescribedBy, "aria-labelledby": ariaLabelledBy, "aria-pressed": selected, onClick: onClick, type: "button", ref: innerRef, disabled: disabled }, other),
        React.createElement(SwitchContentRender, null)));
});

/**
 * Returns an object that specifies special dynamic props that should be applied to specific items inside KiteListItem.
 * Various components inside of KiteListItem need specific props passed to them for a11y, and functionality purposes.
 * The props that get passed to the component are dependent on section of the List (start, center, end) and what component they are being applied
 * to (KiteSwitch, KiteIndicator, KiteMenu, KiteTitle, KiteButton)
 * @param selected
 * @param titleText
 * @param titleId
 * @param subtitleId
 * @param disabled
 * @param variant
 */
const getListDynamicProps = (selected, titleId, subtitleId, disabled, variant, linkIcon, titleText) => {
    return [
        {
            childType: KiteListItemEnd,
            nestedChildType: KiteSwitch,
            props: {
                ariaLabelledBy: titleId,
                ariaDescribedBy: subtitleId,
                disabled,
            },
        },
        {
            childType: KiteListItemEnd,
            nestedChildType: KiteIndicator,
            props: {
                variant,
                linkIcon,
            },
        },
        {
            childType: KiteListItemCenter,
            nestedChildType: KiteIndicator,
            props: {
                indicatorAsContent: true,
            },
        },
        {
            childType: KiteListItemEnd,
            nestedChildType: KiteButton,
            props: {
                ariaDescribedBy: titleId + ' ' + subtitleId,
                disabled,
            },
        },
        {
            childType: KiteListItemCenter,
            nestedChildType: KiteTitle,
            props: {
                id: titleId,
            },
        },
        {
            childType: KiteListItemCenter,
            nestedChildType: KiteSubtitle,
            props: {
                id: subtitleId,
            },
        },
        {
            childType: KiteListItemCenter,
            nestedChildType: KiteInteractiveTitle,
            props: {
                id: titleId,
            },
        },
        {
            childType: KiteListItemEnd,
            nestedChildType: KiteMenu,
            props: {
                disabled,
                ariaLabel: titleText + ', options',
                ariaDescribedBy: subtitleId,
            },
        },
    ];
};

const indicatorVariants = ['link', 'expandable'];
/**
 * - Separate `KiteItemContent` from children, if it exists.
 * - Iterate over the children of `KiteListItem` (`KiteListItemStart`, `KiteListItemCenter`, & `KiteListItemEnd`), as well as the first level of
 * children for each of the 3 sections, and apply any css, html changes needed for a11y and correct functionality.
 * @param {string} blockId
 * @param children
 * @param {boolean} disabled
 * @param {string} linkIcon
 * @param {boolean} selected
 * @param variant
 * @returns {[object, array, string]} [
 * - the first item is the `KiteListItemContent` child, if it exists
 * - the second item is all other `KiteListItem`
 * - the third item is the htmlId for the KiteTitle element
 * ]
 */
const applyListItemLogic = (blockId, children, disabled, linkIcon, selected, variant) => {
    const subtitleId = `list-subtitle-${blockId}`;
    let titleId = `list-title-${blockId}`;
    let titleText = '';
    // Separate `KiteListItemContent` (if it exists) from ListItemChildren
    const [contentChild, otherChildren] = processChildren(children);
    const updatedChildren = React.Children.map(otherChildren, (child) => {
        // Child is `KiteListItemEnd`. Variant is `link` or `expandable`
        // If no `KiteIndicator` was supplied in `KiteListItemEnd`, add one.
        // The Indicator contains icons that denote the ListItem's behavior.
        if (child.type === KiteListItemEnd &&
            indicatorVariants.includes(variant) &&
            !childrenContainsAtLeastOne(child.props.children, KiteIndicator)) {
            return React.cloneElement(child, {
                children: React.createElement(KiteIndicator, { variant: variant, linkIcon: linkIcon }),
            });
        }
        // Or
        // Child has Nested Children.  Modify Nested Children if applicable.
        if (child.props.children) {
            const updatedChildren = React.Children.map(child.props.children, (nestedChild) => {
                var _a, _b, _c, _d, _e;
                // Remove KitePopover from ListItem link variant.
                if (child.type === KiteListItemCenter &&
                    variant === 'link' &&
                    ((_a = nestedChild === null || nestedChild === void 0 ? void 0 : nestedChild.props) === null || _a === void 0 ? void 0 : _a.children) &&
                    childrenContainsAtLeastOne(nestedChild.props.children, KitePopover)) {
                    nestedChild = React.cloneElement(nestedChild, {
                        children: removeChildrenOfType(nestedChild.props.children, KitePopover),
                    });
                }
                // If user is passing id to KiteTitle, use that for a11y instead of generated uuid
                if (nestedChild.type === KiteTitle && ((_b = nestedChild === null || nestedChild === void 0 ? void 0 : nestedChild.props) === null || _b === void 0 ? void 0 : _b.id)) {
                    titleId = nestedChild.props.id;
                }
                // If Menu component is present, and no variant is set, set to "flow"
                if (nestedChild.type === KiteMenu && !((_c = nestedChild === null || nestedChild === void 0 ? void 0 : nestedChild.props) === null || _c === void 0 ? void 0 : _c.variant)) {
                    nestedChild = React.cloneElement(nestedChild, {
                        variant: 'flow',
                    });
                }
                if (nestedChild.type === KiteTitle &&
                    typeof ((_d = nestedChild === null || nestedChild === void 0 ? void 0 : nestedChild.props) === null || _d === void 0 ? void 0 : _d.children) === 'string') {
                    titleText = (_e = nestedChild === null || nestedChild === void 0 ? void 0 : nestedChild.props) === null || _e === void 0 ? void 0 : _e.children;
                }
                // Iterate through children of Start, Center, and End, and apply props changes needed.
                const listDynamicProps = getListDynamicProps(selected, titleId, subtitleId, disabled, variant, linkIcon, titleText);
                const filteredLogic = listDynamicProps.find((dynamicProp) => {
                    return (child.type === dynamicProp.childType &&
                        nestedChild.type === dynamicProp.nestedChildType);
                });
                return !!filteredLogic
                    ? cloneElement(nestedChild, filteredLogic.props)
                    : nestedChild;
            });
            return React.cloneElement(child, { children: updatedChildren });
        }
        // Or
        // Return child unmodified
        return child;
    });
    return [contentChild, updatedChildren, titleId];
};

const applyAnimationProps = ({ containerEl, contentEl, isExpanded, containerHeight, }) => {
    const [contentOpacity, windowMaxHeight] = isExpanded
        ? [1, containerHeight]
        : [0, 0];
    containerEl.setAttribute('style', `--max-height: ${applyUnits(windowMaxHeight)};`);
    contentEl.setAttribute('style', `--opacity: ${contentOpacity};`);
};
const applyUnits = createApplyUnits('px');
const useAnimation = (isExpandable, isExpanded, containerRef, contentRef) => {
    const containerHeightRef = useRef(null);
    useLayoutEffect(() => {
        const containerEl = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current;
        const contentEl = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current;
        if (!containerEl || !contentEl || !isExpandable)
            return;
        contentEl.classList.add('rk-kite-list-item__body');
        const newContainerHeight = containerEl.getBoundingClientRect().height;
        applyAnimationProps({
            containerEl,
            contentEl,
            isExpanded,
            containerHeight: newContainerHeight,
        });
        if (!!!containerHeightRef.current)
            containerHeightRef.current = newContainerHeight;
        containerEl.classList.add('kite-list-item__expandable-container');
    }, []);
    useEffect(() => {
        const contentEl = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current;
        const containerEl = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current;
        if (containerHeightRef.current === null || !isExpandable)
            return;
        applyAnimationProps({
            containerEl,
            contentEl,
            isExpanded,
            containerHeight: containerHeightRef.current,
        });
    }, [
        containerRef.current,
        contentRef.current,
        isExpanded,
        containerHeightRef.current,
    ]);
};

const KiteListItemContentWrap = ({ children, liTitleId, isExpanded, isExpandable, }) => {
    const containerRef = useRef(null);
    const contentRef = useRef(null);
    useAnimation(isExpandable, isExpanded, containerRef, contentRef);
    const contentOuterProps = isExpandable
        ? Object.assign(Object.assign({ 'aria-labelledby': liTitleId }, (!isExpandable && {
            className: 'kite-list-item__expandable-container',
        })), { role: 'region' }) : {};
    const contentInnerProps = Object.assign({}, (!isExpandable && { className: 'rk-kite-list-item__body' }));
    return (React.createElement("div", Object.assign({}, contentOuterProps, { ref: containerRef }),
        React.createElement("div", Object.assign({}, contentInnerProps, { ref: contentRef }), children)));
};

const provisionMouseEvent = (e, payload) => {
    if (!e)
        return undefined;
    if (!payload)
        return e;
    e.payload = payload;
    return e;
};
/**
 * View the design specifications for [list](https://beam.kiteds.com/web/components/list/#overview)
 */
const KiteListItem = forwardRef((props, ref) => {
    const { variant, disabled, href, isExpanded: controlledIsExpanded = false, onClick: clickCallback = () => {
        /* Do Nothing */
    }, linkIcon = 'internal', children: childrenRaw, id, selected, className: incomingClassName } = props, other = __rest(props, ["variant", "disabled", "href", "isExpanded", "onClick", "linkIcon", "children", "id", "selected", "className"]);
    const isExpandable = variant === 'expandable';
    const [isExpanded, setIsExpanded] = useState(isExpandable ? controlledIsExpanded : true);
    useEffect(() => {
        setIsExpanded(controlledIsExpanded);
    }, [controlledIsExpanded]);
    const blockId = useNormalId(id);
    // Make Necessary adjustments to KiteListItem subcomponent children and their props.
    const [contentChild, children, liTitleId] = useMemo(() => applyListItemLogic(blockId, childrenRaw, disabled, linkIcon, selected, variant), [blockId, childrenRaw, disabled, linkIcon, selected, variant]);
    const maybeDisabledProps = {
        disabled,
        'aria-disabled': disabled,
    };
    // Define Wrap component and props for every applicable `KiteListItem` variant
    const mapVariantToWrapSchema = {
        link: [
            'a',
            Object.assign(Object.assign({}, maybeDisabledProps), { href: !disabled && href ? href : null, className: 'kite-list-item__action', onClick: (e) => {
                    clickCallback(provisionMouseEvent(e, { variant }));
                } }),
        ],
        expandable: [
            'button',
            Object.assign(Object.assign({}, maybeDisabledProps), { type: 'button', 'aria-expanded': isExpanded, className: 'kite-list-item__action', onClick: (e) => {
                    const newIsExpanded = !isExpanded;
                    setIsExpanded(newIsExpanded);
                    clickCallback(provisionMouseEvent(e, { variant, isExpanded: newIsExpanded }));
                } }),
        ],
        default: [
            'div',
            {
                className: 'kite-list-item__content',
            },
        ],
    };
    // Pull `WrapComponent` and `wrapProps` from variant schema
    const [WrapComponent, wrapProps] = mapVariantToWrapSchema[variant] || mapVariantToWrapSchema['default'];
    const liProps = Object.assign({ ref, className: classNames('kite-list-item', incomingClassName, {
            'kite-list-item--disabled': disabled,
            'kite-list-item--expandable': isExpandable,
            'kite-list-item--expanded': isExpandable && isExpanded,
        }), id: blockId }, other);
    return (React.createElement("li", Object.assign({}, liProps),
        React.createElement(WrapComponent, Object.assign({}, wrapProps),
            React.createElement("span", { className: "kite-list-item__inner", role: "presentation" }, children)),
        !!contentChild && (React.createElement(KiteListItemContentWrap, { liTitleId, isExpandable, isExpanded }, contentChild))));
});

/**
 * View the design specifications for [list](https://beam.kiteds.com/web/components/list/#overview)
 */
const KiteListItemStart = forwardRef((_a, ref) => {
    var { children, className: incomingClassName } = _a, other = __rest(_a, ["children", "className"]);
    return (React.createElement("div", Object.assign({ ref: ref, className: classNames('kite-list-item__start', incomingClassName) }, other), children));
});

const KiteListItemThumbnail = (_a) => {
    var { src, size = 'lg', className: incomingClassName } = _a, other = __rest(_a, ["src", "size", "className"]);
    const thumbnailClassNames = classNames('kite-list-item__image', incomingClassName, {
        [`kite-list-item__image--${size}`]: !!size,
    });
    return (React.createElement("div", { className: thumbnailClassNames },
        React.createElement(KiteImage, Object.assign({ src: src }, other))));
};

function KiteMenuItem(_a) {
    var { children, className, destructive = false, negative, disabled = false, icon = null, inline = false, onClick, onKeyup, onKeyDown, onSelect, value, href, target, variant = 'button' } = _a, other = __rest(_a, ["children", "className", "destructive", "negative", "disabled", "icon", "inline", "onClick", "onKeyup", "onKeyDown", "onSelect", "value", "href", "target", "variant"]);
    if (!!destructive) {
        console.warn('Property destructive has been deprecated. Use negative prop instead.');
    }
    const liClasses = classNames('kite-picker__item', {
        'kite-picker__item--negative': destructive || negative,
    });
    const spacer = classNames('kite-picker__action-check', 'kite-picker__action-check--hidden');
    const classes = classNames('kite-picker__action', className);
    const TriggerRender = (props) => {
        const { children } = props;
        return variant === 'link' ? (React.createElement("a", Object.assign({ href: href, target: target }, other, props), children)) : (React.createElement("button", Object.assign({}, other, props), children));
    };
    return (React.createElement("li", { className: liClasses },
        React.createElement(TriggerRender, { className: classes, onClick: onClick, onKeyUp: onKeyup, onKeyDown: onKeyDown, disabled: disabled, "aria-disabled": disabled },
            inline && (React.createElement("div", { className: spacer },
                React.createElement(KiteIcon, { icon: 'ki-checkmark' }))),
            icon && (React.createElement("div", { className: "kite-picker__action-icon" },
                React.createElement(KiteIcon, { icon: icon }))),
            React.createElement("span", null, children))));
}

const ARROW_DOWN = 'ArrowDown';
const ARROW_UP = 'ArrowUp';
const CLICK = 'click';
const ESCAPE = 'Escape';
const ENTER = 'Enter';
const SPACE = ' ';
function KiteDropdownBase(_a) {
    var { ariaLabel, ariaDescribedBy, comboboxAriaLabel, buttonClasses, children, disabled, iconProps, inline = false, labelVariant = false, label, helperText, id, errorMessage, maxItemCount, menuIcon, onChange, onToggle, placeholder, position, positionClasses, primaryClasses, showLabel = true, showValue = true, triggerRef, value, baseCSSClass = 'kite-menu', openPosition, enableVirtualization } = _a, others = __rest(_a, ["ariaLabel", "ariaDescribedBy", "comboboxAriaLabel", "buttonClasses", "children", "disabled", "iconProps", "inline", "labelVariant", "label", "helperText", "id", "errorMessage", "maxItemCount", "menuIcon", "onChange", "onToggle", "placeholder", "position", "positionClasses", "primaryClasses", "showLabel", "showValue", "triggerRef", "value", "baseCSSClass", "openPosition", "enableVirtualization"]);
    const listRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [valueInitialized, setValueInitialized] = useState(false);
    const [internalValue, setValue] = useState('');
    const [internalResolvedValue, setInternalResolvedValue] = useState('');
    const [activeDescendant, setActiveDescendant] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(0);
    const selectedIndex = useRef(null);
    const menuRef = useRef(null);
    const uuid = useUID();
    const menuId = !!id ? id : uuid;
    const buttonId = `button-${menuId}`;
    const menuLabelId = `label-${menuId}`;
    const valueId = `value-${menuId}`;
    const positionClass = positionClasses[position];
    const { strategies, context } = useUpDownOverlay(openPosition);
    const [valueToLabel, setValueToLabel] = useState(null);
    useEffect(() => {
        setTimeout(() => {
            const focusVisibleEl = document.querySelector('.kite-focus-visible');
            focusVisibleEl
                ? setActiveDescendant(focusVisibleEl === null || focusVisibleEl === void 0 ? void 0 : focusVisibleEl.id)
                : setActiveDescendant(null);
        });
    }, [focusedIndex]);
    const [itemChildren, labelChildren] = useMemo(() => {
        return partitionChildrenByDisplayName(children, 'KiteLabel');
    }, [children]);
    // Grab first label if at least 1 popover is present.  Otherwise return undefined.
    const [firstChildLabel] = labelChildren;
    /**
     * Get the resolved value (the label that is displayed).
     * If there is no valueToLabel map, it means the label and value are the same, and to return value.
     * If there is a valueToLabel map, return the mapped value.
     * @param value
     */
    const getInternalResolvedValue = (value) => {
        if (!!valueToLabel) {
            return valueToLabel[value];
        }
        else {
            return value;
        }
    };
    const getChildValue = (child) => {
        var _a, _b;
        if (!!valueToLabel) {
            return (_a = child.props) === null || _a === void 0 ? void 0 : _a.value;
        }
        else {
            return (_b = child.props) === null || _b === void 0 ? void 0 : _b.children;
        }
    };
    // build a map to resolve labels for values
    useEffect(() => {
        var _a;
        const childrenArray = React.Children.toArray(itemChildren);
        if (itemChildren &&
            childrenArray.length > 0 &&
            React.isValidElement(childrenArray[0])) {
            // If there is no value prop on children, assume label and value are the same, so there is no need for the valueToLabel map
            if (!((_a = childrenArray[0]) === null || _a === void 0 ? void 0 : _a.props.hasOwnProperty('value'))) {
                setValueToLabel(undefined);
            }
            else if (
            // Avoid remapping valueToLabel if children have not changed.
            (!valueToLabel && itemChildren) ||
                (!!valueToLabel &&
                    childrenArray.length * 2 + 2 !== Object.keys(valueToLabel).length &&
                    childrenArray.length + 2 !== Object.keys(valueToLabel).length)) {
                setValueToLabel([
                    { label: undefined, value: undefined },
                    ...React.Children.map(itemChildren, (child, index) => ({
                        label: child.props.children,
                        value: child.props.value,
                    })),
                ].reduce((final, item) => {
                    return Object.assign(Object.assign({}, final), { [item.value]: item.label, [item.label]: item.value });
                }));
            }
        }
    }, [itemChildren]);
    const incrementFocusedIndex = () => {
        var _a, _b;
        if (focusedIndex < ((_a = React.Children.toArray(itemChildren)) === null || _a === void 0 ? void 0 : _a.length) - 1) {
            setFocusedIndex((focusedIndex + 1) % ((_b = React.Children.toArray(itemChildren)) === null || _b === void 0 ? void 0 : _b.length));
        }
    };
    const decrementFocusedIndex = () => {
        if (focusedIndex !== 0) {
            setFocusedIndex(focusedIndex - 1);
        }
    };
    const preventArrowScroll = (e) => {
        if (e.key === ARROW_DOWN || e.key === ARROW_UP) {
            e.preventDefault();
        }
    };
    const handleClick = (e) => {
        if (e.type === CLICK) {
            toggle(e, internalValue);
        }
    };
    const selectFocusedIndex = (e) => {
        const focusVisibleEl = document.querySelector('.kite-focus-visible');
        if (focusVisibleEl &&
            (focusVisibleEl === null || focusVisibleEl === void 0 ? void 0 : focusVisibleEl.getAttribute('aria-disabled')) !== 'true') {
            focusVisibleEl.click();
            if (!valueToLabel) {
                toggle(e, focusVisibleEl.textContent);
            }
            else {
                toggle(e, valueToLabel[focusVisibleEl.textContent]);
            }
        }
    };
    const handleButtonKeyDown = (e) => {
        if (e.key === SPACE) {
            e.preventDefault();
        }
        else if (e.key === ENTER && !expanded) {
            toggle(e, internalValue);
        }
        else if (e.key === ENTER && expanded) {
            selectFocusedIndex(e);
        }
        else if (e.key === ARROW_DOWN && expanded) {
            incrementFocusedIndex();
        }
        else if (e.key === ARROW_UP) {
            decrementFocusedIndex();
        }
        preventArrowScroll(e);
    };
    const handleButtonKeyUp = (e) => {
        if (e.key === ESCAPE) {
            setExpanded(false);
            e.stopPropagation();
            return;
        }
        else if (e.key === SPACE) {
            if (expanded) {
                selectFocusedIndex(e);
            }
            else {
                toggle(e, internalValue);
            }
        }
        else if (e.key === ARROW_DOWN) {
            if (!expanded) {
                toggle(e, internalValue);
            }
        }
    };
    const toggle = (e = null, v = null, disabled) => {
        var _a;
        const next = !expanded;
        setExpanded(next);
        if (!disabled && v) {
            setValue(v);
            setInternalResolvedValue(getInternalResolvedValue(v));
        }
        if (!!onToggle) {
            onToggle(next);
        }
        if (!next) {
            (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        if (selectedIndex === null || selectedIndex === void 0 ? void 0 : selectedIndex.current) {
            setFocusedIndex(selectedIndex.current);
        }
        else {
            setFocusedIndex(0);
        }
    };
    const classes = classNames(primaryClasses, positionClass, {
        [`${baseCSSClass}--open`]: expanded,
    });
    const options = useMemo(() => {
        return React.Children.map(itemChildren, (child, index) => {
            const _value = getChildValue(child);
            const _handleClick = (e) => {
                if (!!child.props.onClick) {
                    child.props.onClick(e);
                }
                if (child.props.onSelect) {
                    child.props.onSelect(_value);
                }
                toggle(undefined, _value, child.props.disabled);
            };
            if (internalValue === _value) {
                selectedIndex.current = index;
            }
            return React.cloneElement(child, {
                onClick: _handleClick,
                className: index === focusedIndex
                    ? `kite-focus-visible ${child.props.className}`
                    : child.props.className,
                selected: (internalValue === null || internalValue === void 0 ? void 0 : internalValue.toString()) === _value,
            });
        });
    }, [focusedIndex, expanded]);
    useEffect(() => {
        if (!initialized && (valueToLabel === undefined || !!valueToLabel)) {
            ReactDOM.unstable_batchedUpdates(() => {
                if (!!value) {
                    // consumer has passed an initial value
                    setValue(value);
                    setInternalResolvedValue(getInternalResolvedValue(value));
                }
                setInitialized(true);
            });
        }
    }, [initialized, valueToLabel]);
    useEffect(() => {
        setInternalResolvedValue(getInternalResolvedValue(value));
        setValue(value);
    }, [value, valueToLabel]);
    useEffect(() => {
        if (!!onChange && initialized) {
            if (valueInitialized) {
                onChange(internalValue);
            }
            if (!valueInitialized) {
                setValueInitialized(true);
            }
        }
    }, [internalValue, initialized]);
    // Manage DropDown `scrollTop` - always keep `focusedItem` inside viewport
    useEffect(() => {
        if (!listRef.current)
            return;
        const ul = listRef.current;
        const focusedItem = ul.children[focusedIndex];
        if (!focusedItem)
            return;
        const ulRect = ul.getBoundingClientRect();
        const itemRect = focusedItem.getBoundingClientRect();
        // is `focusedItem` above the visible viewport?
        if (itemRect.top < ulRect.top) {
            // Lower `scrollTop` to push `focusedItem` to the top of the viewport
            ul.scrollTop -= ulRect.top - itemRect.top;
        }
        // is `focusedItem` below the visible viewport?
        if (itemRect.bottom > ulRect.bottom) {
            // Increase `scrollTop` to pull `focusedItem` to the bottom of the viewport
            ul.scrollTop += itemRect.bottom - ulRect.bottom;
        }
    }, [focusedIndex]);
    const handleBlur = (e) => {
        var _a, _b;
        const relatedTarget = e['relatedTarget'];
        if (!((_a = menuRef === null || menuRef === void 0 ? void 0 : menuRef.current) === null || _a === void 0 ? void 0 : _a.contains(relatedTarget)) &&
            !((_b = menuRef === null || menuRef === void 0 ? void 0 : menuRef.current) === null || _b === void 0 ? void 0 : _b.isSameNode(relatedTarget))) {
            setExpanded(false);
        }
    };
    const ariaLabelledBy = combineIdentifiers(menuLabelId, { [getHelperTextIdFromLabelId(menuLabelId)]: !!helperText }, valueId);
    // If label text is provided, use aria-label.  otherwise, use constructed aria-labelledby
    const buttonLabelProp = !!comboboxAriaLabel
        ? { 'aria-label': comboboxAriaLabel }
        : {
            'aria-labelledby': ariaLabelledBy,
        };
    const labelElement = useMemo(() => {
        if (firstChildLabel) {
            const labelProps = {
                id: menuLabelId,
                htmlFor: buttonId,
                baseCSSClass,
            };
            if (disabled) {
                labelProps.disabled = disabled;
            }
            if (helperText) {
                labelProps.helperText = helperText;
            }
            return React.cloneElement(firstChildLabel, labelProps);
        }
        else {
            return null;
        }
    }, [firstChildLabel]);
    return (React.createElement(React.Fragment, null,
        React.createElement("span", { ref: menuRef, className: classes, onBlur: handleBlur },
            showLabel &&
                showValue &&
                !inline &&
                (labelElement || (React.createElement(KiteLabel, { id: menuLabelId, baseCSSClass: baseCSSClass, htmlFor: buttonId, helperText: helperText, disabled: disabled }, label))),
            React.createElement("div", Object.assign({ className: buttonClasses, role: 'combobox', id: buttonId, "aria-controls": `${menuId}`, "aria-activedescendant": `${activeDescendant}`, "aria-describedby": errorMessage ? `${menuId}-feedback` : '', "aria-disabled": disabled }, buttonLabelProp, others, { onClick: handleClick, onKeyUp: handleButtonKeyUp, onKeyDown: handleButtonKeyDown, ref: triggerRef, "aria-expanded": expanded, tabIndex: 0 }),
                showLabel &&
                    showValue &&
                    inline &&
                    (labelElement || (React.createElement(KiteLabel, { id: menuLabelId, baseCSSClass: baseCSSClass, htmlFor: buttonId }, label))),
                !!internalValue ? (React.createElement("span", { id: valueId, className: `${baseCSSClass}__value` }, internalResolvedValue)) : (React.createElement("span", { id: `${menuId}-placeholder`, className: `${baseCSSClass}__value ${baseCSSClass}__value--placeholder` }, placeholder)),
                React.createElement(KiteIcon, Object.assign({ icon: menuIcon }, iconProps))),
            React.createElement("span", { className: `${baseCSSClass}__feedback`, id: `${menuId}-feedback` }, errorMessage),
            expanded && (React.createElement(AttachedOverlay, { context: context, origin: triggerRef.current, strategies: strategies, shouldReturnFocus: false },
                React.createElement("div", { onMouseDown: preventOnBlur, className: "kite-picker kite-picker--list kite-picker--overlay kite-picker--open" },
                    React.createElement("div", { className: "kite-picker__content" },
                        React.createElement("ul", { role: "listbox", className: classNames('kite-picker__list', {
                                'rk-picker__list--virtualization': enableVirtualization,
                            }), id: menuId, "aria-label": ariaLabel, onBlur: handleBlur, ref: listRef, style: { '--max-item-count': maxItemCount } },
                            React.createElement(KiteWindow, { menuItemHeight: 45, totalItemCount: options.length, maxItemInWindowCount: maxItemCount, enabledVirtualization: enableVirtualization, focusedIndex: focusedIndex }, options)))))))));
}

const positionClasses = {
    'top-left': 'kite-menu--left kite-menu--top',
    'top-right': 'kite-menu--right kite-menu--top',
    'bottom-left': 'kite-menu--left kite-menu--bottom',
    'bottom-right': 'kite-menu--right kite-menu--bottom',
};
function KiteDropDown(_a) {
    var { ariaLabel, comboboxAriaLabel, children, disabled, errorMessage, id, label, helperText, onChange, onToggle, placeholder, position, maxItemCount = dropdownMaxItemCount, value, variant = 'dropdown', openPosition = 'down', enableVirtualization } = _a, others = __rest(_a, ["ariaLabel", "comboboxAriaLabel", "children", "disabled", "errorMessage", "id", "label", "helperText", "onChange", "onToggle", "placeholder", "position", "maxItemCount", "value", "variant", "openPosition", "enableVirtualization"]);
    // eventually we will be allowing the consumer to get a ref to the button.
    const triggerRef = useRef(null);
    const inline = variant === 'inline';
    const timeInput = variant === 'time-input';
    const baseCSSClass = variant === 'time-input' ? 'kite-time-input' : 'kite-menu';
    const classes = classNames(`${baseCSSClass}`, {
        [`${baseCSSClass}--block`]: !!!position && !timeInput && !inline, // they've not specified a position, but also not an inline
        [`${baseCSSClass}--full`]: !inline && !timeInput,
        [`${baseCSSClass}--dropdown`]: !inline && !timeInput,
        [`${baseCSSClass}--inline`]: inline && !timeInput,
        [`${baseCSSClass}--shrinkwrap`]: inline && timeInput,
        [`${baseCSSClass}--dropdown-shrinkwrap`]: !inline && !timeInput,
        [`${baseCSSClass}--scrollable`]: !!maxItemCount || timeInput,
        [`${baseCSSClass}--disabled`]: disabled,
        [`${baseCSSClass}--error`]: errorMessage,
    });
    const normalId = useNormalId();
    const dropDownId = !!id ? id : `dropdown-${normalId}`;
    return (React.createElement(KiteDropdownBase, Object.assign({ label: label, helperText: helperText, id: dropDownId, ariaLabel: ariaLabel, buttonClasses: `${baseCSSClass}__selection`, comboboxAriaLabel: comboboxAriaLabel, menuIcon: timeInput ? 'ki-clock' : 'ki-chevron-down', disabled: disabled, errorMessage: errorMessage, inline: variant === 'inline', maxItemCount: maxItemCount, onChange: onChange, onToggle: onToggle, placeholder: placeholder, position: position, positionClasses: positionClasses, primaryClasses: classes, triggerRef: triggerRef, value: value, baseCSSClass: baseCSSClass, openPosition: openPosition, enableVirtualization: enableVirtualization }, others), children));
}

function KiteDropDownItem(_a) {
    var { children, className, disabled = false, onClick, onSelect, selected, value } = _a, other = __rest(_a, ["children", "className", "disabled", "onClick", "onSelect", "selected", "value"]);
    const liClasses = classNames('kite-picker__item');
    const normalId = useNormalId();
    const classes = classNames('kite-picker__action', { 'kite-menu__action--disabled': disabled }, className);
    const checkMark = classNames('kite-picker__action-check', {
        'kite-picker__action-check--hidden': !selected,
        'kite-picker__action-check--selected': selected,
    });
    return (React.createElement("li", { className: liClasses },
        React.createElement("div", Object.assign({ id: `kite-dropdown-item--${normalId}`, "aria-selected": selected, role: "option", tabIndex: -1 }, other, { className: classes, onClick: onClick, "aria-disabled": disabled }),
            React.createElement("div", { className: checkMark },
                React.createElement(KiteIcon, { icon: 'ki-checkmark' })),
            React.createElement("span", null, children))));
}
KiteDropDownItem.defaultValues = {
    value: '',
};

const KiteMeterCircularProgress = ({ size, icons, id, statusProp: { icon }, label, valueLabel, }) => {
    const dimension = size === 'small' ? 12 : size === 'medium' ? 44 : 74;
    const viewBox = `0 0 ${dimension * 2} ${dimension * 2}`;
    const cx = dimension;
    const cy = dimension;
    const r = dimension === 12 ? 11 : dimension - 4;
    const tickx = dimension === 12 ? 11 : dimension - 2;
    const normalId = useNormalId(id);
    const meterLabelId = `meter-label-1-${normalId}`;
    const smallLabel = (React.createElement("span", { id: meterLabelId, className: "kite-meter__label" },
        label,
        " ",
        valueLabel));
    const mediumValueLabel = (React.createElement("span", { className: "kite-meter__value-label" }, valueLabel));
    const mediumLabel = (React.createElement("span", { id: meterLabelId, className: "kite-meter__label" }, label));
    const largeLabel = (React.createElement(React.Fragment, null,
        React.createElement("span", { id: meterLabelId, className: "kite-meter__label" }, label),
        React.createElement("span", { className: "kite-meter__value-label" }, valueLabel)));
    return (React.createElement(React.Fragment, null,
        size === 'medium' && mediumLabel,
        React.createElement("div", { className: "kite-meter__progress-group" },
            React.createElement("div", { className: "kite-meter__value-group" },
                !icons && icon && (React.createElement("span", { className: "kite-meter__status-icon" },
                    React.createElement(KiteIcon, { icon: icon }))),
                !icon && size === 'small' && (React.createElement("span", { className: "kite-meter__status-icon" })),
                size === 'small' && smallLabel,
                size === 'large' && largeLabel,
                size === 'medium' && mediumValueLabel),
            React.createElement("svg", { className: "kite-meter__progress", viewBox: viewBox, focusable: "false" },
                React.createElement("circle", { className: "kite-meter__track", cx: cx, cy: cy, r: r }),
                React.createElement("circle", { className: "kite-meter__progress-fill", cx: cx, cy: cy, r: r }),
                React.createElement("rect", { className: "kite-meter__tick", x: tickx, y: 0 })))));
};

const KiteMeterLinearProgress = ({ icons, id, statusLabel, statusProp: { icon }, label, valueLabel, }) => {
    const normalId = useNormalId(id);
    const meterLabelId = `meter-label-${normalId}`;
    const meterValueLabelId = `meter-value-label-${normalId}`;
    return (React.createElement(React.Fragment, null,
        React.createElement("span", { id: meterLabelId, className: "kite-meter__label" }, label),
        React.createElement("div", { className: "kite-meter__track", "aria-hidden": "true" },
            React.createElement("div", { className: "kite-meter__progress-fill" })),
        React.createElement("div", { className: "kite-meter__value-group", role: "status" },
            React.createElement("span", { className: "kite-meter__value-label", id: meterValueLabelId }, valueLabel),
            React.createElement("span", { className: "kite-meter__status-group" },
                !icons && !!icon && (React.createElement("span", { className: "kite-meter__status-icon" },
                    React.createElement(KiteIcon, { icon: icon }))),
                statusLabel && (React.createElement("span", { className: "kite-meter__status-label" }, statusLabel))))));
};

const sizesTranslations = {
    small: 'sm',
    medium: 'md',
    large: 'lg',
};
const statusProps = {
    alert: {
        icon: 'ki-caution-circle-f',
        title: 'Caution',
    },
    positive: {
        icon: 'ki-checkmark-circle-f',
        title: 'Positive',
        css: 'positive',
    },
    caution: {
        icon: 'ki-caution-alert-f',
        title: 'Alert',
    },
    neutral: {
        title: 'Information',
    },
    negative: {
        icon: 'ki-caution-circle-f',
        title: 'Caution',
        css: 'negative',
    },
};
const validateProps = (props) => {
    const { type, size, status } = props;
    return (!!type &&
        !!size &&
        !!status &&
        !!['small', 'medium', 'large', null, undefined].find((value) => value === size) &&
        !!['linear', 'circular', null, undefined].find((value) => value === type));
};
/**
 * View the design specifications for [meter](https://beam.kiteds.com/web/components/meter/design.html).
 */
function KiteMeter(_a) {
    var _b;
    var { className = '', role = null, value = 0, type = 'linear', size = 'medium', status = 'neutral', linearStatusLabel, hideIcons = false, id, label = null, valueLabel = null } = _a, other = __rest(_a, ["className", "role", "value", "type", "size", "status", "linearStatusLabel", "hideIcons", "id", "label", "valueLabel"]);
    if (status === 'alert') {
        console.warn('Enum value error has been deprecated. Use status=negative');
    }
    if (type === 'circular' && !!linearStatusLabel) {
        console.warn('The linearStatusLabel only applies to the linear variant of meter.');
    }
    if (!validateProps({ status, type, size })) {
        return (React.createElement(KiteAlert, { level: "page", type: "error", description: "KiteMeter - invalid definition.  Please check all props." }));
    }
    const statusProp = (_b = statusProps[status]) !== null && _b !== void 0 ? _b : statusProps.neutral;
    const sizeClass = `kite-meter--${sizesTranslations[size]}`;
    const typeClass = `kite-meter--${type}`;
    const statusClass = `kite-meter--${statusProp.css ? statusProp.css : status}`;
    const classes = `kite-meter ${sizeClass} ${typeClass} ${statusClass} ${className}`;
    // Resolve role
    const assistiveStatusLabel = `${label} ${valueLabel}`;
    // Render circlular progress
    const progressCircle = () => (React.createElement(KiteMeterCircularProgress, { statusProp: statusProp, status: status, valueLabel: valueLabel, assistiveStatusLabel: assistiveStatusLabel, label: label, icons: hideIcons, size: size, id: id }));
    // Render linear progress
    const progressLinear = () => (React.createElement(KiteMeterLinearProgress, { statusProp: statusProp, status: status, valueLabel: valueLabel, statusLabel: linearStatusLabel, label: label, icons: hideIcons, size: size, id: id }));
    return (React.createElement("div", Object.assign({ className: classes, style: { '--percent': value }, "aria-live": "polite", "aria-atomic": "true", role: "status" }, other), type === 'linear' ? progressLinear() : progressCircle()));
}

const defaultPaginationLabels = {
    previousPage: 'Previous page',
    nextPage: 'Next page',
    pageLabel: 'Page',
    ofLabel: 'of',
    itemsPerPageLabel: 'Items Per Page',
    throughLabel: 'through',
    itemsLabel: 'items',
};
/**
 * Standard pagination element, adaptable to any number of pages. As a reactive component, all functions for click events should be passed in, which should update the **currentPage** prop appropriately. [View the design specifications](https://beam.kiteds.com/web/components/pagination/design.html)
 */
const KitePagination = (_a) => {
    var { className, expandUp, variant = 'default', maxItemCount = 6, currentPage, onNextPage, onPrevPage, onPageSelect, pageSizeSelection = true, pageProgress = true, totalItems, itemsPerPage, onItemsPerPageChange, itemsPerPageOptions, ariaLabel, renderArialabel, labels = defaultPaginationLabels, enableVirtualization } = _a, other = __rest(_a, ["className", "expandUp", "variant", "maxItemCount", "currentPage", "onNextPage", "onPrevPage", "onPageSelect", "pageSizeSelection", "pageProgress", "totalItems", "itemsPerPage", "onItemsPerPageChange", "itemsPerPageOptions", "ariaLabel", "renderArialabel", "labels", "enableVirtualization"]);
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const [currentPageState, setCurrentPageState] = useState(currentPage);
    const [itemsPerPageState, setItemsPerPageState] = useState(itemsPerPage);
    const [prevPageDisabled, nextPageDisabled, currentStart, currentEnd, pageList,] = useMemo(() => {
        const currentItemNumber = itemsPerPage * (currentPage - 1);
        let _currentEnd;
        if (totalItems - itemsPerPage < 0 ||
            (currentPage === totalPages && totalItems - itemsPerPage * totalPages < 0)) {
            _currentEnd = totalItems;
        }
        else {
            _currentEnd = currentItemNumber + itemsPerPage;
        }
        return [
            currentPage === 1,
            currentPage === totalPages,
            currentItemNumber + 1,
            _currentEnd,
            Array.from({ length: totalPages }, (x, i) => i + 1),
        ];
    }, [currentPage, itemsPerPage, totalPages, totalItems]);
    const [paginationAriaLabel, setPaginationAriaLabel] = useState('');
    useEffect(() => {
        setPaginationAriaLabel(`${labels.pageLabel} ${currentPage} ${labels.ofLabel} ${totalPages}. ${currentStart} ${labels.throughLabel} ${currentEnd} ${labels.ofLabel} ${totalItems} ${labels.itemsLabel}`);
        renderArialabel &&
            renderArialabel(currentPage, totalPages, currentStart, currentEnd, totalItems);
    }, [currentPage, totalPages, currentStart, currentEnd, totalItems]);
    return (React.createElement("nav", Object.assign({ className: classNames('kite-pagination rk-pagination', {
            'kite-pagination--up': expandUp,
            [className]: className,
        }), "aria-label": "pagination" }, other),
        React.createElement("div", { className: "kite-pagination__left" },
            React.createElement("div", { className: "kite-pagination__prevnext" },
                React.createElement("button", { role: "link", className: "kite-pagination__prev", type: "button", onClick: onPrevPage, disabled: prevPageDisabled },
                    React.createElement("span", { className: "kite-assistive-text" }, labels.previousPage),
                    React.createElement(KiteIcon, { icon: "ki-chevron-left" })),
                React.createElement("button", { role: "link", className: "kite-pagination__next", type: "button", onClick: onNextPage, disabled: nextPageDisabled },
                    React.createElement("span", { className: "kite-assistive-text" }, labels.nextPage),
                    React.createElement(KiteIcon, { icon: "ki-chevron-right" }))),
            React.createElement("span", { className: "kite-assistive-text", "aria-live": "polite" }, ariaLabel ? ariaLabel : paginationAriaLabel),
            React.createElement("div", { className: "kite-pagination__page-select" },
                React.createElement("span", { className: "kite-pagination__page-label", "aria-hidden": "true", role: "presentation" }, labels.pageLabel),
                variant === 'default' && (pageList === null || pageList === void 0 ? void 0 : pageList.length) > 0 ? (React.createElement(KiteDropDown, { variant: "inline", maxItemCount: maxItemCount, value: currentPage, comboboxAriaLabel: `Page ${currentPage} of ${totalPages}`, onChange: (e) => {
                        const p = parseInt(e);
                        if (p !== currentPageState) {
                            onPageSelect(parseInt(e));
                            setCurrentPageState(p);
                        }
                    }, openPosition: expandUp ? 'up' : 'down', enableVirtualization: totalPages > 3000 || enableVirtualization }, pageList.map((item) => (React.createElement(KiteDropDownItem, { key: item }, item.toString()))))) : (React.createElement("div", { className: "kite-pagination__page-current" },
                    " ",
                    currentPage)),
                React.createElement("span", { className: "kite-pagination__page-total" }, labels.ofLabel + ' ' + totalPages))),
        React.createElement("div", { className: "kite-pagination__right" },
            pageProgress ? (React.createElement("div", { className: "kite-pagination__count" },
                currentStart,
                " - ",
                currentEnd,
                " ",
                labels.ofLabel,
                " ",
                totalItems,
                " ",
                labels.itemsLabel)) : (React.createElement(React.Fragment, null)),
            pageSizeSelection ? (React.createElement("div", { className: "kite-pagination__page-size" },
                React.createElement("span", { className: "kite-pagination__page-size-label", "aria-hidden": true }, labels.itemsPerPageLabel),
                React.createElement(KiteDropDown, { variant: "inline", value: itemsPerPage, maxItemCount: maxItemCount, comboboxAriaLabel: `Items per page ${itemsPerPage}`, onChange: (e) => {
                        const i = parseInt(e);
                        if (i !== itemsPerPageState) {
                            onItemsPerPageChange(i);
                            setItemsPerPageState(i);
                        }
                    }, openPosition: expandUp ? 'up' : 'down', enableVirtualization: enableVirtualization }, itemsPerPageOptions.map((item) => (React.createElement(KiteDropDownItem, { key: item }, item.toString())))))) : (React.createElement(React.Fragment, null)))));
};

/**
 * If passive alert group doesn't exist, create it and append to body.
 */
const createPassiveAlertGroup = () => {
    var _a;
    if (((_a = document.querySelectorAll('.kite-alert-region.kite-alert-region--passive')) === null || _a === void 0 ? void 0 : _a.length) === 0) {
        const alertsGroup = document.createElement('div');
        alertsGroup.classList.add('kite-alert-region');
        alertsGroup.classList.add('kite-alert-region--passive');
        alertsGroup.setAttribute('role', 'region');
        alertsGroup.setAttribute('aria-label', 'alerts');
        document.body.append(alertsGroup);
    }
};
/**
 * If the alert group is empty, remove from DOM
 */
const removeEmptyPassiveAlertGroup = () => {
    var _a;
    if (((_a = document.querySelectorAll('.kite-alert-region.kite-alert-region--passive')[0]) === null || _a === void 0 ? void 0 : _a.children.length) === 0) {
        document
            .querySelectorAll('.kite-alert-region.kite-alert-region--passive')[0]
            .remove();
    }
};

const KitePassiveAlertTokenEnum = {
    TIMEOUT: 'kite-alert-passive-timeout',
};
/**
 * View the design specifications for [alerts](https://beam.kiteds.com/web/components/alert/design.html).
 */
const KitePassiveAlert = (_a) => {
    var { className, id, linkText, linkUrl, linkVariant = 'link', onClose, onLinkClick, type, status, children, title, disableAutoDismiss = false, shouldCtaLinkDescription = false, showIcon = true, canDismiss = false } = _a, other = __rest(_a, ["className", "id", "linkText", "linkUrl", "linkVariant", "onClose", "onLinkClick", "type", "status", "children", "title", "disableAutoDismiss", "shouldCtaLinkDescription", "showIcon", "canDismiss"]);
    const [alertInitializing, setAlertInitializing] = useState(true);
    const [isPassiveAutoDismiss, setIsPassiveAutoDismiss] = useState(false);
    const [regionRef, setRegionRef] = useState(null);
    const alertRef = useRef(null);
    const { tokenOverrides, getTokenValue } = useKiteTokensContext();
    const [timeout] = useMemo(() => [getTokenValue(KitePassiveAlertTokenEnum.TIMEOUT, '3500')], [tokenOverrides]);
    if (!!type) {
        if (type === 'error') {
            console.warn('Property type has been deprecated. Use status=negative');
        }
        else if (type === 'info') {
            console.warn('Property type has been deprecated. Use status=neutral');
        }
        else if (type === 'success') {
            console.warn('Property type has been deprecated. Use status=positive');
        }
    }
    useEffect(() => {
        setIsPassiveAutoDismiss(!disableAutoDismiss &&
            (type === 'success' ||
                type === 'positive' ||
                type === 'neutral' ||
                type === 'info' ||
                status === 'positive' ||
                status === 'neutral'));
    }, [type, status]);
    useEffect(() => {
        createPassiveAlertGroup();
        setRegionRef(document.querySelectorAll('.kite-alert-region.kite-alert-region--passive'));
        return () => {
            removeEmptyPassiveAlertGroup();
        };
    }, []);
    const handleAlertRef = useCallback((node) => {
        alertRef.current = node;
        setInitialAlertPosition();
        animateAlert(node);
    }, [alertInitializing]);
    const setInitialAlertPosition = (close) => {
        if (alertRef.current) {
            const isLargeBreakpoint = window.matchMedia('(min-width:840px)');
            // speed up initial transformation, which moves alert offscreen
            if (alertInitializing) {
                alertRef.current.style.setProperty('--kite-alert-passive-transition-duration', '10ms');
            }
            if (isLargeBreakpoint.matches) {
                const [offsetX, offsetY] = getDirectionOffset(alertRef.current, 'left');
                alertRef.current.style.setProperty('--translate', `${offsetX}, ${offsetY}`);
            }
            else {
                const [offsetX, offsetY] = getDirectionOffset(alertRef.current, 'up');
                alertRef.current.style.setProperty('--translate', `${offsetX}, ${offsetY}`);
            }
            if (close) {
                alertRef === null || alertRef === void 0 ? void 0 : alertRef.current.addEventListener('transitionend', () => (onClose ? onClose() : null), {
                    once: true,
                });
            }
            else {
                alertRef === null || alertRef === void 0 ? void 0 : alertRef.current.addEventListener('transitionend', () => {
                    alertRef.current.style.setProperty('--kite-alert-passive-transition-duration', 'var(--kite-motion-fast)');
                    setAlertInitializing(false);
                }, { once: true });
            }
        }
    };
    const animateAlert = (alert) => {
        if (!!alert && !alertInitializing) {
            alert.style.setProperty('--translate', '0, 0');
            alert.style.setProperty('visibility', 'visible');
            // Hide alert. Assumption is consumers will have alert queue.
            const hideAlert = () => {
                if (isPassiveAutoDismiss) {
                    setTimeout(() => {
                        // Reset offscreen positioning
                        setInitialAlertPosition(true);
                    }, Number(timeout));
                }
            };
            alert.addEventListener('transitionend', hideAlert, { once: true });
        }
    };
    /**
     * Append each passive alert to passive alerts group at the root of the dom
     */
    return (React.createElement(React.Fragment, null, (regionRef === null || regionRef === void 0 ? void 0 : regionRef.length) >= 1 &&
        ReactDOM.createPortal(React.createElement(KiteAlert, Object.assign({ alertRef: handleAlertRef, className: className, id: id, linkText: linkText, linkUrl: linkUrl, linkVariant: linkVariant, shouldCtaLinkDescription: shouldCtaLinkDescription, onClose: onClose, onLinkClick: onLinkClick, type: type, status: status, title: title, level: "passive", dismissible: canDismiss || !isPassiveAutoDismiss, showIcon: showIcon, autoFocus: false }, other), children), regionRef[0])));
};

const KiteTokensProvider = ({ overridePath = '/kite-overrides.json', children, }) => {
    const [tokenOverrides, setTokenOverrides] = useState(null);
    /**
     * Get json containing javascript token overrides information
     */
    const fetchTokenOverrides = (_a) => __awaiter(void 0, [_a], void 0, function* ({ abortController }) {
        try {
            if (!!tokenOverrides)
                return;
            const tokenOverridesResp = yield fetch(overridePath, {
                signal: abortController.signal,
            });
            const response = yield tokenOverridesResp.json();
            setTokenOverrides(response);
        }
        catch (error) { }
    });
    /**
     * Given a tokenName and a className retrieve the token value, given the following rules
     * 1. If the tokenName is not present in the tokenOverrides json, get the default value from the tokens library
     * 2. If there is an override and no className is provided for that override, return the token override value
     * 3. If the override has a className associated with it, only return the overidden token value if the passed in className matches what is in the override.
     * @param tokenName
     * @param className
     * @param frameworkOverride - used if React Kite is defaulting to icon that is not the default icon provided by the tokens library
     */
    const getTokenValue = (tokenName, frameworkOverride, className) => {
        let tokenValue = getDefaultToken(tokenName, frameworkOverride);
        if (tokenOverrides && tokenOverrides[tokenName]) {
            if (hasScopeOverrides(tokenOverrides[tokenName].scope, className)) {
                tokenValue = tokenOverrides[tokenName].scope[className];
            }
            else {
                tokenValue = tokenOverrides[tokenName].global;
            }
        }
        return tokenValue;
    };
    const hasScopeOverrides = (scopeOverrides, className) => {
        if (!scopeOverrides || !className)
            return false;
        return !!scopeOverrides[className];
    };
    useEffect(() => {
        const abortController = new AbortController();
        fetchTokenOverrides({ abortController });
        return () => abortController.abort(); // Prevent extra fetch request from being made
    }, []);
    return (React.createElement(KiteTokensContext.Provider, { value: { tokenOverrides, getTokenValue, setTokenOverrides } }, children));
};

const oneWord = (str) => {
    return str && typeof str === 'string' ? str.replace(/\s+/g, '') : '';
};

const KiteRadioContext = React.createContext({ disabled: false, onChange: (e) => e });

/**
 * [View the design specifications](https://beam.kiteds.com/web/components/radio-button/design.html)
 */
const KiteRadio = forwardRef((_a, ref) => {
    var { children, className, disabled, label, onChange, tooltip } = _a, other = __rest(_a, ["children", "className", "disabled", "label", "onChange", "tooltip"]);
    return (React.createElement(KiteRadioContext.Provider, { value: { disabled, onChange } },
        React.createElement("div", Object.assign({ ref: ref, className: classNames(`kite-radio-group`, {
                [className]: className,
            }), role: "radiogroup", "aria-labelledby": oneWord(label || '') }, other),
            label && (React.createElement("p", { className: "kite-radio-group__legend", id: oneWord(label) }, label)),
            tooltip && (React.createElement(KitePopover, { disabled: disabled },
                React.createElement(React.Fragment, null, tooltip))),
            children)));
});

/**
 * [View the design specifications](https://beam.kiteds.com/web/components/radio-button/design.html)
 */
const KiteRadioButton = forwardRef((_a, ref) => {
    var { checked, disabled: buttonDisabled, id, label, name, onChange: buttonOnChange, onClick, value, children } = _a, other = __rest(_a, ["checked", "disabled", "id", "label", "name", "onChange", "onClick", "value", "children"]);
    const normalId = useNormalId();
    const descriptionId = `kite-radio__description-${oneWord(id || label || normalId)}`;
    const context = React.useContext(KiteRadioContext);
    const renderRadio = (disabled, onChange) => {
        const onClickHandler = (e) => {
            if (buttonDisabled) {
                e.preventDefault();
            }
            else {
                onClick && onClick(e);
            }
        };
        return (React.createElement("label", { htmlFor: oneWord(id || label || normalId), className: "kite-radio" },
            React.createElement("input", Object.assign({ type: "radio", id: oneWord(id || label || normalId), name: name, ref: ref, value: value, checked: checked, "aria-disabled": buttonDisabled, onClick: onClickHandler, onChange: onChange || buttonOnChange, disabled: disabled, "aria-checked": checked, "aria-labelledby": descriptionId }, other)),
            React.createElement("span", { className: "kite-radio__indicator" }),
            React.createElement("span", { id: descriptionId, "aria-hidden": true, className: "kite-radio__description" },
                label,
                children)));
    };
    if (context) {
        return (React.createElement(KiteRadioContext.Consumer, null, ({ disabled: groupDisabled, onChange: groupOnChange }) => {
            return React.createElement(React.Fragment, null, renderRadio(groupDisabled, groupOnChange));
        }));
    }
    return React.createElement(React.Fragment, null, renderRadio(buttonDisabled, buttonOnChange));
});

/**
 * View the design standard [here](https://beam.kiteds.com/web/components/select/design.html)
 *
 * **NOTE:** Any other props not specifically named that are supplied will be provided to the native `<select>` element.
 */
const KiteSelect = forwardRef((_a, ref) => {
    var { className, children, disabled, errorMessage, helperText, id, label, name, onChange, placeholder, tooltip, value } = _a, other = __rest(_a, ["className", "children", "disabled", "errorMessage", "helperText", "id", "label", "name", "onChange", "placeholder", "tooltip", "value"]);
    const normalId = useNormalId(id);
    const labelId = `rk-select-label-${normalId}`;
    const errorId = `rk-select-error-${normalId}`;
    const popover = tooltip && (React.createElement(KitePopover, { disabled: disabled, ariaLabel: `${label} helpful hint` },
        React.createElement(React.Fragment, null, tooltip)));
    const [optionChildren, labelChildren] = useMemo(() => {
        return partitionChildrenByDisplayName(children, 'KiteLabel');
    }, [children]);
    // Grab first label if at least 1 label is present.  Otherwise return undefined.
    const [firstChildLabel] = labelChildren;
    const labelElement = useMemo(() => {
        if (firstChildLabel) {
            const labelProps = {
                id: labelId,
                htmlFor: oneWord(id || name || label),
                baseCSSClass: 'kite-select',
            };
            if (disabled) {
                labelProps.disabled = disabled;
            }
            if (helperText) {
                labelProps.helperText = helperText;
            }
            return React.cloneElement(firstChildLabel, labelProps);
        }
        else {
            return null;
        }
    }, [firstChildLabel]);
    return (React.createElement("div", { className: classNames('kite-select', {
            'kite-select--error': errorMessage,
            'kite-select--disabled': disabled,
            'kite-popover--form-control': !!tooltip,
            [className]: className,
        }) },
        labelElement ||
            (label && (React.createElement(KiteLabel, { id: labelId, htmlFor: oneWord(id || name || label), baseCSSClass: "kite-select", helperText: helperText, popover: popover, disabled: disabled }, label))),
        React.createElement("span", { className: "kite-select__select-group" },
            React.createElement("select", Object.assign({ ref: ref, id: oneWord(normalId || name || label), name: name, value: value || '', onChange: onChange, disabled: disabled, "aria-labelledby": combineIdentifiers(labelId, {
                    [getHelperTextIdFromLabelId(labelId)]: !!helperText,
                }), "aria-describedby": errorMessage ? errorId : null, className: classNames('kite-select__select', {
                    'kite-select__select--placeholder': !!placeholder && !value,
                }) }, other),
                placeholder && (React.createElement("option", { value: "", disabled: true }, placeholder)),
                optionChildren),
            React.createElement(KiteIcon, { icon: "ki-dropdown", className: "kite-select__icon", id: id ? `rk-select-icon-${id}` : null })),
        errorMessage && (React.createElement("span", { id: errorId, className: "kite-select__feedback" }, errorMessage))));
});

/**
 *  [Skip Link design guidelines](https://beam.kiteds.com/web/components/skip-link/#overview)
 */
const KiteSkipLink = forwardRef((_a, ref) => {
    var { skipLinkInfo, className, anchorDestination, label, scrollIntoViewOptions } = _a, other = __rest(_a, ["skipLinkInfo", "className", "anchorDestination", "label", "scrollIntoViewOptions"]);
    if (!!anchorDestination || !!label) {
        console.warn('The props label and anchorDestination are deprecated, use skipLinkInfo instead.');
    }
    const generateLink = useCallback((singleLinkInfo, defaultLabel) => {
        let linkLabel;
        let anchor;
        let anchorSel;
        let scrollOptions;
        if (defaultLabel) {
            anchor = anchorDestination;
            linkLabel = label ? label : 'Skip to main content';
            anchorSel = anchor && anchor[0] === '#' ? anchor : `#${anchor}`;
            scrollOptions = scrollIntoViewOptions;
        }
        else {
            anchor = singleLinkInfo.anchorDestination;
            linkLabel = singleLinkInfo.label;
            anchorSel = anchor && anchor[0] === '#' ? anchor : `#${anchor}`;
            scrollOptions = singleLinkInfo.scrollIntoViewOptions;
        }
        if (scrollOptions) {
            return (React.createElement("a", { href: anchorSel, onClick: (e) => {
                    const destEl = document.querySelector(anchorSel);
                    if (destEl) {
                        e.preventDefault();
                        // Add setTimeout to give AT time to process the click event and readout new focus
                        setTimeout(() => {
                            destEl === null || destEl === void 0 ? void 0 : destEl.scrollIntoView(scrollOptions);
                            destEl === null || destEl === void 0 ? void 0 : destEl.focus();
                        }, 0);
                    }
                } }, linkLabel));
        }
        else {
            return React.createElement("a", { href: anchorSel }, linkLabel);
        }
    }, [skipLinkInfo, label, anchorDestination, label]);
    return (React.createElement("div", Object.assign({ className: classNames({
            'kite-skip-link': true,
            [className]: className,
        }), ref: ref }, other),
        React.createElement("ul", null, Array.isArray(skipLinkInfo) ? (skipLinkInfo.map((singleLinkInfo) => (React.createElement("li", { key: singleLinkInfo.anchorDestination }, generateLink(singleLinkInfo, false))))) : (React.createElement("li", null, generateLink(null, true))))));
});

const KiteStyles = () => (React.createElement("div", null,
    React.createElement("h1", null, "KiteStyles")));

/**
 * Tab panels organize and contain localized sets of information.
 */
const KiteTab = (_a) => {
    var { children, className, index, selected } = _a, other = __rest(_a, ["children", "className", "index", "selected"]);
    return (React.createElement("div", Object.assign({ className: classNames({
            'kite-tabs__panel': true,
            'kite-tabs__panel--selected': selected,
            [className]: true,
        }), role: "tabpanel", hidden: !selected, id: `rk-tabpanel-${index}`, "aria-labelledby": `rk-tab-${index}` }, other), selected && React.createElement(React.Fragment, null, children)));
};

/**
 * Tabs organize and enable navigation between similar, localized sets of information. Unlike the Navigation Bar (navbar), tabs do not allow navigation between separate pages of content
 * and have a different keyboard interaction model.
 *
 * View the design standard [here](https://beam.kiteds.com/web/components/tabs/design.html)
 */
const KiteTabs = (_a) => {
    var { alignment = 'left', ariaLabel, ariaLabelledBy, children, className, focusChange, selectedIndex = 0 } = _a, other = __rest(_a, ["alignment", "ariaLabel", "ariaLabelledBy", "children", "className", "focusChange", "selectedIndex"]);
    const [previousSelectedIndex, setPreviousSelectedIndex] = useState(-1);
    const tabRefs = useRef([]);
    const inkBarRefs = useRef([]);
    const tabChangeHandler = (selectedIndex) => {
        focusChange(selectedIndex);
    };
    const inkbarTransitionClassName = 'rk-inkbar-transition';
    const alignInkBar = () => {
        if (previousSelectedIndex !== -1) {
            const previousTab = tabRefs.current[previousSelectedIndex];
            const tab = tabRefs.current[selectedIndex];
            const inkbar = inkBarRefs.current[selectedIndex];
            const previousTabRect = previousTab.getBoundingClientRect();
            const tabRect = tab.getBoundingClientRect();
            inkbar.classList.remove(inkbarTransitionClassName);
            inkbar.style.setProperty('transform', `translateX(${previousTabRect.left - tabRect.left}px) scaleX(${previousTabRect.width / tabRect.width})`);
            setPreviousSelectedIndex(selectedIndex);
            inkbar.getBoundingClientRect();
            inkbar.classList.add(inkbarTransitionClassName);
            inkbar.style.setProperty('transform', ``);
        }
    };
    useEffect(() => {
        if (previousSelectedIndex === -1) {
            setPreviousSelectedIndex(selectedIndex);
        }
        alignInkBar();
    }, [selectedIndex]);
    const handleTabKeydown = (e, index) => {
        const eventTarget = e.target;
        const tabs = eventTarget &&
            Array.from(eventTarget.parentElement.children).filter((tab) => tab.classList.contains('kite-tabs__tab'));
        // Down, left and end keys
        if (e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'End') {
            e.preventDefault();
            let isEndKey = e.key === 'End';
            for (let i = index - 1; i >= -1; i--) {
                // Continue at other end of tabs, or handle end key
                if (i < 0 || isEndKey) {
                    i = tabs.length - 1;
                    isEndKey = false;
                }
                // Skip disabled tabs
                if (tabs[i].getAttribute('disabled') === null) {
                    tabs[i].focus();
                    return;
                }
            }
        }
        // Up, right and home keys
        if (e.key === 'ArrowUp' || e.key === 'ArrowRight' || e.key === 'Home') {
            e.preventDefault();
            let isHomeKey = e.key === 'Home';
            for (let i = index + 1; i <= tabs.length; i++) {
                // Continue at other end of tabs, or handle home key
                if (i === tabs.length || isHomeKey) {
                    i = 0;
                    isHomeKey = false;
                }
                // Skip disabled tabs
                if (tabs[i].getAttribute('disabled') === null) {
                    tabs[i].focus();
                    return;
                }
            }
        }
        // Space bar and enter keys
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            // change the selected tab's `tabindex` to 0 and the rest to -1
            // that way if a user tabs away and tabs back, it'll focus on the selected
            // tab and not the first tab by default
            for (let i = 0; i < tabs.length; i++) {
                if (i !== index) {
                    tabs[i].setAttribute('tabindex', -1);
                }
                else {
                    tabs[i].setAttribute('tabindex', 0);
                }
            }
            // move ink bar
            tabChangeHandler(index);
        }
    };
    return (React.createElement("div", Object.assign({ className: classNames(`kite-tabs`, {
            'kite-tabs--center': alignment === 'center',
            'kite-tabs--right': alignment === 'right',
            [className]: className,
        }) }, other),
        React.createElement("div", { className: "kite-tabs__tablist", role: "tablist", "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy }, React.Children.map(children, (child, index) => (React.createElement("button", { type: "button", role: "tab", "aria-selected": selectedIndex === index, tabIndex: index === 0 ? 0 : -1, className: classNames({
                'kite-tabs__tab': true,
                [child.props.className]: child.props.className,
                'kite-tabs__tab--selected': selectedIndex === index,
            }), key: index, id: `rk-tab-${index}`, onClick: () => tabChangeHandler(index), onKeyDown: (e) => handleTabKeydown(e, index), disabled: child.props.disabled, ref: (el) => {
                tabRefs.current[index] = el;
            } },
            child.props.label,
            React.createElement("span", { ref: (el) => {
                    inkBarRefs.current[index] = el;
                }, className: classNames('rk-indicator', {}) },
                React.createElement("span", { className: "rk-underline" })))))),
        React.createElement("div", { className: "kite-tabs__panels" }, React.Children.map(children, (child, index) => {
            return React.cloneElement(child, {
                index,
                selected: selectedIndex === index,
            });
        }))));
};

const KiteTagGroup = (_a) => {
    var { children, className, id: idRaw } = _a, other = __rest(_a, ["children", "className", "id"]);
    const id = useNormalId(idRaw);
    const groupClassNames = classNames('kite-tag-group', className);
    const groupProps = Object.assign({ id, className: groupClassNames }, other);
    return (React.createElement("ul", Object.assign({}, groupProps), React.Children.map(children, (child) => (React.createElement("li", { className: "kite-tag-group__item" }, child)))));
};

const KiteTag = (_a) => {
    var { children, id: idRaw, icon, variant = 'outline', status, className } = _a, other = __rest(_a, ["children", "id", "icon", "variant", "status", "className"]);
    const id = useNormalId(idRaw);
    const kiteTagClassNames = classNames('kite-tag', className, {
        [`kite-tag--${variant}`]: !!variant,
        [`kite-tag--${status}`]: !!status,
    });
    const kiteTagProps = Object.assign({ id, className: kiteTagClassNames }, other);
    const maybeKiteTagIcon = !!icon && (React.createElement("div", { className: "kite-tag__icon" },
        React.createElement(KiteIcon, { icon })));
    return (React.createElement("span", Object.assign({}, kiteTagProps),
        maybeKiteTagIcon,
        children));
};

/**
 * isComponent - Checks a given ReactElement for typeName comparison.
 * @param component - The component to compare
 * @param typeNames - List of typeName symbols to check
 * @param strict - Should throw exception if not matched
 * @throws InvalidChildComponentType
 * @returns boolean
 */
const isComponent = (component, typeNames, componentDisplayNames, strict) => {
    let isMatch;
    {
        isMatch =
            !!(component === null || component === void 0 ? void 0 : component.props) &&
                componentDisplayNames.indexOf(component.type.displayName) >= 0;
    }
    return isMatch;
};
/**
 * findChildren - mine a collection of children for a given list of types.
 * @param children - the collection to search
 * @param typeNames - List of typeName symbols to check
 * @returns - list of matching react elements
 */
const findChildren = (children, typeNames, componentDisplayNames) => {
    if (!!!children) {
        return [];
    }
    return React.Children.map(children, (child) => {
        return isComponent(child, typeNames, componentDisplayNames)
            ? child
            : undefined;
    }).filter((child) => !!child);
};

const KiteTextInputIcon = ({ ariaHidden = true, ariaLabel, icon, onClick, }) => {
    const isButton = !!onClick;
    const isStandalone = !isButton;
    const Wrap = isButton ? 'button' : 'div';
    // Set of props common to all wrap types
    const commonWrapProps = {
        className: classNames('kite-text-input__input-addon', {
            'kite-text-input__input-addon--button': isButton,
        }),
    };
    // Set of props unique to the button wrap
    const buttonWrapProps = {
        type: 'button',
        onClick,
    };
    // ### Aria-label props.
    // If Icon will be rendered as a button, Aria-label props go on the button.
    // If Icon will be rendered standalone, Aria-label props go on the icon.
    const ariaLabelProps = {
        'aria-hidden': ariaHidden,
        'aria-label': ariaLabel,
    };
    // Wrap can be a `div` or a `button`.
    // If button, then wrap receives button props and aria-label props
    const wrapProps = Object.assign(Object.assign(Object.assign({}, commonWrapProps), (isButton && buttonWrapProps)), (isButton && ariaLabelProps));
    // Icon can be standalone or wrapped in a button.
    // If standalone, icon receives incoming aria-label props
    const iconProps = Object.assign({ icon }, (isStandalone && ariaLabelProps));
    return (React.createElement(Wrap, Object.assign({}, wrapProps),
        React.createElement(KiteIcon, Object.assign({}, iconProps))));
};
KiteTextInputIcon.displayName = 'KiteTextInputIcon';

const getOverlaySize = (overlayDimensions, contentDimensions) => contentDimensions;
const createPredicate = (direction, align) => (context) => {
    const [contextDirection, contextAlignment] = context;
    return allTrue(!!context, contextDirection === direction, contextAlignment === align);
};
const getTooltipText = (text, children) => {
    if (!!text)
        return text;
    if (typeof children === 'string')
        return text;
    if (allTrue(React.isValidElement(children), 'props' in children, 'title' in children.props)) {
        return children.props.title;
    }
    return undefined;
};
// Function to inject the tooltip text alongside the child component's children
const injectContent = (element, injectedContent) => {
    // Clone the element and inject tooltip text in its children
    return React.cloneElement(element, {
        children: (React.createElement(React.Fragment, null,
            element.props.children,
            injectedContent)),
    });
};

const arrowLength = 7;
const arrowGutter = 4;
const arrowOffset = arrowLength + arrowGutter;
const arrowCorrection = 2;
const originWidth = 1;
const originHeight = 1;
const orientationData = {
    top: {
        center: {
            originAnchor: [originWidth / 2, 0],
            overlayAnchor: [1 / 2, 1],
            offset: [0, -13],
            contentCss: ['kite-tooltip__arrow', 'kite-tooltip__arrow--top'],
        },
        start: {
            originAnchor: [0, 0],
            overlayAnchor: [0, 1],
            offset: [0, -13],
            contentCss: [
                'kite-tooltip__arrow',
                'kite-tooltip__arrow--top',
                'kite-tooltip__arrow--align-start',
            ],
        },
        end: {
            originAnchor: [originWidth, 0],
            overlayAnchor: [1, 1],
            offset: [0, -13],
            contentCss: [
                'kite-tooltip__arrow',
                'kite-tooltip__arrow--top',
                'kite-tooltip__arrow--align-end',
            ],
        },
    },
    bottom: {
        center: {
            originAnchor: [originWidth / 2, originHeight],
            overlayAnchor: [1 / 2, 0],
            offset: [0, arrowOffset + arrowCorrection],
            contentCss: ['kite-tooltip__arrow', 'kite-tooltip__arrow--bottom'],
        },
        start: {
            originAnchor: [0, originHeight],
            overlayAnchor: [0, 0],
            offset: [0, arrowOffset + arrowCorrection],
            contentCss: [
                'kite-tooltip__arrow',
                'kite-tooltip__arrow--bottom',
                'kite-tooltip__arrow--align-start',
            ],
        },
        end: {
            originAnchor: [1, originHeight],
            overlayAnchor: [1, 0],
            offset: [0, arrowOffset + arrowCorrection],
            contentCss: [
                'kite-tooltip__arrow',
                'kite-tooltip__arrow--bottom',
                'kite-tooltip__arrow--align-end',
            ],
        },
    },
    left: {
        center: {
            originAnchor: [0, originHeight / 2],
            overlayAnchor: [1, 1 / 2],
            offset: [-11, 0],
            contentCss: ['kite-tooltip__arrow', 'kite-tooltip__arrow--left'],
        },
        start: {
            originAnchor: [0, 0],
            overlayAnchor: [1, 0],
            offset: [-11, 0],
        },
        end: {
            originAnchor: [0, originHeight],
            overlayAnchor: [1, 1],
            offset: [-11, 0],
        },
    },
    right: {
        center: {
            originAnchor: [originWidth, originHeight / 2],
            overlayAnchor: [0, 1 / 2],
            offset: [arrowOffset + arrowCorrection, 0],
            contentCss: ['kite-tooltip__arrow', 'kite-tooltip__arrow--right'],
        },
        start: {
            originAnchor: [originWidth, 0],
            overlayAnchor: [0, 0],
            offset: [arrowOffset + arrowCorrection, 0],
        },
        end: {
            originAnchor: [originWidth, originHeight],
            overlayAnchor: [0, 1],
            offset: [arrowOffset + arrowCorrection, 0],
        },
    },
};

function useTooltipOverlay(direction, align) {
    const context = [direction, align];
    const strategies = Object.entries(orientationData).reduce((acc, [itemDirection, subItem]) => [
        ...acc,
        ...Object.entries(subItem).map(([itemAlign, { originAnchor, overlayAnchor, offset, contentCss }]) => new OriginRelative(originAnchor, overlayAnchor, offset, getOverlaySize, createPredicate(itemDirection, itemAlign), contentCss)),
    ], []);
    return { context, strategies };
}

const TooltipText = ({ display, direction, align, originRef, tooltipText, tooltipId, }) => {
    const { context, strategies } = useTooltipOverlay(direction, align);
    return (display && (React.createElement(AttachedOverlay, { context: context, strategies: strategies, origin: originRef.current, shouldReturnFocus: false },
        React.createElement("span", { id: tooltipId, className: 'kite-tooltip__text' }, tooltipText))));
};

const KiteTooltipBodyInteractive = ({ children, tooltipOverlay, }) => injectContent(children, tooltipOverlay);

const KiteTooltipBodyStatic = ({ children, tooltipOverlay, }) => (React.createElement(React.Fragment, null,
    children,
    tooltipOverlay));

const defaultAlignment$1 = 'center';
const KiteTooltip = (_a) => {
    var _b;
    var { ariaLabel, id, type, text, children, direction = 'top', align: alignment = defaultAlignment$1, hideArrow, className } = _a, other = __rest(_a, ["ariaLabel", "id", "type", "text", "children", "direction", "align", "hideArrow", "className"]);
    const [display, setDisplay] = useState(false);
    const triggerRef = useRef(null);
    const { hasFocusVisible } = useFocusManager(triggerRef, {
        onFocus: () => setDisplay(true),
        onBlur: () => setDisplay(false),
    });
    // Only allow default alignment on `left` and `right` positioned tooltips
    const align = ['left', 'right'].includes(direction)
        ? defaultAlignment$1
        : alignment;
    const tooltipId = 'rk-tooltip-' + id;
    const tooltipText = getTooltipText(text, children);
    let leftOffset;
    if ((_b = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) {
        leftOffset = triggerRef.current.offsetWidth / 2 + 'px';
    }
    const insideDialog = () => (triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) && !!closestOrSelf(triggerRef.current, '.rk-dialog');
    const hasArrow = !hideArrow && display;
    const arrowSubclass = hasArrow ? 'arrow' : 'no-arrow';
    const triggerClassNames = classNames('kite-tooltip__trigger', `kite-tooltip--${arrowSubclass}`, {
        'kite-tooltip--abbreviation': type === 'abbreviation',
        [`kite-tooltip--${direction}`]: direction,
        [`kite-tooltip--align-${align}`]: align,
        'kite-tooltip--dialog': insideDialog(),
        [className]: className,
    });
    const handleTriggerKeyDown = (e) => {
        e.key === ' ' && e.preventDefault(); // Prevent window from scrolling when tooltip has focus and space is pressed
    };
    // Blur on MouseOut unless trigger hasFocusVisible
    const handleTriggerMouseEnter = () => setDisplay(true);
    const handleTriggerMouseLeave = () => setDisplay(hasFocusVisible || false);
    const isComponentInteractive = getIsComponentInteractive(children);
    const maybeAriaProps = Object.assign({}, (!isComponentInteractive && {
        'aria-label': ariaLabel,
        'aria-roledescription': 'tooltip',
        role: 'button',
        tabIndex: 0,
    }));
    const triggerProps = Object.assign(Object.assign({}, maybeAriaProps), { className: triggerClassNames, onKeyDown: handleTriggerKeyDown, onMouseEnter: handleTriggerMouseEnter, onMouseLeave: handleTriggerMouseLeave });
    const KiteTooltipBody = isComponentInteractive
        ? KiteTooltipBodyInteractive
        : KiteTooltipBodyStatic;
    const tooltipOverlay = useMemo(() => (React.createElement(TooltipText, { display,
        direction,
        align,
        originRef: triggerRef,
        tooltipId,
        tooltipText })), [display, direction, align, triggerRef, tooltipId, tooltipText]);
    const TooltipWrap = 'div';
    const Trigger = type === 'abbreviation' ? 'abbr' : 'div';
    return (React.createElement(TooltipWrap, { className: 'kite-tooltip' },
        React.createElement(Trigger, Object.assign({ style: { '--tooltip-arrow-x-transform': leftOffset } }, triggerProps, { ref: triggerRef }, other),
            React.createElement(KiteTooltipBody, { tooltipOverlay }, children))));
};
KiteTooltip.displayName = 'KiteTooltip';

const withValidatedChildren = (allowedChildren) => (WrappedComponent) => {
    const parentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    // Create and return new `Validator` component.
    // `Validator` component wraps `WrappedComponent`, and performs all validation logic.
    // If validation logic is successful, `Validator` simply returns `WrappedComponent`
    const Validator = (props) => {
        const { children } = props, rest = __rest(props, ["children"]);
        // Validation Filter
        React.Children.forEach(children, (childComponent) => {
            // Accept Falsy values
            if (!childComponent)
                return;
            // Reject invalid React elements
            if (!React.isValidElement(childComponent)) {
                throw new Error(`Only valid React elements are allowed.`);
            }
            // Accept allowed child types
            const childComponentType = childComponent.type;
            if (allowedChildren.includes(childComponentType))
                return;
            /**
             * Handle Error
             */
            // Convert array of allowed children Components to a comma-delimited list
            const allowedChildrenText = allowedChildren
                .map((type) => type.name)
                .join(', ');
            throw new Error(`Invalid child type! You can not use \`${childComponentType.name}\`. Only ${allowedChildrenText} are allowed in \`${parentName}\`.`);
        });
        // If all is well, Validator returns WrappedComponent
        return React.createElement(WrappedComponent, Object.assign({}, rest), children);
    };
    // Explicitly create contextual displayName that ties HOC to WrappedComponent for easy debugging
    Validator.displayName = `withValidatedChildren(${parentName})`;
    return Validator;
};

const TEXT = 'text';
const PASSWORD = 'password';
const MULTILINE = 'multiline';
const SHOW_PASSWORD = 'Show password';
const HIDE_PASSWORD = 'Hide password';
const KiteTextInputTokenEnum = {
    INPUT_SECURE: 'kite-text-input-secure-icon-name',
    INPUT_SECURE_VISIBLE: 'kite-text-input-secure-visible-icon-name',
    INPUT_CHECKMARK: 'kite-text-input-secure-confirm-icon-name',
};
const DEFAULT_MAX_LENGTH_ERROR = 'Maximum number of characters reached.';
/**
 * An `<input>` element. View the design standard [here](https://beam.kiteds.com/web/components/text-input/design.html).
 */
const KiteTextInput = (_a) => {
    var { ariaLive = 'off', children, className, disabled, dynamicHintId, errorId, errorMessage, helperText, id, label, name, onBlur, onChange, onFocus, inputRef, placeholder, tooltip, type, confirm, hidePasswordTooltip, value, maxLength, charCountSRText: charCountSrTextOverride, allowTextAreaResize = true, hideCharacterCountLabel = true } = _a, other = __rest(_a, ["ariaLive", "children", "className", "disabled", "dynamicHintId", "errorId", "errorMessage", "helperText", "id", "label", "name", "onBlur", "onChange", "onFocus", "inputRef", "placeholder", "tooltip", "type", "confirm", "hidePasswordTooltip", "value", "maxLength", "charCountSRText", "allowTextAreaResize", "hideCharacterCountLabel"]);
    const [showText, setShowText] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const inputType = showText ? TEXT : PASSWORD;
    const hintNormalId = useNormalId();
    const inputNormalId = useNormalId();
    const hintId = dynamicHintId || `hint-${hintNormalId}`;
    const inputId = id || `input-${inputNormalId}`;
    const labelId = `label-${inputId}`;
    const charCount = (value === null || value === void 0 ? void 0 : value.length) || 0;
    const charCountId = id ? `${id}-char-count` : `${inputNormalId}-char-count`;
    const charCountLabelId = `${charCountId}-label`;
    const charCountSRText = charCountSrTextOverride || !!maxLength
        ? `${charCount} out of ${maxLength} characters`
        : 'character count';
    const charCountVisibleText = `${charCount}/${maxLength}`;
    const [visited, setVisited] = useState(false);
    const [blurred, setBlurred] = useState(false);
    const [componentErrorMsg, setComponentErrorMsg] = useState(errorMessage);
    const [errorMsgRole, setErrorMsgRole] = useState('');
    // On every keyup, check if KiteTextInput has reached its maxLength (if applicable)
    // If it has, then test to see if a character-echoing key has been it.
    // If it has, then show default max character reached error (for multiline and text inputs) and
    // hide (or do nothing if already hidden) the char-max limit reached message and reshow
    const onTextInputKeyUp = (e) => {
        if (!!maxLength && charCount >= maxLength && e.key.length === 1) {
            let error = null;
            const shouldShowDefaultErrorMessage = (!type || type === TEXT || type === MULTILINE) && errorMessage !== null;
            if (errorMessage) {
                error = errorMessage;
            }
            else if (shouldShowDefaultErrorMessage) {
                error = DEFAULT_MAX_LENGTH_ERROR;
            }
            setComponentErrorMsg(error);
            // we only want to set the role to alert if there is no custom error message as aria live is used for
            // controlled input's error message reading
            if (error && shouldShowDefaultErrorMessage) {
                setErrorMsgRole('alert');
            }
        }
        else {
            setComponentErrorMsg(errorMessage || '');
            setErrorMsgRole('');
        }
    };
    useEffect(() => {
        setComponentErrorMsg(errorMessage || '');
        setErrorMsgRole('');
    }, [errorMessage]);
    const { tokenOverrides, getTokenValue } = useKiteTokensContext();
    const [secureIcon, secureVisibleIcon, checkmarkIcon] = useMemo(() => [
        getTokenValue(KiteTextInputTokenEnum.INPUT_SECURE, 'ki-eye-f', className),
        getTokenValue(KiteTextInputTokenEnum.INPUT_SECURE_VISIBLE, 'ki-eye-cross-f', className),
        getTokenValue(KiteTextInputTokenEnum.INPUT_CHECKMARK, null, className),
    ], [tokenOverrides, className]);
    if (tooltip) {
        console.warn(`Property 'tooltip' has been deprecated. Use popover as a child (https://v3.react.kiteds.com/components/text-input/#child-kitepopover).`);
    }
    useEffect(() => {
        if (isFocused) {
            setVisited(true);
        }
        if (!isFocused && visited) {
            setBlurred(true);
        }
    }, [isFocused]);
    const handleFocus = (e) => {
        setIsFocused(true);
        if (onFocus) {
            onFocus(e);
        }
    };
    const handleBlur = (e) => {
        setIsFocused(false);
        if (onBlur) {
            onBlur(e);
        }
    };
    const toggleText = (e) => {
        e.preventDefault();
        setShowText(!showText);
    };
    const containerRef = useRef(null);
    const pseudoInputRef = useRef(null);
    /**
     * Retrieve Slots
     */
    // Dynamic Hint Slot
    let hasDynamicHint = false;
    const dynamicHints = findChildren(children, null, ['KiteDynamicHint']).map((child) => React.cloneElement(child, {
        show: isFocused,
        inputRef: pseudoInputRef,
        id: hintId,
        name: name !== null && name !== void 0 ? name : inputId,
    }));
    hasDynamicHint = dynamicHints.length > 0;
    // Popover Slot
    const popovers = findChildren(children, null, ['KitePopover']).map((child) => React.cloneElement(child, {
        ariaLabel: `${label} helpful hint`,
        ariaControls: oneWord(id || name || label),
        onlyTop: true,
    }));
    // Character Count Assistive Text Slot
    const charCountAsistiveLabel = !!maxLength ? (React.createElement("span", { id: charCountLabelId, className: "kite-assistive-text" }, charCountSRText)) : null;
    // Character Count Label Slot
    // We only alow the character count label to be hidden if the input is text type as to be backwards compatible
    const shouldDisplayCharCountLabel = !!maxLength &&
        (((!type || type === TEXT) && !hideCharacterCountLabel) ||
            type === MULTILINE);
    const charCountLabel = shouldDisplayCharCountLabel ? (React.createElement("span", { id: charCountId, className: type === MULTILINE
            ? 'kite-text-area__character-count'
            : 'kite-text-input__character-count', "aria-hidden": true }, charCountVisibleText)) : null;
    const tooltipEl = findChildren(children, null, ['KiteTooltip']).map((child) => React.cloneElement(child, {
        ariaLabel: child.props.text,
        ariaControls: oneWord(id || name || label),
        direction: child.props.direction || 'top',
        className: classNames(child.props.className, 'rk-tooltip--inline'),
    }));
    const popover = popovers.length > 0 ? (React.createElement(React.Fragment, null, popovers[0])) : !!tooltip ? (React.createElement(KitePopover, { disabled: disabled, onlyTop: true, ariaLabel: label + ' helpful hint' }, tooltip)) : undefined;
    const labelEl = findChildren(children, null, ['KiteLabel']).map((child) => {
        const labelProps = {
            id: labelId,
            baseCSSClass: type === 'multiline' ? 'kite-text-area' : 'kite-text-input',
            htmlFor: oneWord(inputId || name || label),
        };
        if (disabled) {
            labelProps.disabled = disabled;
        }
        if (helperText) {
            labelProps.helperText = helperText;
        }
        return React.cloneElement(child, labelProps);
    });
    // Icon Slots
    // Retrieve all `KiteTextInputIcon` components from component children.
    // User could potentially add more than one of the same slot type.
    // Handle duplicates by running a reduce on the incoming array of Icons. Transforms list of X icons into a 2-element tuple.  Redundant slot types override previous.  Last slot type encountered gets returned.
    // Deconstruct result into `iconLeft` and `iconRight` variables.
    const [iconLeft, iconRight] = findChildren(children, null, [
        'KiteTextInputIcon',
    ]).reduce((acc, textInputIcon) => {
        // Pull slot off icon props
        const { slot } = textInputIcon.props;
        // Direct Icon into Left or Right side of tuple based on slot value
        if (slot === 'left')
            acc[0] = textInputIcon;
        if (slot === 'right')
            acc[1] = textInputIcon;
        return acc;
    }, 
    // Seed reduction with undefined tuple
    [undefined, undefined]);
    const passwordSlotRight = (React.createElement("button", { className: classNames({
            'kite-text-input__mask-toggle': true,
            'kite-text-input__mask-toggle--pressed': showText,
        }), title: hidePasswordTooltip ? null : showText ? HIDE_PASSWORD : SHOW_PASSWORD, onClick: toggleText, onKeyDown: (e) => (e.keyCode === 32 ? toggleText(e) : null), type: "button" },
        React.createElement("span", { className: "kite-text-input__unmask-icon" },
            React.createElement(KiteIcon, { icon: secureIcon })),
        React.createElement("span", { className: "kite-text-input__mask-icon" },
            React.createElement(KiteIcon, { icon: secureVisibleIcon })),
        React.createElement("span", { className: "kite-assistive-text" }, showText ? HIDE_PASSWORD : SHOW_PASSWORD)));
    const textInputSlotLeft = iconLeft;
    const textInputSlotRight = type === PASSWORD ? passwordSlotRight : iconRight;
    if (type === 'password' && !!iconRight)
        console.warn('The right-side icon of a Password input can not be overridden.');
    if (type === 'multiline' && (!!iconLeft || !!iconRight))
        console.warn('Icons can not be added to Multiline text inputs.');
    const formControlClasses = classNames(className, {
        'kite-text-input': type !== 'multiline',
        'kite-text-input--error': componentErrorMsg,
        'kite-text-area': type === 'multiline',
        'kite-text-area--error': componentErrorMsg,
        'kite-dynamic-hint-container': hasDynamicHint,
        'kite-text-input--disabled': type !== 'multiline' && disabled,
        'kite-text-area--disabled': type === 'multiline' && disabled,
        'kite-text-area--no-resize': !allowTextAreaResize,
    });
    const inputClasses = classNames({
        'kite-text-input__input-group': type !== 'multiline',
        'kite-text-area__input-group': type === 'multiline',
        'kite-text-input--confirmed': confirm,
        'kite-text-input--masked': type === PASSWORD,
    });
    const ariaProps = {
        'aria-invalid': blurred && !!componentErrorMsg ? true : undefined,
    };
    const ariaLabels = [
        (label === null || label === void 0 ? void 0 : label.length) ? labelId : '',
        (helperText === null || helperText === void 0 ? void 0 : helperText.length) ? getHelperTextIdFromLabelId(labelId) : '',
        !!maxLength ? charCountLabelId : '',
    ]
        .join(' ')
        .trim();
    return (React.createElement("div", { className: formControlClasses, ref: containerRef },
        (labelEl === null || labelEl === void 0 ? void 0 : labelEl.length)
            ? labelEl[0]
            : (label === null || label === void 0 ? void 0 : label.length) && (React.createElement(KiteLabel, { id: labelId, baseCSSClass: type === 'multiline' ? 'kite-text-area' : 'kite-text-input', htmlFor: oneWord(inputId || name || label), helperText: helperText, popover: popover, tooltip: tooltipEl[0], disabled: disabled }, label)),
        React.createElement("div", { className: inputClasses, ref: pseudoInputRef },
            type === MULTILINE ? (React.createElement(React.Fragment, null,
                charCountLabel,
                React.createElement("textarea", Object.assign({ id: inputId, className: "kite-text-area__input", name: name, value: value, onChange: onChange, disabled: disabled, onBlur: onBlur, onFocus: onFocus, onKeyUp: onTextInputKeyUp, placeholder: placeholder, "aria-labelledby": ariaLabels, "aria-describedby": `${componentErrorMsg ? errorId || oneWord(`${inputId}-error`) : ''}${hasDynamicHint ? ' ' + hintId : ''}` }, other, { ref: inputRef, maxLength: maxLength && maxLength }, ariaProps)))) : (React.createElement(React.Fragment, null,
                textInputSlotLeft,
                charCountLabel,
                React.createElement("input", Object.assign({ className: "kite-text-input__input", id: inputId, name: name, type: type === PASSWORD ? inputType : type, value: value, placeholder: placeholder, onChange: onChange, onFocus: (e) => handleFocus(e), onBlur: (e) => handleBlur(e), onKeyUp: onTextInputKeyUp, disabled: disabled, maxLength: maxLength, "aria-labelledby": ariaLabels, "aria-describedby": `${componentErrorMsg ? errorId || oneWord(`${inputId}-error`) : ''}${hasDynamicHint ? ' ' + hintId : ''}` }, other, { ref: inputRef }, ariaProps)),
                textInputSlotRight)),
            confirm && (React.createElement("span", { className: "kite-text-input__confirmed-icon" },
                React.createElement(KiteIcon, { icon: checkmarkIcon }))),
            type !== 'multiline' && (React.createElement("div", { className: "kite-text-input__appearance" }))),
        componentErrorMsg && (React.createElement("span", { className: classNames({
                'kite-text-input__feedback': type !== 'multiline',
                'kite-text-area__feedback': type === 'multiline',
            }), id: errorId || `${inputId}-error`, "aria-live": ariaLive, role: errorMsgRole }, componentErrorMsg)),
        type !== 'multiline' && !!dynamicHints && dynamicHints[0],
        charCountAsistiveLabel));
};
var KiteTextInput$1 = withValidatedChildren([
    KiteDynamicHint,
    KitePopover,
    KiteTooltip,
    KiteTextInputIcon,
    KiteLabel,
])(KiteTextInput);

const getTime = (duration, milliseconds) => {
    const animation = 400;
    return {
        short: 2500 + animation,
        long: 3500 + animation,
        custom: milliseconds + animation,
    }[duration];
};
const defaultAlignment = 'right';
const mapAlignmentToFlexValue = {
    left: 'start',
    center: 'center',
    right: 'end',
};
/**
 * The KiteToast allows for simple, inobtrusive user comms.
 */
function KiteToast(_a) {
    var _b, _c, _d, _e, _f, _g;
    var { id, children, icon, position = 'top', alignment, topOffset = 0, bottomOffset = 0, milliseconds, onExpire, duration = 'short', visible = false } = _a, other = __rest(_a, ["id", "children", "icon", "position", "alignment", "topOffset", "bottomOffset", "milliseconds", "onExpire", "duration", "visible"]);
    /*
     * Normalizing vertical (`vert`) and horizontal (`horz`) from `position` and `alignment` props:
     *
     * Historically:
     * - `position` was a single string "vert-horz", combining both vertical and horizontal.
     * - Splitting on "-" yields [`vert`, `horz`].
     *
     * Current approach:
     * - `position` has no "-". It contains only the vertical component.
     * - Splitting on "-" yields [`vert`].
     * - Horizontal component is now handled separately via an `alignment` prop
     *
     * By destructuring `position.split("-")` into [`vert`, `legacyHorz`?], we can accommodate both forms.
     * - `vert` always contains the vertical component
     * - `legacyHorz` has value when position is in Legacy form, otherwise it is `undefined`
     *
     * We then fully normalize the horizontal component with:
     *   horz = alignment || legacyHorz || defaultAlignment;
     *
     * Note: We avoid assigning a default directly to `alignment` so that a valid `legacyHorz`
     * can override it, thereby preserving consistent normalization between old and new approaches.
     */
    const [vert, horzLegazy] = position.split('-');
    const horz = alignment || horzLegazy || defaultAlignment;
    if (!!horzLegazy)
        console.warn(`Hyphenated position values have been DEPRECATED.
      
      Supplying "vertical-horizontal" formatted values to the \`position\` prop has been deprecated.  
      \`position\` prop now carries the vertical placement value, and \`alignment\` carries the horizontal value.  
      
      Standard:
      position="${vert}" 
      alignment="${horzLegazy}"
      
      Deprecated:
      position="${vert}-${horzLegazy}"
      
      
      `);
    const toastNormalId = useNormalId(id);
    const [counting, setCounting] = useState(false);
    const [content, setContent] = useState(children);
    const toastRef = useRef(null);
    const verticalOffset = ((_d = (_c = (_b = toastRef === null || toastRef === void 0 ? void 0 : toastRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect()) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 0) + 72 + 19;
    const verticalPosition = 42 + 30 + ((_g = (_f = (_e = toastRef === null || toastRef === void 0 ? void 0 : toastRef.current) === null || _e === void 0 ? void 0 : _e.getBoundingClientRect()) === null || _f === void 0 ? void 0 : _f.height) !== null && _g !== void 0 ? _g : 0);
    const [style, setStyle] = useState({});
    useEffect(() => {
        setStyle({
            [vert]: `-${verticalPosition}px`,
        });
    }, [toastRef.current]);
    const classes = classNames('rk-toast', 'kite-toast', `kite-toast--${vert}`, `kite-toast--align-${mapAlignmentToFlexValue[horz]}`, {
        'rk-toast__visible': visible,
        'rk-toast__invisible': !visible,
    });
    // all of this for a11y
    const finishCollapse = () => {
        const timer = setTimeout(() => {
            if (!visible) {
                setContent(null);
            }
            return clearTimeout(timer);
        }, 400);
    };
    useEffect(() => {
        setContent(React.createElement(React.Fragment, null,
            !!icon && React.createElement(KiteSvgIcon, { role: "img", className: "kite-icon", icon: icon }),
            children));
    }, [children]);
    useEffect(() => {
        if (visible && !counting) {
            setContent(React.createElement(React.Fragment, null,
                !!icon && (React.createElement(KiteSvgIcon, { role: "img", className: "kite-icon", icon: icon })),
                children));
            setCounting(true);
            // pop the toast
            setStyle({
                top: undefined,
                bottom: undefined,
                [vert]: `-${verticalPosition}px`,
                margin: visible
                    ? vert === 'top'
                        ? `${verticalOffset + Math.floor(topOffset)}px 0 0`
                        : `0 0 ${verticalOffset + Math.floor(bottomOffset)}px`
                    : '',
            });
            const timer = setTimeout(() => {
                // go away toast
                setCounting(false);
                setStyle({
                    [vert]: `-${verticalPosition}px`,
                    margin: 0,
                });
                return (() => {
                    clearTimeout(timer);
                    // notify the consumer
                    if (!!onExpire) {
                        onExpire();
                    }
                    // reset styles
                    setStyle({
                        top: 'unset',
                        bottom: 'unset',
                        opacity: undefined,
                        [vert]: `-${verticalPosition}px`,
                        margin: undefined,
                    });
                    finishCollapse();
                })();
            }, getTime(duration, Math.floor(milliseconds)));
        }
    }, [visible, children]);
    return (React.createElement(React.Fragment, null, createPortal(React.createElement("div", Object.assign({ id: toastNormalId, className: classes, ref: toastRef, "aria-live": "polite", role: "status", style: style }, other), visible ? content : ''), document.body)));
}

function KiteChipGroup(_a) {
    var { caption = '', children, id, name, overflowBehavior = 'wrapped', variant = 'button' } = _a, other = __rest(_a, ["caption", "children", "id", "name", "overflowBehavior", "variant"]);
    const normalId = useNormalId(id);
    const resolvedName = !!name ? name : `chip-radio-${normalId}`;
    const groupNormalId = useNormalId(id);
    const groupId = `kite-chip-group-${groupNormalId}`;
    const classes = classNames('kite-chip-group', {
        'kite-chip-group--scroll': overflowBehavior === 'scrollable',
        'kite-chip-group--wrap': overflowBehavior === 'wrapped',
    });
    const items = !!!children && children === null
        ? []
        : Children.map(children, (item) => {
            return !item
                ? undefined
                : cloneElement(item, Object.assign(Object.assign({}, item), { type: variant, name: resolvedName }));
        });
    return (React.createElement("fieldset", Object.assign({ className: classes, id: groupId }, other),
        React.createElement("legend", { className: "kite-type-style--caption-1" }, caption + (variant === 'radio' ? ' only one can be selected' : '')),
        items));
}

/** withKeyNav - allows for common key navigation (arrow) handling */
function withKeyNav({ parentSelector, childSelector, disabledSelector = ':not([disabled])', focusableSelector, direction = 'both', }) {
    // TODO: hutch - fix the return type
    return function (WrappedComponent) {
        class KeyNavComponent extends React.Component {
            constructor(props) {
                super(props);
                this.props = null;
                this.type = props.type;
                this.key = props.key;
                this.props = props;
                this.keyRef = props.keyRef;
            }
            componentDidMount() {
                var _a;
                (_a = this.keyRef) === null || _a === void 0 ? void 0 : _a.setAttribute('tabindex', '0');
            }
            handleKeyDown(e) {
                addPreventPageScroll();
            }
            handleKeyUp(e) {
                var _a, _b;
                if (isArrow(e.key, direction)) {
                    // Find the next element to navigate to (next | previous).
                    // That element might the child selector and the focusable selector might be on
                    // the same element.
                    const subsequentElement = isForward(direction, e.key)
                        ? queryOrSelf(nextSibling(closestOrSelf(e.currentTarget, childSelector), childSelector, disabledSelector), focusableSelector)
                        : queryOrSelf(previousSibling(closestOrSelf(e.currentTarget, childSelector), childSelector, disabledSelector), focusableSelector);
                    //
                    // We'll find the first or last element if we've reached the boundary of the parent.
                    //
                    const subsequentChild = isForward(direction, e.key)
                        ? childAt((_a = e.currentTarget) === null || _a === void 0 ? void 0 : _a.closest(parentSelector), focusableSelector, disabledSelector, 'first')
                        : childAt((_b = e.currentTarget) === null || _b === void 0 ? void 0 : _b.closest(parentSelector), focusableSelector, disabledSelector, 'last');
                    // if there is no subsequent element, then we have the right - other side - of the children
                    const next = (subsequentElement !== null && subsequentElement !== void 0 ? subsequentElement : subsequentChild);
                    // we've refined which element so ... focus.
                    queryOrSelf(next, focusableSelector).focus();
                }
            }
            render() {
                // We need to decorate our keyUp & keyDown events to trap for arrows
                const keyUpWrapper = (e) => {
                    this.handleKeyUp(e);
                    if (!!this.props.onKeyUp) {
                        this.props.onKeyUp(e);
                    }
                };
                const keyDownWrapper = (e) => {
                    this.handleKeyDown(e);
                    if (!!this.props.handleKeyDown) {
                        this.props.handleKeyDown(e);
                    }
                };
                return (React.createElement(WrappedComponent, Object.assign({}, this.props, { onKeyUp: keyUpWrapper, onKeyDown: keyDownWrapper, ref: (e) => (this.keyRef = e) })));
            }
        }
        return KeyNavComponent;
    };
}

function KiteChipButtonPre(_a, ref) {
    var { className, label, value, type, disabled, dismissible, onClick, onKeyDown, onKeyUp, normalId, selected } = _a, other = __rest(_a, ["className", "label", "value", "type", "disabled", "dismissible", "onClick", "onKeyDown", "onKeyUp", "normalId", "selected"]);
    const id = `chip-button-${normalId}`;
    const classes = classNames('rk-chip__selector', 'rk-chip__focusable', className);
    return (React.createElement("button", Object.assign({ id: id, className: classes, "aria-pressed": !dismissible ? selected : null, "aria-disabled": type === 'radio' ? selected : null, ref: ref, disabled: disabled, onClick: (e) => onClick(value), onKeyUp: onKeyUp, onKeyDown: onKeyDown }, other),
        React.createElement("div", { className: "kite-chip__inner" },
            React.createElement("span", { className: "kite-chip__background" }),
            selected && !dismissible && (React.createElement("div", { className: "kite-chip__indicator" },
                React.createElement("div", { className: "kite-chip__icon" },
                    React.createElement(KiteIcon, { icon: "ki-checkmark", ariaHidden: true })))),
            React.createElement("span", { className: "kite-chip__text" }, label),
            dismissible && (React.createElement("div", { className: "kite-chip__dismiss" },
                React.createElement("span", { className: "kite-assistive-text" }, "Remove"),
                React.createElement(KiteIcon, { icon: "ki-x-alt", ariaHidden: true }))))));
}
const KiteChipButton = forwardRef(KiteChipButtonPre);
function KiteChip({ label, selected, dismissible = false, type = 'checkbox', name, disabled, value, onClick, onKeyUp, onKeyDown, id, props, }, ref) {
    const normalId = useNormalId(id);
    const classes = classNames({
        'kite-chip': true,
        'kite-chip--selected': selected,
        'kite-chip--disabled': disabled,
    });
    const handleKeyUp = (e) => {
        if (dismissible && e.key === ESCAPE$3) {
            onClick(value);
            e.preventDefault();
            e.stopPropagation();
            return false;
        }
        onKeyUp(e);
    };
    return (React.createElement(KiteChipButton, Object.assign({ className: classes, dismissible: dismissible, normalId: normalId, selected: selected, disabled: disabled, type: type, ref: ref, value: value, label: label, onClick: onClick, onKeyUp: handleKeyUp, onKeyDown: onKeyDown }, props)));
}
var KiteChip$1 = withKeyNav({
    parentSelector: '.kite-chip-group',
    childSelector: '.rk-chip__selector',
    focusableSelector: '.rk-chip__focusable',
})(forwardRef(KiteChip));

export { AttachedOverlay, KiteAccordion, KiteAccordionRow, KiteAlert, KiteAlertsGroup, KiteBadge, KiteButton, KiteCalendar, KiteCard, KiteCell, KiteCheckbox, KiteChip$1 as KiteChip, KiteChipGroup, KiteColumn, KiteCombobox, KiteComboboxListItem, KiteDataTable, KiteDateInput, KiteDialog, KiteDropDown, KiteDropDownItem, KiteDynamicHint, KiteDynamicHintRule, KiteFeedback, KiteFormDialog$1 as KiteFormDialog, KiteGrid, KiteGridCell, KiteIcon, KiteImage, KiteIndicator, KiteInteractiveList, KiteInteractiveListItem, KiteInteractiveTitle, KiteLabel, KiteLink, KiteList, KiteListItem, KiteListItemCenter, KiteListItemContent, KiteListItemEnd, KiteListItemStart, KiteListItemThumbnail, KiteMenu, KiteMenuItem, KiteMeter, KitePagination, KitePassiveAlert, KitePaymentLogo, KitePopover, KiteProgressIndicator, KiteRadio, KiteRadioButton, KiteRow, KiteSelect, KiteSkipLink, KiteStyles, KiteSubtitle, KiteSwitch, KiteTab, KiteTableHeader, KiteTabs, KiteTag, KiteTagGroup, KiteTextInput$1 as KiteTextInput, KiteTextInputIcon, KiteTitle, KiteToast, KiteTokensProvider, KiteTooltip, boldSubstring, findBestSide, useComboboxBehavior, useKiteTokensContext, useMultiSelectBehavior, useSearchBehavior, useSingleSelectBehavior };
//# sourceMappingURL=react-kite.esm.js.map
